import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CaretLeftIcon, CaretRightIcon, CaretDownIcon, CaretUpIcon, FolderIcon, UserIcon, MapPinIcon, Image as ImageIcon, MonitorIcon, SparkleIcon, FunnelIcon, XIcon, MusicNoteIcon, ArrowsLeftRightIcon, PlayIcon, SkipBackIcon, SkipForwardIcon, MagnifyingGlassIcon, PauseIcon, SpeakerHighIcon, SpeakerXIcon, PlusIcon, RadioIcon, MicrophoneIcon, MagicWandIcon, UploadIcon, InfoIcon, ArrowsOutIcon, TrashIcon, ChatCircleIcon, UsersIcon, QueueIcon, UserPlusIcon } from '@phosphor-icons/react';
import Masonry from 'react-masonry-css';
import { ProjectService } from '../lib/projectService';
import { ProjectMemberService } from '../lib/projectMemberService';
import { supabase } from '../lib/supabase';
import { generateImageBlob } from '../lib/imageGeneration';
import { ChatPanel } from './chat/ChatPanel';
import { UsersPanel } from './sidebar/UsersPanel';

import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext'; // Import useToast
import { useProject } from '../context/ProjectContext';
import { useProjectPermissions } from '../hooks/useProjectPermissions';
import { Project, Shot, MoodImageRequest } from '../types';
import YouTube from 'react-youtube';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DotsThreeVerticalIcon, ShuffleIcon, RepeatIcon, RepeatOnceIcon, ArrowCounterClockwiseIcon } from '@phosphor-icons/react';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import { DiscordIcon } from './icons/DiscordIcon';
import { useDiscordRPC } from '../hooks/useDiscordRPC';
import { DiscordDownloadModal } from './DiscordDownloadModal';
import { FullscreenImageViewer, ViewerImage } from './FullscreenImageViewer';

// --- Helper: Format Seconds to MM:SS ---
// --- Helper: Format Seconds to MM:SS ---
const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

// --- Helper: Portal ---
const Portal = ({ children }: { children: React.ReactNode }) => {
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);
    if (!mounted) return null;
    return createPortal(children, document.body);
};



// --- Helper: Map Music Genre -> Film Genre ---
// --- Helper: Map Dominant Music Genre ---
const getDominantMusicGenres = (tracks: any[]) => {
    if (!tracks || tracks.length === 0) return [];

    const genreCounts: Record<string, number> = {};

    tracks.forEach(t => {
        if (!t.genre) return;
        // Normalize slightly if needed, or take raw
        const g = t.genre.trim();
        genreCounts[g] = (genreCounts[g] || 0) + 1;
    });

    // Find Top 4
    return Object.entries(genreCounts)
        .sort((a, b) => b[1] - a[1]) // Sort desc by count
        .slice(0, 4)
        .map(e => e[0]);
};

// --- Sortable Component ---
// --- Sortable Component ---
// --- Sortable Component ---
const SortableTrackItem = ({ track, index, isActive, isPlaying, onPlay, onPause, onDelete, onArtistClick, onAddToQueue }: any) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: track.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners} className={`w-full flex items-center gap-4 p-3 rounded-xl hover:bg-white/5 group text-left transition-colors relative ${isActive ? 'bg-indigo-500/10 border border-indigo-500/20' : 'border border-transparent'}`}>
            {/* Drag Handle - Strictly Centered */}
            <div
                className="w-8 flex items-center justify-center cursor-grab active:cursor-grabbing text-zinc-600 hover:text-zinc-400 self-stretch"
            >
                <div className="flex flex-col items-center justify-center h-full relative w-full">
                    <DotsThreeVerticalIcon className="w-4 h-4 opacity-0 group-hover:opacity-100 absolute transition-all" />
                    <span className="text-xs font-mono font-medium opacity-100 group-hover:opacity-0 transition-opacity text-zinc-500">{index + 1}</span>
                </div>
            </div>

            <div
                role="button"
                tabIndex={0}
                onClick={onPlay}
                className="flex items-center gap-4 flex-1 min-w-0 text-left cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50 rounded-lg"
            >
                <div className="w-12 h-12 rounded-lg bg-zinc-800 flex-shrink-0 relative overflow-hidden flex items-center justify-center group-hover/image:bg-zinc-700 shadow-sm transition-all">
                    {!track.cover ? (
                        <MusicNoteIcon className="w-5 h-5 text-zinc-600" />
                    ) : (
                        <>
                            <MusicNoteIcon className="w-5 h-5 text-zinc-600 absolute" />
                            <img
                                src={track.cover}
                                className={`w-full h-full object-cover relative z-10 ${isActive ? 'brightness-50' : 'group-hover:brightness-50'} transition-all`}
                                onError={(e) => e.currentTarget.style.display = 'none'}
                            />
                            {/* Play/Pause Overlay */}
                            <div
                                className={`absolute inset-0 z-20 flex items-center justify-center ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity cursor-pointer`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (isActive && isPlaying) {
                                        onPause();
                                    } else {
                                        onPlay();
                                    }
                                }}
                            >
                                {isActive && isPlaying ? (
                                    <PauseIcon className="w-5 h-5 text-white" />
                                ) : (
                                    <PlayIcon className="w-5 h-5 text-white" />
                                )}
                            </div>
                        </>
                    )}

                </div>
                <div className="min-w-0 flex-1">
                    <p className={`text-base font-medium truncate leading-tight mb-0.5 ${isActive ? 'text-indigo-400' : 'text-zinc-200 group-hover:text-white'}`}>{track.title}</p>
                    <button
                        onClick={(e) => { e.stopPropagation(); onArtistClick(e, track.artist, track.artistId); }}
                        className="text-sm text-zinc-500 truncate group-hover:text-zinc-400 transition-colors hover:text-white hover:underline block max-w-full text-left"
                    >
                        {track.artist}
                    </button>
                </div>
            </div>

            {/* Duration, Queue & Delete */}
            <div className="flex items-center gap-1">
                {track.duration && (
                    <span className="text-xs font-mono text-zinc-600 group-hover:text-zinc-500 transition-colors hidden sm:block mr-2">
                        {track.duration}
                    </span>
                )}
                {onAddToQueue && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onAddToQueue(track); }}
                        className="hover:bg-indigo-500/10 hover:text-indigo-400 text-zinc-600 p-2 rounded-full cursor-pointer transition-colors opacity-0 group-hover:opacity-100"
                        title="Add to Queue"
                    >
                        <QueueIcon className="w-4 h-4" />
                    </button>
                )}
                <button onClick={(e) => { e.stopPropagation(); onDelete(track.id); }} className="hover:bg-red-500/10 hover:text-red-400 text-zinc-600 p-2 rounded-full cursor-pointer transition-colors opacity-0 group-hover:opacity-100">
                    <XIcon className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};


interface CreativeSidebarProps {
    isOpen: boolean;
    onToggle: () => void;
    side?: 'left' | 'right';
    onSwapSides?: () => void;
    onNavigateToLookbook?: (moodRequest: MoodImageRequest) => void;
    onClearProject?: () => void;
    isActive?: boolean;
}

export const CreativeSidebar: React.FC<CreativeSidebarProps> = ({
    isOpen,
    onToggle,
    side = 'right',
    onSwapSides,
    onNavigateToLookbook,
    onClearProject,
    isActive = true // Default to true for backward compatibility
}) => {
    const { user } = useAuth();
    const { showToast } = useToast();

    // State for showing toggle handle on edge hover (when collapsed)
    const [isHandleVisible, setIsHandleVisible] = useState(false);

    // Portal Ref for Miniplayer
    const portalSlotRef = useRef<HTMLDivElement>(null);

    // Drag Controls for Popout
    const dragControls = useDragControls();
    const [popoutWidth, setPopoutWidth] = useState(480);

    // Tab State (Chat replaces Notes/Stickies, Users tab for owner only)
    const [activeTab, setActiveTab] = useState<'chat' | 'assets' | 'soundtrack' | 'users'>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('ezart_sidebar_active_tab');
            // Migrate old 'notes' to 'chat'
            if (saved === 'notes') return 'chat';
            return (saved === 'chat' || saved === 'assets' || saved === 'soundtrack' || saved === 'users') ? saved as any : 'assets';
        }
        return 'assets';
    });

    // Project & Asset State - use shared context
    const { selectedProjectId: selectedProject, setSelectedProjectId: setSelectedProject, projects, setProjects, currentUserRole, projectMembers } = useProject();
    const projectMembersRef = useRef(projectMembers);
    useEffect(() => { projectMembersRef.current = projectMembers; }, [projectMembers]);

    // Project permissions for collaborative features
    const permissions = useProjectPermissions(selectedProject || undefined);

    const [assets, setAssets] = useState<Shot[]>([]);
    const [selectedAsset, setSelectedAsset] = useState<Shot | null>(null);
    const [fullscreenAssetIndex, setFullscreenAssetIndex] = useState<number | null>(null);

    // Shared Player Refs for dynamic positioning
    const mainSlotRef = useRef<HTMLDivElement>(null);
    const controlsSlotRef = useRef<HTMLDivElement>(null);
    const miniSlotRef = useRef<HTMLDivElement>(null);
    const closedSlotRef = useRef<HTMLDivElement>(null);
    const constraintsRef = useRef<HTMLDivElement>(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolume] = useState(() => {
        const saved = localStorage.getItem('ezart_player_volume');
        return saved ? parseInt(saved) : 100;
    });
    const [isMuted, setIsMuted] = useState(() => {
        return localStorage.getItem('ezart_player_muted') === 'true';
    });
    const [isHoveringVolume, setIsHoveringVolume] = useState(false);
    const [showBackToTop, setShowBackToTop] = useState(false);
    const playlistScrollRef = useRef<HTMLDivElement>(null);
    const [playlistSearchOpen, setPlaylistSearchOpen] = useState(false);
    const [playlistSearchQuery, setPlaylistSearchQuery] = useState('');
    const playlistSearchInputRef = useRef<HTMLInputElement>(null);

    // Virtualized playlist - load tracks incrementally as user scrolls
    const TRACKS_PER_PAGE = 20;
    const [visibleTrackCount, setVisibleTrackCount] = useState(TRACKS_PER_PAGE);
    const loadMoreRef = useRef<HTMLDivElement>(null);
    const playlistScrollContainerRef = useRef<HTMLDivElement>(null);

    // Playlist & View States
    const [viewMode, setViewMode] = useState<'playlist' | 'search'>('playlist');
    const [playerMode, setPlayerMode] = useState<'video' | 'cover'>('cover');

    // Toggle state
    const [playlistTracks, setPlaylistTracks] = useState<{ id: string; title: string; artist: string; artistId?: string; cover: string; duration?: string; genre?: string }[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<{ id: string; title: string; artist: string; artistId?: string; cover: string; duration?: string; genre?: string }[]>([]);
    const [searchArtists, setSearchArtists] = useState<{ id: string; name: string; image: string }[]>([]);
    const [selectedArtist, setSelectedArtist] = useState<{ id: string; name: string; image?: string } | null>(null);
    const [artistAlbums, setArtistAlbums] = useState<any[]>([]);
    const [artistTopSongs, setArtistTopSongs] = useState<any[]>([]); // New State
    const [discographyFilter, setDiscographyFilter] = useState<'popular' | 'albums' | 'singles'>('popular'); // New State
    const [expandedAlbum, setExpandedAlbum] = useState<string | null>(null);
    const [radioTracks, setRadioTracks] = useState<{ id: string; title: string; artist: string; artistId?: string; cover: string; duration?: string; genre?: string }[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isLoadingRadio, setIsLoadingRadio] = useState(false);
    const [isGeneratingMood, setIsGeneratingMood] = useState(false);
    const [loadingPhase, setLoadingPhase] = useState('Listening...');
    const shouldAutoPlayRadio = useRef(false);
    const [deletedTracks, setDeletedTracks] = useState<any[]>([]); // Undo History
    const [isImportingCSV, setIsImportingCSV] = useState(false);
    const [csvImportProgress, setCsvImportProgress] = useState({ current: 0, total: 0 });
    const csvFileInputRef = useRef<HTMLInputElement>(null);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    // Queue System - tracks queued to play next (synced across project members)
    interface QueueTrack {
        id: string;
        title: string;
        artist: string;
        artistId?: string;
        cover: string;
        videoId?: string;
        duration?: string;
        addedBy: string;      // User ID who added to queue
        addedByName: string;  // Username for display
        addedAt: number;      // Timestamp
    }
    const [queue, setQueue] = useState<QueueTrack[]>([]);
    const queueRef = useRef<QueueTrack[]>([]);
    useEffect(() => { queueRef.current = queue; }, [queue]);

    // Track failed/restricted video IDs to exclude from future searches
    const failedVideoIds = useRef<Set<string>>(new Set());

    // Ref to hold the latest playNext function to prevent stale closures in YouTube player callback
    const playNextRef = useRef<() => void>(() => { });

    // Ref to track which videoId we last auto-advanced from (prevents double-advance race condition)
    const lastAdvancedFromVideoRef = useRef<string | null>(null);

    // Ref to hold the latest playOnYoutube function for listen along feature
    const playOnYoutubeRef = useRef<(track: any, index: number, fromSync?: boolean) => void>(() => { });

    // Track previous selectedProject to avoid resetting track index on unrelated updates
    const prevSelectedProjectRef = useRef<string | null>(null);

    // Robustly track the playing track ID for highlighting, independent of index shifts
    const [currentTrackId, setCurrentTrackId] = useState<string | null>(null);

    // Toast auto-hide effect
    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => setToast(null), 4000);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    // Cycle parameters for loading animation
    useEffect(() => {
        if (!isGeneratingMood) return;
        const phases = ['Listening to Audio...', 'Extracting Vibe...', 'Dreaming in Color...', 'Rendering Art...'];
        let i = 0;
        setLoadingPhase(phases[0]);
        const interval = setInterval(() => {
            i = (i + 1) % phases.length;
            setLoadingPhase(phases[i]);
        }, 1200); // Fast tempo
        return () => clearInterval(interval);
    }, [isGeneratingMood]);

    // Playback Context (Playlist vs Radio vs Search vs Album)
    const [activeContext, setActiveContext] = useState<'playlist' | 'radio' | 'search' | 'album'>('playlist');
    const [currentTrackIndex, setCurrentTrackIndex] = useState(-1);
    const currentTrackIndexRef = useRef(currentTrackIndex);
    useEffect(() => { currentTrackIndexRef.current = currentTrackIndex; }, [currentTrackIndex]);
    const [radioTrackIndex, setRadioTrackIndex] = useState(-1);
    const [searchTrackIndex, setSearchTrackIndex] = useState(-1);
    const [albumTrackIndex, setAlbumTrackIndex] = useState(-1);
    const [albumContextTracks, setAlbumContextTracks] = useState<any[]>([]);
    const [videoAspectRatio, setVideoAspectRatio] = useState(16 / 9); // Default 16:9, updated on video load

    // Calculate Total Duration
    const totalDuration = useMemo(() => {
        let totalSeconds = 0;
        playlistTracks.forEach(t => {
            if (!t.duration) return;
            // Handle numeric duration (seconds)
            if (typeof t.duration === 'number') {
                totalSeconds += t.duration;
                return;
            }
            // Handle string duration ("MM:SS" or "HH:MM:SS" or "SEC")
            if (typeof t.duration === 'string') {
                const parts = t.duration.split(':').map(Number);
                if (parts.length === 2 && !parts.some(isNaN)) { // MM:SS
                    totalSeconds += parts[0] * 60 + parts[1];
                } else if (parts.length === 3 && !parts.some(isNaN)) { // HH:MM:SS
                    totalSeconds += parts[0] * 3600 + parts[1] * 60 + parts[2];
                } else if (!isNaN(Number(t.duration))) {
                    totalSeconds += Number(t.duration);
                }
            }
        });

        if (totalSeconds === 0) return null;
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    }, [playlistTracks]);


    // Persistence: Load Player Mode
    useEffect(() => {
        if (user?.preferences?.playerMode) {
            setPlayerMode(user.preferences.playerMode);
        }
    }, [user?.preferences?.playerMode]);

    const updatePreferences = (prefs: any) => {
        if (typeof window !== 'undefined') {
            // Save relevant prefs
            if (prefs.playerMode) localStorage.setItem('ezart_player_mode', prefs.playerMode);
        }
    };

    const togglePlayerMode = () => {
        const newMode = playerMode === 'video' ? 'cover' : 'video';
        setPlayerMode(newMode);
        updatePreferences({ playerMode: newMode });
    };

    // ... (existing code)

    const addToPlaylist = (track: any) => {
        // Check permissions - only owner/editor can add to playlist in collaborative projects
        if (!permissions.canAddToPlaylist) {
            setToast({ message: 'You don\'t have permission to add songs to this playlist', type: 'error' });
            return;
        }

        // Prevent duplicates
        if (!playlistTracks.some(t => t.id === track.id)) {
            const newTracks = [...playlistTracks, track];
            setPlaylistTracks(newTracks);
            persistPlaylist(newTracks);
        }
        setSearchQuery('');
        setSearchResults([]);
        setViewMode('playlist'); // Switch back to playlist view
    };

    const handleRemoveTrack = (id: string) => {
        const trackToRemove = playlistTracks.find(t => t.id === id);
        if (trackToRemove) {
            setDeletedTracks(prev => [...prev, trackToRemove]);
        }
        const newTracks = playlistTracks.filter(t => t.id !== id);
        setPlaylistTracks(newTracks);
        persistPlaylist(newTracks);
    };

    const handleUndoDelete = () => {
        if (deletedTracks.length === 0) return;
        const trackRestored = deletedTracks[deletedTracks.length - 1];
        setDeletedTracks(prev => prev.slice(0, -1));

        const newTracks = [...playlistTracks, trackRestored];
        setPlaylistTracks(newTracks);
        persistPlaylist(newTracks);
    };

    // CSV Import Handler
    const handleCSVImport = () => {
        csvFileInputRef.current?.click();
    };

    const parseCSV = (text: string): any[] => {
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length < 2) return [];

        const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
        const tracks = [];

        for (let i = 1; i < lines.length; i++) {
            const values: string[] = [];
            let currentValue = '';
            let insideQuotes = false;

            // Parse CSV with proper quote handling
            for (let char of lines[i]) {
                if (char === '"') {
                    insideQuotes = !insideQuotes;
                } else if (char === ',' && !insideQuotes) {
                    values.push(currentValue.trim().replace(/^"|"$/g, ''));
                    currentValue = '';
                } else {
                    currentValue += char;
                }
            }
            values.push(currentValue.trim().replace(/^"|"$/g, ''));

            const row: any = {};
            headers.forEach((header, index) => {
                row[header] = values[index] || '';
            });

            tracks.push(row);
        }

        return tracks;
    };

    const handleCSVFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setIsImportingCSV(true);
        setCsvImportProgress({ current: 0, total: 0 });

        try {
            const text = await file.text();
            const csvTracks = parseCSV(text);

            if (csvTracks.length === 0) {
                setToast({ message: 'No tracks found in CSV file', type: 'error' });
                setIsImportingCSV(false);
                return;
            }

            // Flexible Header Resolution (moved outside loop for reuse)
            const resolveHeader = (row: any, candidates: string[]) => {
                const keys = Object.keys(row);
                for (const cand of candidates) {
                    if (row[cand]) return row[cand];
                }
                for (const cand of candidates) {
                    const match = keys.find(k => k.toLowerCase() === cand.toLowerCase());
                    if (match && row[match]) return row[match];
                }
                for (const cand of candidates) {
                    const match = keys.find(k => k.toLowerCase().includes(cand.toLowerCase()));
                    if (match && row[match]) return row[match];
                }
                return '';
            };

            // Pre-process CSV tracks to extract metadata and filter invalid entries
            const processedTracks = csvTracks.map((csvTrack, index) => ({
                index,
                trackName: resolveHeader(csvTrack, ['Track Name', 'Track', 'Title', 'Song', 'Name']),
                artistName: resolveHeader(csvTrack, ['Artist Name(s)', 'Artist Name', 'Artist', 'Band', 'Performer']),
                durationMs: parseInt(resolveHeader(csvTrack, ['Duration (ms)', 'Duration', 'Time']) || '0'),
                genre: resolveHeader(csvTrack, ['Genres', 'Genre', 'Style', 'Category'])
            }));

            // Pre-filter: Remove invalid tracks and duplicates before any API calls
            let currentTracks = [...playlistTracks];
            const existingTrackKeys = new Set(
                currentTracks.map(t => `${t.title.toLowerCase()}|${t.artist.toLowerCase()}`)
            );

            const validTracks: typeof processedTracks = [];
            let earlyFailCount = 0;
            let earlyDuplicateCount = 0;

            for (const track of processedTracks) {
                if (!track.trackName || !track.artistName) {
                    earlyFailCount++;
                    continue;
                }
                const key = `${track.trackName.toLowerCase()}|${track.artistName.toLowerCase()}`;
                if (existingTrackKeys.has(key)) {
                    earlyDuplicateCount++;
                    continue;
                }
                existingTrackKeys.add(key); // Prevent duplicates within CSV itself
                validTracks.push(track);
            }

            setCsvImportProgress({ current: 0, total: validTracks.length });

            let successCount = 0;
            let failCount = earlyFailCount;
            let duplicateCount = earlyDuplicateCount;
            let processedCount = 0;

            // Process a single track - returns the track object or null
            const processTrack = async (trackData: typeof validTracks[0]): Promise<any | null> => {
                const { trackName, artistName, durationMs, genre, index } = trackData;

                // Normalize: lowercase, replace special chars with space, collapse multiple spaces
                const normalize = (s: string) => s.toLowerCase().replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim();
                const artistNorm = normalize(artistName);
                const artistWords = artistNorm.split(/\s+/).filter(w => w.length > 1);
                const targetNorm = normalize(trackName);
                const targetWords = targetNorm.split(/\s+/).filter(w => w.length > 2);

                // Helper to check if an artist matches
                // Exclude common words that cause false matches
                const commonWords = ['the', 'a', 'an', 'and', 'or', 'of', 'in', 'on', 'at', 'to', 'for'];
                const isArtistMatch = (resultArtist: string) => {
                    const matchArtistNorm = normalize(resultArtist || '');

                    // Exact or substring match (most reliable)
                    if (artistNorm === matchArtistNorm) return true;
                    if (artistNorm.includes(matchArtistNorm) || matchArtistNorm.includes(artistNorm)) return true;

                    // Word matching - filter out common words and require significant matches
                    const significantArtistWords = artistWords.filter(w => !commonWords.includes(w) && w.length >= 3);
                    const matchArtistWords = matchArtistNorm.split(/\s+/).filter(w => w.length > 1 && !commonWords.includes(w));

                    // Require at least one significant word (3+ chars, not common) to match
                    const hasSignificantMatch = significantArtistWords.some(w => matchArtistNorm.includes(w)) ||
                        matchArtistWords.filter(w => w.length >= 3).some(w => artistNorm.includes(w));

                    return hasSignificantMatch;
                };

                // Helper to check if title matches (for search results - can be looser)
                const isTitleMatch = (resultTitle: string) => {
                    const matchNorm = normalize(resultTitle || '');
                    return targetNorm.includes(matchNorm) || matchNorm.includes(targetNorm) ||
                        targetWords.some(w => matchNorm.includes(w));
                };

                // Stricter title match for discography lookup - requires most words to match
                const isStrictTitleMatch = (resultTitle: string) => {
                    const matchNorm = normalize(resultTitle || '');
                    const matchWords = matchNorm.split(/\s+/).filter(w => w.length > 2);

                    // Exact or near-exact match
                    if (targetNorm === matchNorm) return true;
                    if (targetNorm.includes(matchNorm) || matchNorm.includes(targetNorm)) return true;

                    // Require at least 60% of significant words to match
                    if (targetWords.length === 0) return false;
                    const matchedWords = targetWords.filter(w => matchNorm.includes(w));
                    const matchRatio = matchedWords.length / targetWords.length;

                    // Also check reverse - how many of the result's words are in our target
                    const reverseMatched = matchWords.filter(w => targetNorm.includes(w));
                    const reverseRatio = matchWords.length > 0 ? reverseMatched.length / matchWords.length : 0;

                    // Both directions should have good match
                    return matchRatio >= 0.6 && reverseRatio >= 0.5;
                };

                // Helper to find a valid match from search results
                const findMatch = (tracks: any[]) => {
                    const variantKeywords = ['remix', 'live', 'acoustic', 'instrumental', 'cover', 'mix', 'edit', 'version', 'remaster'];

                    // Debug: Log first few tracks to see what we're matching against
                    if (tracks.length > 0) {
                        console.log(`[CSV Import] Searching for: "${trackName}" by "${artistName}"`);
                        console.log(`[CSV Import] Normalized target: "${targetNorm}" | artist: "${artistNorm}"`);
                        tracks.slice(0, 3).forEach((t, i) => {
                            const artistMatch = isArtistMatch(t.artist);
                            const titleMatch = isTitleMatch(t.title);
                            console.log(`  ${i + 1}. "${t.title}" by "${t.artist}" - artist:${artistMatch}, title:${titleMatch}`);
                        });
                    }

                    const validCandidates = tracks
                        .filter((t: any) => isArtistMatch(t.artist) && isTitleMatch(t.title))
                        .map((t: any) => {
                            let score = 50; // Base score
                            const tTitle = t.title.toLowerCase();
                            const csvTitle = trackName.toLowerCase();

                            // Exact title match gets big bonus
                            if (normalize(tTitle) === targetNorm) {
                                score += 50;
                            } else if (tTitle === csvTitle) {
                                score += 30;
                            }

                            // STRONG penalty for variant keywords not in CSV title
                            variantKeywords.forEach(keyword => {
                                if (tTitle.includes(keyword) && !csvTitle.includes(keyword)) {
                                    score -= 25; // Strong penalty (was 5)
                                }
                            });

                            // Bonus if CSV has variant keyword and result matches it
                            // Penalty if CSV has variant keyword but result doesn't
                            variantKeywords.forEach(keyword => {
                                if (csvTitle.includes(keyword) && tTitle.includes(keyword)) {
                                    score += 20; // UserIcon specifically wants this variant
                                } else if (csvTitle.includes(keyword) && !tTitle.includes(keyword)) {
                                    score -= 20; // UserIcon wants variant but this isn't it
                                }
                            });

                            return { ...t, score };
                        });

                    if (validCandidates.length === 0) return null;

                    // Sort by score descending (highest score = best match)
                    validCandidates.sort((a: any, b: any) => b.score - a.score);

                    // Log for debugging
                    if (validCandidates.length > 1) {
                        console.log(`[CSV Import] Candidates for "${trackName}":`);
                        validCandidates.slice(0, 3).forEach((c: any, i: number) => {
                            console.log(`  ${i + 1}. "${c.title}" (score: ${c.score})`);
                        });
                    }

                    return validCandidates[0];
                };

                // Try multiple search strategies
                const searchStrategies = [
                    `${trackName} ${artistName}`,        // Original: "Song Artist"
                    `${artistName} ${trackName}`,        // Reversed: "Artist Song"
                    `"${artistName}" ${trackName}`,      // Quoted artist
                ];

                let foundArtistId: string | null = null;

                for (let i = 0; i < searchStrategies.length; i++) {
                    const query = searchStrategies[i].replace(/[&<>"']/g, "");

                    try {
                        const { data, error } = await supabase.functions.invoke('search-music', {
                            body: { query }
                        });

                        if (data?.tracks && data.tracks.length > 0) {
                            if (i === 0) {
                                console.log(`[CSV Import] "${trackName}" by "${artistName}" - Found ${data.tracks.length} results`);
                            } else {
                                console.log(`[CSV Import] "${trackName}" by "${artistName}" - Retry #${i + 1} found ${data.tracks.length} results`);
                            }

                            const bestMatch = findMatch(data.tracks);

                            // Only accept match if score is good enough (>= 50)
                            // Low scores indicate variant mismatch (e.g., remix when we want original)
                            if (bestMatch && bestMatch.score >= 50) {
                                console.log(`[CSV Import] MATCHED "${trackName}" → "${bestMatch.title}" by "${bestMatch.artist}" (score: ${bestMatch.score})`);
                                return {
                                    id: bestMatch.id || `csv-${Date.now()}-${index}`,
                                    title: bestMatch.title || trackName,
                                    artist: bestMatch.artist || artistName,
                                    artistId: bestMatch.artistId || foundArtistId || undefined, // Include artistId for direct lookup
                                    cover: bestMatch.cover || '',
                                    duration: bestMatch.duration || (durationMs > 0 ? formatTime(durationMs / 1000) : undefined),
                                    genre: bestMatch.genre || genre || undefined
                                };
                            } else if (bestMatch) {
                                console.log(`[CSV Import] "${trackName}" - Best match "${bestMatch.title}" has low score (${bestMatch.score}), trying discography...`);
                            }

                            // Save artist ID for discography fallback
                            if (!foundArtistId) {
                                const artistTrack = data.tracks.find((t: any) => isArtistMatch(t.artist) && t.artistId);
                                if (artistTrack?.artistId) {
                                    foundArtistId = artistTrack.artistId;
                                }
                            }

                            // Only log rejection on first attempt
                            if (i === 0) {
                                console.log(`[CSV Import] No match in attempt ${i + 1}, trying alternate search...`);
                            }
                        }
                    } catch (error) {
                        console.error(`[CSV Import] Search attempt ${i + 1} failed:`, error);
                    }
                }

                // FINAL FALLBACK: If we found the artist but not the song, search their discography
                if (foundArtistId) {
                    try {
                        console.log(`[CSV Import] "${trackName}" - Searching artist discography (ID: ${foundArtistId})...`);
                        const { data: discogData } = await supabase.functions.invoke('search-music', {
                            body: { artistId: foundArtistId }
                        });

                        if (discogData?.albums) {
                            // Collect all matching tracks from discography
                            const allMatchingTracks: any[] = [];
                            const variantKeywords = ['live', 'remix', 'acoustic', 'instrumental', 'demo', 'version', 'edit', 'mix', 'remaster'];

                            for (const album of discogData.albums) {
                                if (album.tracks) {
                                    for (const track of album.tracks) {
                                        if (isStrictTitleMatch(track.title)) {
                                            // Score the match - prefer exact matches, penalize variants
                                            const trackTitleLower = track.title.toLowerCase();
                                            const targetLower = trackName.toLowerCase();
                                            let score = 0;

                                            // Exact match gets highest priority
                                            if (normalize(track.title) === targetNorm) {
                                                score = 100;
                                            } else if (trackTitleLower.includes(targetLower) || targetLower.includes(trackTitleLower.split('(')[0].trim())) {
                                                score = 50;
                                            }

                                            // Penalize if track has variant keywords that target doesn't have
                                            for (const keyword of variantKeywords) {
                                                if (trackTitleLower.includes(keyword) && !targetLower.includes(keyword)) {
                                                    score -= 20;
                                                }
                                            }

                                            allMatchingTracks.push({ track, album, score });
                                        }
                                    }
                                }
                            }

                            // Sort by score descending and pick best match
                            if (allMatchingTracks.length > 0) {
                                allMatchingTracks.sort((a, b) => b.score - a.score);
                                const best = allMatchingTracks[0];
                                console.log(`[CSV Import] MATCHED via discography "${trackName}" → "${best.track.title}" by "${best.track.artist}" (score: ${best.score})`);
                                return {
                                    id: best.track.id || `csv-${Date.now()}-${index}`,
                                    title: best.track.title || trackName,
                                    artist: best.track.artist || artistName,
                                    artistId: best.track.artistId || foundArtistId || undefined, // Include artistId for direct lookup
                                    cover: best.track.cover || best.album.cover || '',
                                    duration: best.track.duration || (durationMs > 0 ? formatTime(durationMs / 1000) : undefined),
                                    genre: best.track.genre || genre || undefined
                                };
                            }
                        }
                    } catch (error) {
                        console.error(`[CSV Import] Discography lookup failed:`, error);
                    }
                }

                // All strategies failed
                console.log(`[CSV Import] REJECTED "${trackName}" by "${artistName}" - No match found after ${searchStrategies.length} search attempts`);
                return null;
            };

            // Process tracks in parallel batches of 3, maintaining original order
            const BATCH_SIZE = 3;
            const BATCH_DELAY = 100; // ms between batches
            const PERSIST_INTERVAL = 10; // Persist every N successful tracks
            let pendingPersist = 0;

            for (let i = 0; i < validTracks.length; i += BATCH_SIZE) {
                const batch = validTracks.slice(i, i + BATCH_SIZE);

                // Process batch in parallel, collecting results with their original indices
                const batchResults = await Promise.all(
                    batch.map(async (trackData, batchIndex) => {
                        const result = await processTrack(trackData);
                        // Update progress as each completes
                        processedCount++;
                        setCsvImportProgress({ current: processedCount, total: validTracks.length });
                        return { result, originalIndex: batchIndex };
                    })
                );

                // Sort by original index to maintain CSV order, then add to playlist
                batchResults
                    .sort((a, b) => a.originalIndex - b.originalIndex)
                    .forEach(({ result }) => {
                        if (result) {
                            // Post-search duplicate check
                            const postSearchDuplicate = currentTracks.some(t =>
                                t.title.toLowerCase() === result.title.toLowerCase() &&
                                t.artist.toLowerCase() === result.artist.toLowerCase()
                            );

                            if (postSearchDuplicate) {
                                duplicateCount++;
                            } else {
                                currentTracks = [...currentTracks, result];
                                successCount++;
                                pendingPersist++;
                            }
                        } else {
                            failCount++;
                        }
                    });

                // Update UI with ordered tracks after batch completes
                setPlaylistTracks([...currentTracks]);

                // Batch persistence - save every PERSIST_INTERVAL tracks or on last batch
                if (pendingPersist >= PERSIST_INTERVAL || i + BATCH_SIZE >= validTracks.length) {
                    if (pendingPersist > 0) {
                        persistPlaylist(currentTracks);
                        pendingPersist = 0;
                    }
                }

                // Small delay between batches to respect API limits
                if (i + BATCH_SIZE < validTracks.length) {
                    await new Promise(resolve => setTimeout(resolve, BATCH_DELAY));
                }
            }

            // Build detailed result message
            const parts = [];
            if (successCount > 0) parts.push(`${successCount} imported`);
            if (duplicateCount > 0) parts.push(`${duplicateCount} skipped (duplicates)`);
            if (failCount > 0) parts.push(`${failCount} failed`);

            if (successCount > 0) {
                setToast({ message: parts.join(', '), type: 'success' });
            } else if (duplicateCount > 0 && failCount === 0) {
                setToast({ message: `All ${duplicateCount} tracks already in playlist`, type: 'success' });
            } else {
                setToast({ message: 'Failed to import tracks. Check CSV format.', type: 'error' });
            }

            setViewMode('playlist');
        } catch (error) {
            console.error('CSV import failed:', error);
            showToast('Failed to import CSV file. Please check the file format.', 'error');
        } finally {
            setIsImportingCSV(false);
            setCsvImportProgress({ current: 0, total: 0 });
            // Reset file input
            if (csvFileInputRef.current) {
                csvFileInputRef.current.value = '';
            }
        }
    };

    // YouTube Playback State

    const [playingTrack, setPlayingTrack] = useState<{ name: string; artist: string; artistId?: string; image: string; videoId?: string; duration?: string; genre?: string } | null>(null);
    const playingTrackRef = useRef<{ name: string; artist: string; artistId?: string; image: string; videoId?: string; duration?: string; genre?: string } | null>(null);
    useEffect(() => { playingTrackRef.current = playingTrack; }, [playingTrack]);
    const isPlayingRef = useRef(false);
    useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
    // Track programmatic seeks to avoid re-broadcasting them as user seeks
    const lastProgrammaticSeekRef = useRef<number>(0);
    // Track what we received from sync (to avoid echo-back of same state, but allow new user actions)
    const lastSyncReceivedRef = useRef<{ timestamp: number; trackId: string; isPlaying: boolean }>({ timestamp: 0, trackId: '', isPlaying: false });
    const [loadingVideo, setLoadingVideo] = useState(false);
    const [player, setPlayer] = useState<any>(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isDraggingProgress, setIsDraggingProgress] = useState(false);

    // Playback Control Utilities
    const [isShuffle, setIsShuffle] = useState(false);
    const [repeatOne, setRepeatOne] = useState(false); // Default: Loop All (Implicit)
    const [playbackHistory, setPlaybackHistory] = useState<number[]>([]);

    // Discord Rich Presence
    const discord = useDiscordRPC();
    const [showDiscordModal, setShowDiscordModal] = useState(false);
    const discordConnectedRef = useRef(false);

    // Keep ref in sync with discord state for setTimeout callbacks
    useEffect(() => {
        discordConnectedRef.current = discord.connected;
    }, [discord.connected]);

    // Rate Limit / Debounce Ref for Discord
    const lastPresenceUpdateRef = useRef<{
        track: string;
        isPaused: boolean;
        startTimestamp: number;
    } | null>(null);

    // Auto-connect to Discord when playback starts
    useEffect(() => {
        if (isPlaying && !discord.connected && !discord.connecting) {
            discord.connect();
        }
    }, [isPlaying, discord.connected, discord.connecting]);


    // --- Listen Along Logic ---
    const [isListeningAlong, setIsListeningAlong] = useState(false);
    const [hostId, setHostId] = useState<string | null>(null);
    const [listeners, setListeners] = useState<string[]>([]); // Track who's listening to us

    // Master clock state for precise sync
    // trackStartTime: server timestamp when current track started playing (at position 0)
    // If track was seeked to 30s, trackStartTime = Date.now() - 30000
    const masterClockRef = useRef<{
        trackId: string | null;
        trackStartTime: number;  // Server timestamp when track "started" at position 0
        isPaused: boolean;
        pausedAt: number | null;  // When playback was paused (to calculate position at pause)
    }>({
        trackId: null,
        trackStartTime: 0,
        isPaused: true,
        pausedAt: null
    });

    // ===== UNIFIED SYNC HELPERS =====
    // These functions handle music sync for both Listen Along and Project Sync

    /**
     * Update master clock when track changes
     */
    const updateMasterClockForNewTrack = useCallback((trackId: string, trackTitle: string) => {
        masterClockRef.current = {
            trackId,
            trackStartTime: Date.now(),
            isPaused: !isPlaying,
            pausedAt: isPlaying ? null : Date.now()
        };
        console.log('🎵 Master clock initialized for NEW track:', trackTitle, '(starting at 0)');
    }, [isPlaying]);

    /**
     * Update master clock on play/pause changes
     */
    const updateMasterClockPlayState = useCallback(() => {
        const clock = masterClockRef.current;
        if (isPlaying && clock.isPaused) {
            // Resuming from pause
            if (clock.pausedAt) {
                const pauseDuration = Date.now() - clock.pausedAt;
                clock.trackStartTime += pauseDuration;
            }
            clock.isPaused = false;
            clock.pausedAt = null;
            console.log('🎵 Master clock resumed');
        } else if (!isPlaying && !clock.isPaused) {
            // Pausing
            clock.isPaused = true;
            clock.pausedAt = Date.now();
            console.log('🎵 Master clock paused');
        }
    }, [isPlaying]);

    /**
     * Sync player to received state (used by Project Sync receivers).
     * Uses actualPlayerVideoId from YouTube for reliable track detection,
     * playOnYoutubeRef for proper track loading, and graduated rate
     * adjustment for smooth heartbeat drift correction.
     */
    const syncPlayerToState = useCallback((payload: {
        trackId: string;
        track: any;
        isPlaying: boolean;
        masterClock: any;
        sentAt: number;
        seeked?: boolean;
        isHeartbeat?: boolean;
    }) => {
        const { trackId, track, isPlaying: remoteIsPlaying, masterClock, sentAt, seeked, isHeartbeat } = payload;
        if (!track) return;

        // Latency compensation
        const now = Date.now();
        const networkLatency = sentAt ? (now - sentAt) : 0;
        const latencyCompMs = networkLatency / 2; // Half RTT

        // Check what YouTube is ACTUALLY playing (more reliable than React state)
        let actualPlayerVideoId: string | null = null;
        try {
            const videoData = playerRef.current?.getVideoData?.();
            actualPlayerVideoId = videoData?.video_id || null;
        } catch (e) { /* Player not ready */ }

        const isNewTrack = !actualPlayerVideoId || actualPlayerVideoId !== (track.videoId || trackId);

        // ── HEARTBEATS: graduated drift correction ──
        if (isHeartbeat) {
            if (isNewTrack) return; // Player hasn't loaded track yet
            if (!remoteIsPlaying) return; // No drift correction when paused

            if (playerRef.current && masterClock?.trackStartTime) {
                try {
                    const referenceTime = (masterClock.isPaused && masterClock.pausedAt)
                        ? masterClock.pausedAt : now;
                    const basePosition = (referenceTime - masterClock.trackStartTime) / 1000;
                    const targetPosition = basePosition + (latencyCompMs / 1000);
                    const currentPosition = playerRef.current.getCurrentTime?.() || 0;
                    const drift = targetPosition - currentPosition; // Positive = behind, Negative = ahead
                    const absDrift = Math.abs(drift);

                    if (absDrift > 3) {
                        // Extreme drift: hard seek
                        console.log('Project Sync: Extreme drift (' + drift.toFixed(1) + 's) - hard sync');
                        lastProgrammaticSeekRef.current = Date.now();
                        lastSyncReceivedRef.current = { timestamp: Date.now(), trackId, isPlaying: remoteIsPlaying };
                        playerRef.current.seekTo?.(targetPosition, true);
                        playerRef.current.setPlaybackRate?.(1.0);
                    } else if (absDrift > 0.3) {
                        // Graduated rate adjustment (nearly invisible to ear)
                        let rate: number;
                        if (absDrift < 1.0) {
                            rate = drift > 0 ? 1.02 : 0.98;  // Gentle
                        } else if (absDrift < 2.0) {
                            rate = drift > 0 ? 1.04 : 0.96;  // Moderate
                        } else {
                            rate = drift > 0 ? 1.06 : 0.94;  // Stronger
                        }
                        const currentRate = playerRef.current.getPlaybackRate?.() || 1.0;
                        if (currentRate !== rate) {
                            console.log(`Project Sync: Drift ${drift.toFixed(2)}s - rate ${rate}x`);
                            playerRef.current.setPlaybackRate?.(rate);
                        }
                    } else {
                        // Within 0.3s - perfect sync, return to normal speed
                        const currentRate = playerRef.current.getPlaybackRate?.() || 1.0;
                        if (currentRate !== 1.0) {
                            console.log(`Project Sync: Synced (${drift.toFixed(2)}s) - rate 1.0x`);
                            playerRef.current.setPlaybackRate?.(1.0);
                        }
                    }
                } catch (e) { /* Player might not be fully ready */ }
            }
            return;
        }

        // ── ACTIONS: track changes, seeks, play/pause ──
        console.log('Project Sync: Action received', {
            trackId, title: track?.title, isPlaying: remoteIsPlaying,
            isNewTrack, seeked: !!seeked
        });

        // Update masterClockRef to match received state
        if (masterClock?.trackStartTime) {
            masterClockRef.current = {
                trackId,
                trackStartTime: masterClock.trackStartTime,
                isPaused: masterClock.isPaused || false,
                pausedAt: masterClock.pausedAt || null
            };
        }

        lastSyncReceivedRef.current = { timestamp: Date.now(), trackId, isPlaying: remoteIsPlaying };

        // Helper: seek to the correct position based on master clock
        const seekToPosition = (delay = 0) => {
            if (!playerRef.current || !masterClock?.trackStartTime) return;
            const doSeek = () => {
                const seekNow = Date.now();
                const referenceTime = (!remoteIsPlaying && masterClock.pausedAt)
                    ? masterClock.pausedAt : seekNow;
                const basePos = (referenceTime - masterClock.trackStartTime) / 1000;
                // Recalculate latency at seek time for delayed seeks
                const seekLatency = sentAt ? ((seekNow - sentAt) / 2) : 0;
                const targetPos = basePos + (seekLatency / 1000);
                lastProgrammaticSeekRef.current = Date.now();
                try { playerRef.current?.seekTo(Math.max(0, targetPos), true); } catch (e) { }
            };
            if (delay > 0) { setTimeout(doSeek, delay); } else { doSeek(); }
        };

        // Find track in playlist by videoId or database id
        const existingTrackIndex = playlistTracksRef.current.findIndex(t =>
            (t as any).videoId === trackId || t.id === trackId
        );

        if (existingTrackIndex !== -1) {
            // Track exists in playlist
            setCurrentTrackIndex(existingTrackIndex);

            if (isNewTrack) {
                // Load track via playOnYoutube (handles video resolution)
                playOnYoutubeRef.current(playlistTracksRef.current[existingTrackIndex], existingTrackIndex, true);
                seekToPosition(300);
                if (!remoteIsPlaying) {
                    setTimeout(() => { try { playerRef.current?.pauseVideo(); } catch (e) { } }, 300);
                }
            } else if (seeked) {
                // Seek only (same track)
                seekToPosition();
                if (remoteIsPlaying) {
                    setIsPlaying(true);
                    try { playerRef.current?.playVideo(); } catch (e) { }
                } else {
                    setIsPlaying(false);
                    try { playerRef.current?.pauseVideo(); } catch (e) { }
                }
            } else {
                // Play/pause state change
                if (remoteIsPlaying) {
                    setIsPlaying(true);
                    try { playerRef.current?.playVideo(); } catch (e) { }
                } else {
                    setIsPlaying(false);
                    try { playerRef.current?.pauseVideo(); } catch (e) { }
                }
            }
        } else {
            // Track not in playlist - set playingTrack directly
            if (isNewTrack && track) {
                setPlayingTrack({
                    name: track.title,
                    artist: track.artist,
                    image: track.cover,
                    videoId: track.videoId
                });
            }

            if (isNewTrack) {
                seekToPosition(300);
            } else if (seeked) {
                seekToPosition();
            }

            if (remoteIsPlaying) {
                setIsPlaying(true);
                try { playerRef.current?.playVideo(); } catch (e) { }
            } else {
                setIsPlaying(false);
                try { playerRef.current?.pauseVideo(); } catch (e) { }
            }
        }

        // Reset playback rate to 1.0 on any action (clean slate)
        try { playerRef.current?.setPlaybackRate?.(1.0); } catch (e) { }
    }, [setCurrentTrackIndex, setIsPlaying, setPlayingTrack]);

    // 1. Host Logic: Broadcast State
    // Keep a ref to the channel so we can broadcast from interval
    const hostChannelRef = useRef<any>(null);
    // Ref to broadcast function so we can call it when listeners join
    const broadcastStateRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        if (!user || isListeningAlong) return; // Don't broadcast if listening to someone else

        // CRITICAL: Don't set up Listen Along if user is in a project - Project Sync takes priority
        if (selectedProject && currentUserRole) return;

        const roomKey = user.username || user.id;
        const channel = supabase.channel(`listening_room:${roomKey}`, {
            config: { presence: { key: user.username || user.id } }
        });
        hostChannelRef.current = channel;

        // Track presence (who's listening)
        channel.on('presence', { event: 'sync' }, () => {
            const state = channel.presenceState();
            const listenerNames = Object.keys(state).filter(name => name !== roomKey);
            console.log('Host: Listeners updated:', listenerNames);
            setListeners(listenerNames);
        });

        channel.on('presence', { event: 'join' }, ({ key, newPresences }) => {
            console.log('Host: Listener joined:', key, '- sending current state');
            // Broadcast current state immediately when a new listener joins
            broadcastStateRef.current?.();
        });

        channel.on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
            console.log('Host: Listener left:', key);
        });

        channel.subscribe(async (status) => {
            if (status === 'SUBSCRIBED') {
                console.log('Host: Broadcasting on channel:', `listening_room:${roomKey}`);
                // Track our own presence (so we can filter ourselves out)
                await channel.track({ online_at: new Date().toISOString() });
            }
        });

        return () => {
            supabase.removeChannel(channel);
            hostChannelRef.current = null;
            setListeners([]);
        };
    }, [user, isListeningAlong, selectedProject, currentUserRole]);

    // Broadcast state changes and periodically for position sync
    // Use a ref to track listeners so broadcastState can access latest value
    const listenersRef = useRef<string[]>([]);
    useEffect(() => { listenersRef.current = listeners; }, [listeners]);

    useEffect(() => {
        if (!user || isListeningAlong || !hostChannelRef.current) return;

        // If user is in a project with a role, Project Sync controls the master clock - don't interfere
        // CRITICAL: Use stable state (selectedProject + currentUserRole) instead of projectSyncChannelRef.current
        // The ref is briefly null during channel teardown/setup, which lets Listen Along corrupt masterClockRef
        if (selectedProject && currentUserRole) return;

        const track = playlistTracks[currentTrackIndex ?? -1];
        if (!track) return;

        // Use unified sync helpers
        if (masterClockRef.current.trackId !== track.id) {
            updateMasterClockForNewTrack(track.id, track.title);
        }
        updateMasterClockPlayState();
        const clock = masterClockRef.current;

        // Capture stable state for use in broadcastState closure (interval function)
        const isInProjectWithRole = !!(selectedProject && currentUserRole);

        const broadcastState = () => {
            if (currentTrackIndex === undefined || currentTrackIndex === -1 || !playlistTracks[currentTrackIndex]) return;
            if (!hostChannelRef.current) return;

            // CRITICAL: If user is in a project, defer to Project Sync - don't broadcast Listen Along clock
            // Use stable state check (captured in effect scope) instead of ref which can be briefly null
            if (isInProjectWithRole) {
                console.log('Host: Skipping Listen Along broadcast - Project Sync is active');
                return;
            }

            // Note: We broadcast regardless of listener count because presence sync can be unreliable
            // The overhead is negligible and ensures listeners always get updates

            const track = playlistTracks[currentTrackIndex];
            const clock = masterClockRef.current;

            console.log('Host: Broadcasting master clock - track:', track.title,
                'startTime:', clock.trackStartTime,
                'isPaused:', clock.isPaused);

            hostChannelRef.current.send({
                type: 'broadcast',
                event: 'player_update',
                payload: {
                    trackId: track.id,
                    track: track, // Send full track object so listener can add it
                    isPlaying: isPlaying,
                    // Master clock data
                    masterClock: {
                        trackStartTime: clock.trackStartTime,  // When track "started" at pos 0
                        isPaused: clock.isPaused,
                        pausedAt: clock.pausedAt,
                    },
                    sentAt: Date.now(),  // For latency compensation
                    project_id: selectedProject,  // For project membership detection
                    context: activeContext,
                    host: user.username || user.id
                }
            });
        };

        // Store ref so presence handler can call it when new listeners join
        broadcastStateRef.current = broadcastState;

        // Broadcast immediately on state change
        broadcastState();

        // Broadcast frequently for tight music sync (every 1.5 seconds)
        const intervalId = isPlaying ? setInterval(broadcastState, 1500) : undefined;

        return () => {
            if (intervalId) clearInterval(intervalId);
            broadcastStateRef.current = null;
        };
    }, [user, isPlaying, currentTrackIndex, activeContext, isListeningAlong, player, playlistTracks, selectedProject, currentUserRole]); // Dep array triggers broadcast

    // 2. Listener Logic: Join Channel
    // Use refs to track state and avoid stale closures
    const listenAlongTrackRef = useRef<string | null>(null);
    const listenAlongSyncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const listenerChannelRef = useRef<any>(null); // Store channel ref so we can close it on X click
    const playlistTracksRef = useRef(playlistTracks);
    const playerRef = useRef(player);

    // State for host's project (for auto-detection of membership)
    const [hostProjectId, setHostProjectId] = useState<string | null>(null);
    const [isHostProjectMember, setIsHostProjectMember] = useState(false);
    const checkedMembershipRef = useRef<string | null>(null); // Track which project we've checked

    // Keep refs updated
    useEffect(() => { playlistTracksRef.current = playlistTracks; }, [playlistTracks]);
    useEffect(() => { playerRef.current = player; }, [player]);

    // Reset visible track count when playlist changes significantly (e.g., new project loaded)
    useEffect(() => {
        setVisibleTrackCount(TRACKS_PER_PAGE);
    }, [selectedProject?.id]);

    // Load more tracks function
    const loadMoreTracks = useCallback(() => {
        if (visibleTrackCount < playlistTracks.length) {
            setVisibleTrackCount(prev => Math.min(prev + TRACKS_PER_PAGE, playlistTracks.length));
        }
    }, [visibleTrackCount, playlistTracks.length]);

    // Intersection observer to load more tracks as user scrolls
    useEffect(() => {
        if (!loadMoreRef.current) return;

        // Use viewport as root (null) for more reliable intersection detection
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && visibleTrackCount < playlistTracks.length) {
                    setVisibleTrackCount(prev => Math.min(prev + TRACKS_PER_PAGE, playlistTracks.length));
                }
            },
            {
                root: null, // Use viewport - more reliable across different scroll contexts
                threshold: 0.1,
                rootMargin: '200px' // Start loading earlier
            }
        );

        observer.observe(loadMoreRef.current);

        return () => observer.disconnect();
    }, [visibleTrackCount, playlistTracks.length]);

    useEffect(() => {
        // Check both hash params (old format) and search params (new format)
        const hash = window.location.hash;
        const hashParams = new URLSearchParams(hash.split('?')[1]);
        const searchParams = new URLSearchParams(window.location.search);
        const targetUser = searchParams.get('listen_to') || hashParams.get('listen_to');

        console.log('Listen Along check - targetUser:', targetUser, 'user:', user?.username);

        // Check against username OR id to be safe, but primarily username
        if (targetUser && user && targetUser !== user.username && targetUser !== user.id) {
            console.log('Listener: Joining listening room:', targetUser);
            setIsListeningAlong(true);
            setHostId(targetUser);

            const listenerName = user.username || user.id;
            const channel = supabase.channel(`listening_room:${targetUser}`, {
                config: { presence: { key: listenerName } }
            });
            listenerChannelRef.current = channel; // Store for cleanup on X click

            // Track if we've received any broadcast from host
            let receivedFirstBroadcast = false;

            // Timeout to disconnect if host stops broadcasting
            let hostTimeoutId: NodeJS.Timeout | null = null;
            const resetHostTimeout = () => {
                if (hostTimeoutId) clearTimeout(hostTimeoutId);
                // Use shorter timeout for initial connection, longer for ongoing
                const timeout = receivedFirstBroadcast ? 30000 : 8000;
                hostTimeoutId = setTimeout(() => {
                    if (!receivedFirstBroadcast) {
                        // Host never responded - they're not online
                        console.log('Listener: Host is not broadcasting - disconnecting');
                        setToast({ type: 'error', message: `@${targetUser} is not currently playing music` });
                    } else {
                        console.log('Listener: Host stopped broadcasting - disconnecting');
                    }
                    supabase.removeChannel(channel);
                    listenerChannelRef.current = null;
                    listenAlongTrackRef.current = null;
                    setIsListeningAlong(false);
                    setHostId(null);
                    setIsPlaying(false);
                    // Actually pause the player
                    if (playerRef.current) {
                        try { playerRef.current.pauseVideo(); } catch (e) { }
                    }
                    // Clear URL param
                    const url = new URL(window.location.href);
                    url.searchParams.delete('listen_to');
                    window.history.replaceState(null, '', url.pathname + url.search);
                }, timeout);
            };

            channel.on('broadcast', { event: 'player_update' }, async (payload) => {
                const { trackId, track, isPlaying: hostPlaying, masterClock, seeked, sentAt, project_id } = payload.payload;

                // Mark that host is actively broadcasting
                receivedFirstBroadcast = true;
                // Reset timeout on any message from host
                resetHostTimeout();

                // Auto-detect project membership: if host is broadcasting from a project we're a member of,
                // switch from Listen Along to Project Member mode (full control)
                if (project_id && checkedMembershipRef.current !== project_id) {
                    checkedMembershipRef.current = project_id;
                    setHostProjectId(project_id);

                    try {
                        const access = await ProjectMemberService.checkProjectAccess(project_id);
                        if (access.hasAccess) {
                            console.log('Listen Along: User is a member of host\'s project, switching to Project Member mode');
                            setIsHostProjectMember(true);

                            // Disconnect from Listen Along
                            if (listenerChannelRef.current) {
                                supabase.removeChannel(listenerChannelRef.current);
                                listenerChannelRef.current = null;
                            }
                            listenAlongTrackRef.current = null;
                            setIsListeningAlong(false);
                            setHostId(null);

                            // Clear URL param
                            const url = new URL(window.location.href);
                            url.searchParams.delete('listen_to');
                            window.history.replaceState(null, '', url.pathname + url.search);

                            // Switch to the project (this will set up project sync channel)
                            setSelectedProject(project_id);
                            setToast({ type: 'success', message: 'Switched to Project Member mode - you have full control!' });
                            return; // Exit handler - project sync will take over
                        } else {
                            setIsHostProjectMember(false);
                        }
                    } catch (error) {
                        console.error('Failed to check project access:', error);
                        setIsHostProjectMember(false);
                    }
                }

                if (!track) {
                    console.log('Listener: No track data received from host');
                    return;
                }

                // Check if this is a new track FIRST - before any other logic
                const isNewTrack = listenAlongTrackRef.current !== trackId;

                if (isNewTrack) {
                    console.log('Listener: NEW TRACK detected:', track.title, '(was:', listenAlongTrackRef.current, ')');
                }

                // If host stopped playing, pause our playback but stay connected
                if (!hostPlaying) {
                    console.log('Listener: Host paused playback');
                    setIsPlaying(false);
                    if (playerRef.current) {
                        try { playerRef.current.pauseVideo(); } catch (e) { }
                    }
                    return;
                }

                // MASTER CLOCK: Calculate exact position with latency compensation
                // Position = (now - trackStartTime) / 1000 + (networkLatency / 2000)
                // The trackStartTime is when the track "started" at position 0
                let targetPosition: number | undefined;
                if (masterClock && masterClock.trackStartTime) {
                    const now = Date.now();
                    const basePosition = (now - masterClock.trackStartTime) / 1000;

                    // Latency compensation: add half of round-trip time
                    const networkLatency = sentAt ? (now - sentAt) : 0;
                    const latencyCompensation = networkLatency / 2000; // Half RTT in seconds
                    targetPosition = basePosition + latencyCompensation;

                    console.log('Listener: Master clock received:', {
                        trackId,
                        title: track.title,
                        hostPlaying,
                        trackStartTime: masterClock.trackStartTime,
                        basePosition: basePosition.toFixed(2) + 's',
                        networkLatency: networkLatency + 'ms',
                        adjustedPosition: targetPosition.toFixed(2) + 's'
                    });
                } else {
                    // No master clock - for new tracks, start at 0; for existing tracks, skip sync
                    if (isNewTrack) {
                        console.log('Listener: No master clock but new track - starting at 0');
                        targetPosition = 0;
                    } else {
                        console.log('Listener: No master clock data, skipping sync');
                        return;
                    }
                }

                if (isNewTrack) {
                    listenAlongTrackRef.current = trackId;

                    // DON'T add to playlist - just play the track directly
                    // This prevents host's tracks from being saved to listener's playlist
                    console.log('Listener: Playing host track directly:', track.title);

                    // Set up the track for playback without modifying playlist
                    const trackName = track.title || track.name;
                    const artistName = track.artist;
                    const coverImage = track.cover;
                    const videoId = track.videoId;

                    // Set the playing track state directly
                    setPlayingTrack({
                        name: trackName,
                        artist: artistName,
                        artistId: track.artistId, // Pass through for direct artist lookup
                        image: coverImage,
                        videoId: videoId
                    });
                    setActiveContext('playlist'); // Use playlist context for player display
                    setIsPlaying(true);

                    // Seek to master clock position after player loads
                    if (targetPosition !== undefined && targetPosition > 0) {
                        // Clear any pending sync
                        if (listenAlongSyncTimeoutRef.current) {
                            clearTimeout(listenAlongSyncTimeoutRef.current);
                        }
                        // Store master clock info for when we'll actually seek (after player loads)
                        const capturedStartTime = masterClock.trackStartTime;

                        listenAlongSyncTimeoutRef.current = setTimeout(() => {
                            const currentPlayer = playerRef.current;
                            if (currentPlayer) {
                                // Recalculate position from master clock at seek time with latency compensation
                                const now = Date.now();
                                const basePosition = (now - capturedStartTime) / 1000;
                                const latencyComp = sentAt ? ((now - sentAt) / 2000) : 0;
                                const finalPosition = basePosition + latencyComp;
                                console.log(`Listener: Initial sync - position ${finalPosition.toFixed(1)}s (latency comp: ${(latencyComp * 1000).toFixed(0)}ms)`);
                                currentPlayer.seekTo(finalPosition, true);
                            }
                        }, 300); // Wait for video to load (reduced from 800ms)
                    }
                } else {
                    // Same track - sync play/pause state
                    setIsPlaying(hostPlaying);

                    const currentPlayer = playerRef.current;
                    if (currentPlayer && targetPosition !== undefined) {
                        // Check if host explicitly seeked - hard sync
                        if (seeked) {
                            console.log(`Listener: Host seeked - hard sync to ${targetPosition.toFixed(1)}s`);
                            lastProgrammaticSeekRef.current = Date.now();
                            currentPlayer.seekTo(targetPosition, true);
                            currentPlayer.setPlaybackRate(1.0);
                        } else {
                            // Spotify-style smooth sync via graduated playback rate adjustment
                            const listenerPosition = currentPlayer.getCurrentTime() || 0;
                            const drift = targetPosition - listenerPosition; // Positive = behind, Negative = ahead
                            const absDrift = Math.abs(drift);

                            // Hard seek if drift is extreme (> 3 seconds)
                            if (absDrift > 3) {
                                console.log(`Listener: Extreme drift (${drift.toFixed(1)}s) - hard sync`);
                                lastProgrammaticSeekRef.current = Date.now();
                                currentPlayer.seekTo(targetPosition, true);
                                currentPlayer.setPlaybackRate(1.0);
                            } else if (absDrift > 0.3) {
                                // Graduated rate adjustment (nearly invisible to ear)
                                // Uses smaller adjustments for smaller drift to avoid pitch shift
                                let rate: number;
                                if (absDrift < 1.0) {
                                    rate = drift > 0 ? 1.02 : 0.98;  // Gentle
                                } else if (absDrift < 2.0) {
                                    rate = drift > 0 ? 1.04 : 0.96;  // Moderate
                                } else {
                                    rate = drift > 0 ? 1.06 : 0.94;  // Stronger
                                }
                                console.log(`Listener: Drift ${drift.toFixed(2)}s - rate ${rate}x`);
                                currentPlayer.setPlaybackRate(rate);
                            } else {
                                // Within 0.3s - perfect sync, return to normal speed
                                const currentRate = currentPlayer.getPlaybackRate();
                                if (currentRate !== 1.0) {
                                    console.log(`Listener: Synced (${drift.toFixed(2)}s) - rate 1.0x`);
                                    currentPlayer.setPlaybackRate(1.0);
                                }
                            }
                        }
                    }
                }
            });

            // Start the timeout when we first connect
            channel.subscribe(async (status) => {
                console.log('Listener: Channel subscription status:', status);
                if (status === 'SUBSCRIBED') {
                    // Track our presence so host can see us
                    await channel.track({ online_at: new Date().toISOString() });
                    // Start waiting for host broadcasts
                    resetHostTimeout();
                }
            });

            return () => {
                console.log('Listener: Leaving listening room');
                if (hostTimeoutId) clearTimeout(hostTimeoutId);
                supabase.removeChannel(channel);
                listenerChannelRef.current = null;
                listenAlongTrackRef.current = null;
                if (listenAlongSyncTimeoutRef.current) {
                    clearTimeout(listenAlongSyncTimeoutRef.current);
                }
            };
        }
    }, [user]); // Only re-run if user changes - refs handle the rest

    // Function to disconnect from listen along (called by X button)
    const disconnectListenAlong = () => {
        console.log('Listener: Manually disconnecting');
        if (listenerChannelRef.current) {
            supabase.removeChannel(listenerChannelRef.current);
            listenerChannelRef.current = null;
        }
        setIsListeningAlong(false);
        setHostId(null);
        setIsPlaying(false);
        // Actually pause the player, not just the state
        if (player) {
            try { player.pauseVideo(); } catch (e) { }
        }
        listenAlongTrackRef.current = null;
        // Clear the URL param
        const url = new URL(window.location.href);
        url.searchParams.delete('listen_to');
        window.history.replaceState(null, '', url.pathname + url.search);
    };

    // --- Project Sync Logic (for collaborative projects) ---
    // Syncs TEMP player state across all project members
    // SINGLE CONTROLLER MODEL: Only the person who initiated playback (played/queued a song)
    // can broadcast state. Everyone else is read-only until they initiate a new action.
    const projectSyncChannelRef = useRef<any>(null);
    const lastProjectBroadcastRef = useRef<{ trackId: string; isPlaying: boolean } | null>(null);
    const isProjectOwner = currentUserRole === 'owner';
    // Tracks who currently controls playback - only this user broadcasts
    // When YOU play/queue a song, you become controller. Everyone else is read-only.
    const playbackControllerRef = useRef<string | null>(null);
    const [playbackController, setPlaybackController] = useState<string | null>(null);
    // True if user can control playback (not in a project, OR is the controller)
    const isPlaybackController = !selectedProject || !currentUserRole || !playbackController || playbackController === user?.id;

    // Project sync: All members (owner + invited) can broadcast and receive
    useEffect(() => {
        console.log('Project Sync Setup:', {
            selectedProject,
            hasUser: !!user,
            currentUserRole,
            projectMembersLength: projectMembers.length,
            isOwner: isProjectOwner
        });
        // Use currentUserRole instead of projectMembers.length - more reliable for members
        // currentUserRole is 'owner', 'editor', 'viewer', or null (no access)
        if (!selectedProject || !user || !currentUserRole) return;

        const channelName = `project_sync:${selectedProject}`;

        // All members (including owner) can broadcast and receive
        const channel = supabase.channel(channelName);
        projectSyncChannelRef.current = channel;

        // Listen for player updates from ANY member
        channel.on('broadcast', { event: 'project_player_update' }, (payload) => {
            const { actor_id, isHeartbeat } = payload.payload;

            // Ignore our own broadcasts
            if (actor_id === user.id) return;

            // For non-heartbeat actions: update controller tracking
            if (!isHeartbeat) {
                playbackControllerRef.current = actor_id;
                setPlaybackController(actor_id);
            }

            // Delegate all sync logic to unified helper
            syncPlayerToState(payload.payload);
        });

        // Listen for queue updates from ANY member
        channel.on('broadcast', { event: 'project_queue_update' }, (payload) => {
            const { queue: newQueue, actor_id } = payload.payload;

            // Ignore our own broadcasts
            if (actor_id === user.id) return;

            console.log('Project Sync: Queue updated by member', newQueue?.length, 'tracks', isProjectOwner ? '(owner received)' : '');
            if (Array.isArray(newQueue)) {
                setQueue(newQueue);
            }
        });

        channel.subscribe(async (status) => {
            if (status === 'SUBSCRIBED') {
                console.log('Project Sync: Connected to', channelName, isProjectOwner ? '(owner)' : '(member)');

                // Latecomer sync: fetch current playback state from DB
                // This allows users joining mid-session to sync to whatever is playing
                try {
                    const project = await ProjectService.getProject(selectedProject);
                    const currentPlayback = (project as any)?.metadata?.currentPlayback;
                    if (currentPlayback && currentPlayback.track && currentPlayback.masterClock) {
                        const age = Date.now() - (currentPlayback.updatedAt || 0);
                        // Only sync if state is recent (< 5 minutes old)
                        if (age < 5 * 60 * 1000) {
                            console.log('Project Sync: Latecomer sync from DB', currentPlayback.track.title);
                            syncPlayerToState({
                                trackId: currentPlayback.trackId,
                                track: currentPlayback.track,
                                isPlaying: currentPlayback.isPlaying,
                                masterClock: currentPlayback.masterClock,
                                sentAt: currentPlayback.updatedAt, // Use updatedAt for latency calc
                            });
                        }
                    }
                } catch (e) {
                    // Non-critical, just means latecomer won't auto-sync
                }
            }
        });

        return () => {
            if (projectSyncChannelRef.current) {
                supabase.removeChannel(projectSyncChannelRef.current);
                projectSyncChannelRef.current = null;
            }
        };
    }, [selectedProject, user, currentUserRole, isProjectOwner]);

    // SINGLE CONTROLLER broadcast: Only the playback controller broadcasts state
    // When YOU initiate an action (play, seek, track change), you become controller
    // When you RECEIVE sync, the sender is controller and you DON'T broadcast
    useEffect(() => {
        if (!projectSyncChannelRef.current || !selectedProject || !user) return;

        // NOT the controller? Don't broadcast anything.
        const isController = playbackControllerRef.current === user.id;
        if (!isController) return;

        // Get track from playlist, or create a track object from playingTrack (for search/discography songs)
        let track = playlistTracks[currentTrackIndex ?? -1];
        if (!track && playingTrack?.videoId) {
            track = {
                id: playingTrack.videoId,
                title: playingTrack.name,
                artist: playingTrack.artist,
                cover: playingTrack.image,
                videoId: playingTrack.videoId,
                duration: playingTrack.duration || '',
                genre: playingTrack.genre || ''
            } as any;
        }
        if (!track) return;

        const trackVideoId = (track as any).videoId || track.id;

        // Initialize master clock for new local tracks
        if (masterClockRef.current.trackId !== trackVideoId) {
            masterClockRef.current = {
                trackId: trackVideoId,
                trackStartTime: Date.now(),
                isPaused: !isPlaying,
                pausedAt: isPlaying ? null : Date.now()
            };
            console.log('Project Sync: Master clock initialized for NEW track (LOCAL):', track.title || (track as any).name);
        }

        // Heartbeat function reads LIVE refs to avoid stale closures during track transitions.
        // setInterval captures closure values, but after auto-advance the track/clock change.
        // Reading from refs ensures heartbeats always send the CURRENT track and masterClock.
        const broadcastProjectState = (isHeartbeat = false) => {
            if (!projectSyncChannelRef.current) return;
            if (playbackControllerRef.current !== user?.id) return;

            // For heartbeats: read LIVE state from refs (avoids stale closure after auto-advance)
            // For immediate broadcasts: use closure values (they're fresh from the effect run)
            let broadcastTrack: any;
            let broadcastClock: any;
            let broadcastIsPlaying: boolean;

            if (isHeartbeat) {
                broadcastTrack = playlistTracksRef.current[currentTrackIndexRef.current ?? -1];
                if (!broadcastTrack && playingTrackRef.current?.videoId) {
                    broadcastTrack = {
                        id: playingTrackRef.current.videoId,
                        title: playingTrackRef.current.name,
                        artist: playingTrackRef.current.artist,
                        cover: playingTrackRef.current.image,
                        videoId: playingTrackRef.current.videoId,
                    };
                }
                if (!broadcastTrack) return;
                broadcastClock = masterClockRef.current;
                broadcastIsPlaying = isPlayingRef.current;
            } else {
                broadcastTrack = track;
                broadcastClock = masterClockRef.current;
                broadcastIsPlaying = isPlaying;
            }

            const broadcastTrackId = (broadcastTrack as any).videoId || broadcastTrack.id;

            projectSyncChannelRef.current.send({
                type: 'broadcast',
                event: 'project_player_update',
                payload: {
                    trackId: broadcastTrackId,
                    track: broadcastTrack,
                    isPlaying: broadcastIsPlaying,
                    masterClock: {
                        trackStartTime: broadcastClock.trackStartTime,
                        isPaused: broadcastClock.isPaused,
                        pausedAt: broadcastClock.pausedAt,
                    },
                    sentAt: Date.now(),
                    actor_id: user?.id,
                    project_id: selectedProject,
                    isHeartbeat
                }
            });
        };

        // Broadcast state change
        const broadcastTrackId = (track as any).videoId || track.id;
        const lastBroadcast = lastProjectBroadcastRef.current;
        const hasStateChanged = !lastBroadcast ||
            lastBroadcast.trackId !== broadcastTrackId ||
            lastBroadcast.isPlaying !== isPlaying;

        if (hasStateChanged) {
            console.log('Project Sync: Broadcasting state change (controller)', { trackId: broadcastTrackId, isPlaying });
            broadcastProjectState(false);

            // Persist current playback state so latecomers can sync
            if (selectedProject && playbackControllerRef.current === user?.id) {
                const clock = masterClockRef.current;
                ProjectService.updateProjectMetadata(selectedProject, {
                    currentPlayback: {
                        trackId: broadcastTrackId,
                        track: { title: track.title, artist: track.artist, cover: track.cover, videoId: (track as any).videoId || track.id },
                        isPlaying,
                        masterClock: { trackStartTime: clock.trackStartTime, isPaused: clock.isPaused, pausedAt: clock.pausedAt },
                        updatedAt: Date.now()
                    }
                }).catch(() => { /* Non-critical, ignore errors */ });
            }
        }
        lastProjectBroadcastRef.current = { trackId: broadcastTrackId, isPlaying };

        // Periodic heartbeats for drift correction (only when playing, only from controller)
        // 1.5s matches Listen Along frequency for consistent sync quality
        const intervalId = isPlaying ? setInterval(() => broadcastProjectState(true), 1500) : undefined;

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [isPlaying, currentTrackIndex, selectedProject, user?.id, playlistTracks, playingTrack]);

    // DnD Sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Reset Progress on Song Change:
    // REMOVED manual reset to prevent race conditions. Poller handles it.
    // useEffect(() => { ... }, [playingTrack]);

    // Poll for Progress (Smooth)
    useEffect(() => {
        if (!player || isDraggingProgress) return;

        let animationFrameId: number;
        let lastPolledTime = currentTime;
        let lastSeekBroadcast = 0; // Throttle seek broadcasts

        const updateProgress = () => {
            if (!player) return;
            try {
                // Use a local variable to avoid stale closure issues if we need to check values
                const curr = player.getCurrentTime();
                const dur = player.getDuration();

                if (typeof curr === 'number' && !isNaN(curr)) {
                    // Detect seeks via YouTube controls (position jumps > 2 seconds)
                    // Normal playback advances ~0.016s per frame, so 2s jump = definitely a seek
                    const positionDelta = Math.abs(curr - lastPolledTime);
                    const now = Date.now();
                    const timeSinceProgrammaticSeek = now - lastProgrammaticSeekRef.current;

                    // Only detect user seeks, NOT:
                    // - Programmatic seeks from sync (within 2s of lastProgrammaticSeekRef)
                    // - Syncs received from other members (within 2s of lastSyncReceivedRef)
                    // - Track changes (position jumping TO near 0)
                    // - While dragging slider
                    const timeSinceSyncReceived = now - lastSyncReceivedRef.current.timestamp;
                    const isUserSeek = positionDelta > 2 &&
                        !isDraggingProgress &&
                        (now - lastSeekBroadcast) > 1000 &&
                        timeSinceProgrammaticSeek > 2000 &&  // Ignore programmatic seeks
                        timeSinceSyncReceived > 2000 &&  // Ignore position changes from received syncs
                        curr > 1;  // Ignore track changes (jumping to 0)

                    if (isUserSeek) {
                        // Detected a seek via YouTube controls - broadcast it
                        console.log('Detected seek via YT controls:', lastPolledTime.toFixed(1), '→', curr.toFixed(1));
                        lastSeekBroadcast = now;

                        // Update master clock - seeking implies continued playback
                        if (!isListeningAlong && masterClockRef.current.trackId) {
                            masterClockRef.current.trackStartTime = Date.now() - (curr * 1000);
                            masterClockRef.current.isPaused = false; // Seek means keep playing
                        }

                        // Get track for broadcast
                        let track = playlistTracksRef.current[currentTrackIndexRef.current];
                        const currentPlayingTrack = playingTrackRef.current;
                        if (!track && currentPlayingTrack?.videoId) {
                            track = {
                                id: currentPlayingTrack.videoId,
                                title: currentPlayingTrack.name,
                                artist: currentPlayingTrack.artist,
                                cover: currentPlayingTrack.image,
                                videoId: currentPlayingTrack.videoId
                            } as any;
                        }

                        // Broadcast seek to project members (only if we're the controller)
                        if (track && projectSyncChannelRef.current && playbackControllerRef.current === user?.id) {
                            // Use videoId for consistent trackId matching (prevents echo-back detection failures)
                            const seekTrackId = (track as any).videoId || track.id;
                            console.log('Project Sync: Broadcasting seek (YT controls) to:', curr.toFixed(1) + 's');
                            projectSyncChannelRef.current.send({
                                type: 'broadcast',
                                event: 'project_player_update',
                                payload: {
                                    trackId: seekTrackId,
                                    track: track,
                                    isPlaying: true,  // Force true - seeking implies continued playback
                                    masterClock: {
                                        trackStartTime: Date.now() - (curr * 1000),
                                        isPaused: false,  // Force not paused
                                        pausedAt: null,
                                    },
                                    sentAt: Date.now(),
                                    actor_id: user?.id,
                                    project_id: selectedProject,
                                    seeked: true
                                }
                            });
                        }
                    }

                    lastPolledTime = curr;

                    // Only update if time has changed meaningfully (prevent spam when paused)
                    // But always update if we are near 0 (reset/start)
                    if (Math.abs(curr - currentTime) > 0.1 || curr < 1) {
                        setCurrentTime(curr);
                    }
                }

                if (typeof dur === 'number' && dur > 0 && !isNaN(dur)) {
                    // Only update duration if it has significantly changed or was 0
                    setDuration(prev => (Math.abs(prev - dur) > 0.5 || prev === 0) ? dur : prev);
                }

                // Sync Volume & Mute State
                const vol = player.getVolume();
                const muted = player.isMuted();
                if (typeof vol === 'number') {
                    setVolume(prev => Math.abs(prev - vol) > 1 ? vol : prev); // 1% threshold
                }
                if (typeof muted === 'boolean') {
                    setIsMuted(prev => prev !== muted ? muted : prev);
                }
            } catch (e) {
                // Ignore player errors during unmount
            }
            animationFrameId = requestAnimationFrame(updateProgress);
        };

        animationFrameId = requestAnimationFrame(updateProgress);
        return () => cancelAnimationFrame(animationFrameId);
    }, [player, isDraggingProgress, currentTime, isListeningAlong, selectedProject, user?.id, playingTrack?.videoId, currentTrackIndex]);

    // Handle mouse release outside the progress bar element
    // onMouseUp on the input only fires if released INSIDE the element
    // This ensures seek broadcasts happen even when user drags outside
    useEffect(() => {
        if (!isDraggingProgress) return;

        const handleGlobalMouseUp = () => {
            setIsDraggingProgress(false);

            // Perform the seek if player is ready
            if (playerRef.current) {
                const newTime = currentTime;
                playerRef.current.seekTo(newTime, true);
                // Ensure playback continues after seek (YouTube briefly pauses during seeks)
                setIsPlaying(true);
                try { playerRef.current.playVideo(); } catch (e) { }
                lastProgrammaticSeekRef.current = Date.now();

                // Update master clock on seek - seeking means we want to keep playing
                if (!isListeningAlong && masterClockRef.current.trackId) {
                    masterClockRef.current.trackStartTime = Date.now() - (newTime * 1000);
                    masterClockRef.current.isPaused = false;
                    masterClockRef.current.pausedAt = null;
                    console.log('Host: Master clock updated for seek (global mouseup) to:', newTime);
                }

                // Get track from playlist or create from playingTrack (for search/discography)
                let track = playlistTracksRef.current[currentTrackIndexRef.current];
                const currentPlayingTrack = playingTrackRef.current;
                if (!track && currentPlayingTrack?.videoId) {
                    track = {
                        id: currentPlayingTrack.videoId,
                        title: currentPlayingTrack.name,
                        artist: currentPlayingTrack.artist,
                        cover: currentPlayingTrack.image,
                        videoId: currentPlayingTrack.videoId // Include videoId for receiver
                    } as any;
                }

                if (!track) return;

                // Broadcast seek for listen along
                if (hostChannelRef.current && !isListeningAlong) {
                    const clock = masterClockRef.current;
                    console.log('Host: Broadcasting seek via master clock (global mouseup) to:', newTime);
                    hostChannelRef.current.send({
                        type: 'broadcast',
                        event: 'player_update',
                        payload: {
                            trackId: track.id,
                            track: track,
                            isPlaying: true,  // Force true - seeking implies continued playback
                            masterClock: {
                                trackStartTime: clock.trackStartTime,
                                isPaused: false,
                                pausedAt: null,
                                serverTime: Date.now()
                            },
                            context: activeContext,
                            host: user?.username || user?.id,
                            seeked: true,
                            sentAt: Date.now()
                        }
                    });
                }

                // Broadcast seek for project members
                // Channel is only set up if we have project access
                // Only broadcast seek if we're the controller
                if (projectSyncChannelRef.current && playbackControllerRef.current === user?.id) {
                    const clock = masterClockRef.current;
                    // Use videoId for consistent trackId matching (prevents echo-back detection failures)
                    const seekTrackId = (track as any).videoId || track.id;
                    console.log('Project Sync: Broadcasting seek (global mouseup) to:', newTime);
                    projectSyncChannelRef.current.send({
                        type: 'broadcast',
                        event: 'project_player_update',
                        payload: {
                            trackId: seekTrackId,
                            track: track,
                            isPlaying: true,  // Force true - seeking implies continued playback (YT briefly pauses during seeks)
                            masterClock: {
                                trackStartTime: clock.trackStartTime,
                                isPaused: false,
                                pausedAt: null,
                            },
                            sentAt: Date.now(),
                            actor_id: user?.id,
                            project_id: selectedProject,
                            seeked: true
                        }
                    });
                }
            }
        };

        document.addEventListener('mouseup', handleGlobalMouseUp);
        document.addEventListener('touchend', handleGlobalMouseUp);

        return () => {
            document.removeEventListener('mouseup', handleGlobalMouseUp);
            document.removeEventListener('touchend', handleGlobalMouseUp);
        };
    }, [isDraggingProgress, currentTime, isListeningAlong, isPlaying, activeContext, user, selectedProject]);

    // Background-safe auto-advance: Use setInterval to detect video end even when tab is inactive
    // requestAnimationFrame is throttled in background tabs, but setInterval still runs (at reduced rate)
    // Only auto-advance if: not in a project, OR we are the playback controller
    // Non-controllers receive track changes via sync - they don't auto-advance on their own
    useEffect(() => {
        if (!player || !isPlaying || !playingTrack?.videoId) return;
        // In a project but not the controller? Don't auto-advance - wait for sync
        if (selectedProject && currentUserRole && playbackControllerRef.current && playbackControllerRef.current !== user?.id) return;

        const checkVideoEnded = () => {
            try {
                const playerState = player.getPlayerState();
                const currentVideoId = playingTrack?.videoId;
                // YouTube PlayerState: 0 = ended, 1 = playing, 2 = paused, 3 = buffering
                if (playerState === 0 && currentVideoId) {
                    // Prevent double-advance: check if we already advanced from this video
                    if (lastAdvancedFromVideoRef.current === currentVideoId) {
                        return; // Already handled by onEnd callback
                    }
                    // Re-check controller status at runtime (not just effect setup)
                    if (selectedProject && currentUserRole && playbackControllerRef.current && playbackControllerRef.current !== user?.id) {
                        return;
                    }
                    console.log('[Background Auto-Advance] Video ended, playing next track');
                    lastAdvancedFromVideoRef.current = currentVideoId;
                    playNextRef.current();
                }
            } catch (e) {
                // Ignore errors if player is not ready
            }
        };

        // Check every 1 second - this interval continues even in background tabs
        const intervalId = setInterval(checkVideoEnded, 1000);

        return () => clearInterval(intervalId);
    }, [player, isPlaying, playingTrack?.videoId, selectedProject, currentUserRole, user?.id]);

    // Update Discord Rich Presence when track or playback state changes
    // Update Discord Rich Presence when track or playback state changes
    useEffect(() => {
        if (!discord.connected) return;

        if (playingTrack) {
            const startTimestamp = Date.now() - (currentTime * 1000);

            // 1. Check if we should update (Debounce / Rate Limit)
            // Always update if: Track changed OR Pause State changed
            // Only update time if: Drift > 2 seconds (e.g. Seek)

            if (lastPresenceUpdateRef.current) {
                const last = lastPresenceUpdateRef.current;
                const trackChanged = last.track !== playingTrack.name;
                const pauseChanged = last.isPaused !== !isPlaying;
                const timeDrift = Math.abs(startTimestamp - last.startTimestamp);

                // If nothing significant changed, skip update (prevents spamming every 100ms)
                if (!trackChanged && !pauseChanged && timeDrift < 2000) {
                    return;
                }
            }

            discord.updatePresence({
                track: playingTrack.name,
                artist: playingTrack.artist,
                image: playingTrack.image,
                duration: duration,
                currentTime: currentTime,
                isPaused: !isPlaying,
                genre: playingTrack.genre,
                userId: user?.id,
                username: user?.username
            });

            // Update Ref
            lastPresenceUpdateRef.current = {
                track: playingTrack.name,
                isPaused: !isPlaying,
                startTimestamp: startTimestamp
            };
        } else {
            discord.updatePresence(null);
            lastPresenceUpdateRef.current = null;
        }
    }, [playingTrack, isPlaying, currentTime, duration, discord.connected, user]);

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!isPlaybackController) return;
        const newTime = parseFloat(e.target.value);
        setCurrentTime(newTime);
    };

    const handleSeekEnd = (e: React.MouseEvent<HTMLInputElement> | React.TouchEvent<HTMLInputElement>) => {
        if (!isPlaybackController) return;
        if (player) {
            const newTime = currentTime;
            player.seekTo(newTime, true);
            // Ensure playback continues after seek
            setIsPlaying(true);
            try { player.playVideo(); } catch (e) { }
            lastProgrammaticSeekRef.current = Date.now();

            // Update master clock on seek - seeking means we want to keep playing
            if (!isListeningAlong && masterClockRef.current.trackId) {
                masterClockRef.current.trackStartTime = Date.now() - (newTime * 1000);
                masterClockRef.current.isPaused = false;
                masterClockRef.current.pausedAt = null;
                console.log('Host: Master clock updated for seek to:', newTime);
            }

            // Get track from playlist or create from playingTrack (for search/discography/artist blend/mix)
            let track = playlistTracksRef.current[currentTrackIndexRef.current];
            if (!track && playingTrack?.videoId) {
                track = {
                    id: playingTrack.videoId,
                    title: playingTrack.name,
                    artist: playingTrack.artist,
                    cover: playingTrack.image,
                    videoId: playingTrack.videoId // Include videoId for receiver
                } as any;
            }

            if (track) {
                // Broadcast seek immediately for listen along using master clock
                if (hostChannelRef.current && !isListeningAlong) {
                    const clock = masterClockRef.current;
                    console.log('Host: Broadcasting seek via master clock to:', newTime);
                    hostChannelRef.current.send({
                        type: 'broadcast',
                        event: 'player_update',
                        payload: {
                            trackId: track.id,
                            track: track,
                            isPlaying: true,  // Force true - seeking implies continued playback
                            masterClock: {
                                trackStartTime: clock.trackStartTime,
                                isPaused: false,
                                pausedAt: null,
                                serverTime: Date.now()
                            },
                            context: activeContext,
                            host: user?.username || user?.id,
                            seeked: true, // Flag to indicate this was a seek action
                            sentAt: Date.now()
                        }
                    });
                }

                // Only broadcast seek if we're the controller
                if (projectSyncChannelRef.current && playbackControllerRef.current === user?.id) {
                    const clock = masterClockRef.current;
                    // Use videoId for consistent trackId matching (prevents echo-back detection failures)
                    const seekTrackId = (track as any).videoId || track.id;
                    console.log('Project Sync: Broadcasting seek to:', newTime, 'trackStartTime:', clock.trackStartTime);
                    projectSyncChannelRef.current.send({
                        type: 'broadcast',
                        event: 'project_player_update',
                        payload: {
                            trackId: seekTrackId,
                            track: track,
                            isPlaying: true,  // Force true - seeking implies continued playback
                            masterClock: {
                                trackStartTime: clock.trackStartTime,
                                isPaused: false,
                                pausedAt: null,
                            },
                            sentAt: Date.now(),
                            actor_id: user?.id,
                            project_id: selectedProject,
                            seeked: true // Flag to indicate this was a seek action
                        }
                    });
                }
            }

            // Wait a bit before resuming polling to let YouTube catch up
            setTimeout(() => {
                setIsDraggingProgress(false);
            }, 500);
        } else {
            setIsDraggingProgress(false);
        }
    };

    const togglePlay = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!player) return;
        // Only the playback controller can pause/play in project sync
        if (!isPlaybackController) return;
        if (isPlaying) {
            player.pauseVideo();
        } else {
            player.playVideo();
        }
    };

    const toggleMute = () => {
        if (!player) return;
        if (isMuted) {
            player.unMute();
            setIsMuted(false);
            localStorage.setItem('ezart_player_muted', 'false');
            if (volume === 0) {
                setVolume(100);
                player.setVolume(100);
                localStorage.setItem('ezart_player_volume', '100');
            }
        } else {
            player.mute();
            setIsMuted(true);
            localStorage.setItem('ezart_player_muted', 'true');
        }
    };

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = parseInt(e.target.value);
        setVolume(val);
        localStorage.setItem('ezart_player_volume', val.toString());
        if (player) {
            player.setVolume(val);
            if (val > 0 && isMuted) {
                player.unMute();
                setIsMuted(false);
                localStorage.setItem('ezart_player_muted', 'false');
            } else if (val === 0 && !isMuted) {
                player.mute();
                setIsMuted(true);
                localStorage.setItem('ezart_player_muted', 'true');
            }
        }
    };

    const fetchYoutubeId = async (query: string, songName?: string, artistName?: string, strict = true, excludeVideoIds?: Set<string>) => {
        const cleanString = (str: string) => str.toLowerCase().replace(/[^a-z0-9 ]/g, "");

        // Strip version/remaster info that YouTube videos typically don't include
        // e.g. "Where Is My Mind? (2007 Remaster)" -> "Where Is My Mind"
        const stripVersionInfo = (str: string) => {
            return str
                .replace(/\s*\(.*?(remaster|version|edition|deluxe|anniversary|bonus|expanded|stereo|mono|mix|single|album|radio).*?\)/gi, '')
                .replace(/\s*\[.*?(remaster|version|edition|deluxe|anniversary|bonus|expanded|stereo|mono|mix|single|album|radio).*?\]/gi, '')
                .replace(/\s*-\s*(remaster|remastered|\d{4}\s*remaster).*$/gi, '')
                .trim();
        };

        // FunnelIcon out excluded video IDs (failed/age-restricted)
        const filterExcluded = (videos: any[]) => {
            if (!excludeVideoIds || excludeVideoIds.size === 0) return videos;
            return videos.filter(v => !excludeVideoIds.has(v.videoId));
        };

        // Helper: Find Best Match from Reference
        const findBestMatch = (videos: any[], targetTitle: string, targetArtist: string) => {
            const targetClean = cleanString(targetTitle);
            const targetCleanStripped = cleanString(stripVersionInfo(targetTitle)); // Version-stripped for matching
            const artistClean = cleanString(targetArtist);

            // Negative keywords for ambient noise
            const noiseKeywords = ["relaxing", "sleep", "study", "loop", "hour", "1 hour", "10 hours", "rain sounds", "white noise"];

            // Junk keywords for unwanted content types (unless explicitly requested)
            const junkKeywords = ["reaction", "review", "cover", "tutorial", "lesson", "karaoke", "instrumental", "remix", "mashup", "live", "fan made", "fan video", "meaning", "explained", "breakdown", "analysis", "slowed", "reverb", "nightcore", "sped up", "music awards", "billboard"];

            // Score each video candidate
            const scoredVideos = videos.map((video: any) => {
                const videoTitle = cleanString(video.title);
                const channelName = cleanString(video.channel || "");
                let score = 0;

                // Exclude ambient noise videos
                const isFalsePositiveNoise = noiseKeywords.some(k =>
                    videoTitle.includes(k) &&
                    !targetClean.includes(k) &&
                    !artistClean.includes(k)
                );
                if (isFalsePositiveNoise) return { video, score: -1000 };

                // Exclude junk content
                const isJunk = junkKeywords.some(k => {
                    if (targetClean.includes(k) || artistClean.includes(k)) return false;
                    return videoTitle.includes(k);
                });
                if (isJunk) return { video, score: -1000 };

                // Check title and artist match
                // Use BOTH full title AND stripped title (without remaster/version info)
                const hasTitle = videoTitle.includes(targetClean) || videoTitle.includes(targetCleanStripped);

                // Strict Artist Check
                // 1. Word boundary match in title/channel (Joji -> Joji, but NOT Jojivlogs)
                // 2. Compact match in channel (Taylor Swift -> TaylorSwiftVEVO)
                const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const artistRegex = new RegExp(`\\b${escapeRegExp(artistClean)}\\b`, 'i');
                const artistCompact = artistClean.replace(/\s/g, '');

                const hasArtist = artistRegex.test(videoTitle)
                    || artistRegex.test(channelName)
                    || (artistCompact.length > 3 && channelName.includes(artistCompact));

                // TITLE MATCH REQUIREMENT
                // Allow "Token Matching" to handle intrusive words like "Rabbitology" in "Skeleton Key (Rabbitology Remix)"
                // Instead of requiring contiguous "Skeleton Key Remix", we require "Skeleton", "Key", "Remix" to appear.
                // Use the STRIPPED version for token matching (ignores remaster/version info)
                if (!hasTitle) {
                    const titleWords = targetCleanStripped.split(' ').filter(w => w.length > 1);
                    const allWordsMatch = titleWords.every(w => videoTitle.includes(w));

                    if (!allWordsMatch) {
                        return { video, score: 0 };
                    }
                    // If matched by tokens, we continue (it's a valid candidate)
                }

                // STRICT REQUIREMENTS: Both title AND artist must match
                // Title must be in video title (checked above)
                // Artist must be in video title OR channel name
                if (!hasArtist) {
                    return { video, score: 0 }; // Reject: artist not found
                }

                // Base score - both requirements met at this point
                score += 500;

                // BONUS: Exact Title Match (hasArtist is guaranteed true at this point)
                // REDUCED from 1000 to 200 to prevent fan re-uploads with "clean" titles from beating official videos
                if (videoTitle === targetClean || videoTitle === `${artistClean} ${targetClean}` || videoTitle === `${targetClean} ${artistClean}`) {
                    score += 200;
                }

                // BONUS: Prefer "official" content (without requiring it)
                if (videoTitle.includes("official")) score += 150;
                if (videoTitle.includes("audio")) score += 60; // Reduced from 100
                if (videoTitle.includes("music video")) score += 80; // Reduced from 120
                if (videoTitle.includes("lyric")) score += 50; // Reduced from 80
                if (videoTitle.includes("visualizer")) score += 50; // Reduced from 60

                // BONUS: VEVO and Topic channels are usually official
                if (channelName.includes("vevo")) score += 200;
                if (channelName.includes("topic")) score += 150;

                // BONUS: Channel name matches artist (likely official channel)
                // Use STRICT MATCHING to avoid fan channels like "Taylor Swift Song" or "Taylor Swift Fans" getting the bonus
                const isExactChannelMatch = channelName === artistCompact;
                const isVevoChannelMatch = channelName === `${artistCompact}vevo`;
                const isTopicChannelMatch = channelName === `${artistCompact}topic`;

                if (hasArtist && (isExactChannelMatch || isVevoChannelMatch || isTopicChannelMatch)) {
                    // HUGE BOOST for verified artist channel interactions
                    score += 500;

                    // Extra bonus for pure exact match
                    if (isExactChannelMatch) {
                        score += 500;
                    }
                }

                // BONUS: Match parenthetical content (hushed, remix, acoustic, etc.)
                // Extract parenthetical content from target title
                const targetParens = (targetTitle.match(/\([^)]+\)/g) || []).map(p => cleanString(p.replace(/[()]/g, '')));
                if (targetParens.length > 0) {
                    // Give big bonus if video title contains the same parenthetical content
                    for (const paren of targetParens) {
                        if (paren.length > 2 && videoTitle.includes(paren)) {
                            score += 250; // Strong preference for exact version match
                        }
                    }
                    // Penalize if video has different parenthetical content
                    // Penalize if video has different parenthetical content (e.g. "Remix" vs "Live")
                    // BUT ignore "safe" technical terms like "Visualizer", "Audio", "Official"
                    const safeParens = ["visualizer", "audio", "official", "video", "lyric", "lyrics", "hq", "hd", "4k", "mv"];
                    const videoParens = (video.title.match(/\([^)]+\)/g) || []).map((p: string) => cleanString(p.replace(/[()]/g, '')));

                    for (const vp of videoParens) {
                        // If parenthesis content is NOT in target parens AND NOT a safe term -> Penalize
                        if (vp.length > 2 && !targetParens.some(tp => tp.includes(vp) || vp.includes(tp))) {
                            const isSafe = safeParens.some(safe => vp.includes(safe));
                            if (!isSafe) {
                                score -= 100; // Penalize wrong version (e.g. "Live" or "Remix")
                            }
                        }
                    }
                }

                console.log(`Scoring: "${cleanString(video.title)}" | Channel: "${cleanString(video.channel || '')}" | hasTitle:${hasTitle} | hasArtist:${hasArtist} | Score:${score}`);
                return { video, score };
            });

            // Sort by score descending and return best match (if score > 0)
            scoredVideos.sort((a, b) => b.score - a.score);
            const best = scoredVideos[0];

            // STRICTER THRESHOLD: Require a decent score to prevent garbage matches
            return best && best.score > 100 ? best.video : null;
        };

        const performSearch = async (q: string) => {
            try {
                // FIX: Sanitize query to prevent Edge Function errors with special chars
                // Use SPACE replacement to avoid merging words (e.g. "Key(Remix)" -> "Key Remix")
                const sanitizedQuery = q.replace(/[^\w\s]/gi, " ");
                // console.log(`Sanitized query: "${q}" -> "${sanitizedQuery}"`);

                const { data, error } = await supabase.functions.invoke('youtube-search', {
                    body: { query: sanitizedQuery }
                });
                if (error) throw error;
                // FunnelIcon out excluded video IDs (age-restricted, embedding disabled, etc.)
                return filterExcluded(data?.videos || []);
            } catch (e) {
                console.warn('Supabase search failed for:', q, e);
                return [];
            }
        };

        // 1. Search Logic
        let videos = [];

        // USER REQUEST: Use verified metadata for search if available
        if (songName && artistName) {
            // Construct clean query from metadata
            const metadataQuery = `${artistName} ${songName}`;
            videos = await performSearch(metadataQuery);
        } else {
            // Fallback to raw query (e.g. just typing in search bar)
            videos = await performSearch(query);
        }

        if (songName && artistName) {
            let match = findBestMatch(videos, songName, artistName);
            if (match) return match.videoId;

            // 2. Retry with Unquoted Artist Title (Was Quoted, but API hates quotes)
            if (!match) {
                const q2 = `${artistName} ${songName}`;
                // console.log(`Retry 2 (Unquoted): ${q2}`);
                const videos2 = await performSearch(q2);
                match = findBestMatch(videos2, songName, artistName);
                if (match) return match.videoId;
            }

            // 3. Retry with stripped version (removes remaster/version info)
            // e.g. "Where Is My Mind? (2007 Remaster)" -> "Where Is My Mind"
            if (!match) {
                const strippedSong = stripVersionInfo(songName);
                if (strippedSong !== songName && strippedSong.length > 0) {
                    const q3 = `${artistName} ${strippedSong}`;
                    const videos3 = await performSearch(q3);
                    match = findBestMatch(videos3, strippedSong, artistName);
                    if (match) return match.videoId;
                }
            }

            // 4. Retry with "Artist Title Official Audio" (Maximize chance of music source)
            if (!match) {
                // Remove special chars from song name for cleaner search
                const cleanSong = songName.replace(/[\(\[\].*?[\)\]]/g, "");
                const q4 = `${artistName} ${cleanSong} official audio`;
                const videos4 = await performSearch(q4);
                match = findBestMatch(videos4, songName, artistName);

                // If strict match fails here, check for ANY result in q4 that vaguely matches title
                // This captures "Official Video" when "Official Audio" was searched
                // FIX: Use cleanSong (no brackets) for this check to match "(Acoustic)" etc.
                if (!match) {
                    const looseMatch = videos4.find((v: any) => cleanString(v.title).includes(cleanString(cleanSong)));
                    if (looseMatch) return looseMatch.videoId;
                }

                if (match) return match.videoId;
            }

            // 5. Complex Artist Fallback (e.g. "&Tilly x BlauDisS" -> search "&Tilly")
            const separators = / x | & | feat\. | ft\. |,| and /i;
            if (!match && artistName && separators.test(artistName)) {
                const primaryArtist = artistName.split(separators)[0].trim();

                const q5 = `${primaryArtist} ${songName}`;
                const videos5 = await performSearch(q5);
                // We use the FULL artistName in findBestMatch so the flexible matching we added earlier can still work
                // (it will split the target artist and match if *primary* is found in title)
                match = findBestMatch(videos5, songName, artistName);
                if (match) return match.videoId;
            }

            // REMOVED "Last Resort" fallback to prevent playing wrong songs.
            // If we haven't found a match by now, it's safer to fail.
            // console.warn("No confident video match found.");
            return null;
        } else {
            // No context, just return first result
            if (videos.length > 0) return videos[0].videoId;
        }

        return null;
    };

    const playOnYoutube = async (track: any, index: number, fromSync = false) => {
        // Block user-initiated plays when listening along - only allow sync plays
        if (isListeningAlong && !fromSync) {
            console.log('Blocked: Cannot play tracks while listening along');
            return;
        }

        // If this is a user-initiated play (not from sync), claim playback controller
        if (!fromSync && selectedProject && currentUserRole && user) {
            playbackControllerRef.current = user.id;
            setPlaybackController(user.id);
            console.log('Project Sync: Claimed playback controller');
        }

        // Enforce Playlist Context
        setActiveContext('playlist');
        setCurrentTrackIndex(index);
        setCurrentTrackId(track.id); // Track by ID for robust highlighting
        setRadioTrackIndex(-1); // Reset Radio Index

        // Reset progress bar to 0 when starting a new track
        setCurrentTime(0);
        setDuration(0);

        setLoadingVideo(true);
        // Clear previous video ID to show loading state if switching
        setPlayingTrack(prev => prev ? { ...prev, videoId: undefined } : null);

        const trackName = track.title || track.name;
        const artistName = track.artist || track.artists?.[0]?.name;
        const coverImage = track.cover || track.album?.images?.[0]?.url;

        // Optimistic UI update
        const tempTrack = {
            name: trackName,
            artist: artistName,
            artistId: track.artistId, // Pass through for direct artist lookup
            image: coverImage,
            videoId: track.videoId || undefined // Use stored videoId if we have it
        };
        setPlayingTrack(tempTrack);

        // Resolution Strategy:
        // 1. Use stored videoId (already done via tempTrack)
        // 2. Check MusicBrainz Relations (Official Link)
        // 3. Search via Server/Invidious

        let videoId = track.videoId;

        if (!videoId && track.id) {
            // Validate UUID format to prevent 400 errors from MusicBrainz
            const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(track.id);

            if (isUUID) {
                try {
                    // Check MB Relations
                    const mbRes = await fetch(`https://musicbrainz.org/ws/2/recording/${track.id}?inc=url-rels&fmt=json`, {
                        headers: { 'UserIcon-Agent': 'EzArt/1.0 ( contact@example.com )' }
                    });
                    if (mbRes.ok) {
                        const mbData = await mbRes.json();
                        const youtubeRel = mbData.relations?.find((r: any) => r.type === 'youtube');
                        if (youtubeRel?.url?.resource) {
                            const match = youtubeRel.url.resource.match(/[?&]v=([^&]+)/); // Extract ID
                            // Also handle short URLs if needed, but standard is usually full
                            if (match) videoId = match[1];
                        }
                    }
                } catch (e) {
                    // console.warn('MB Relation check failed:', e);
                }
            }
        }

        if (!videoId) {
            videoId = await fetchYoutubeId(`${trackName} ${artistName}`, trackName, artistName, true, failedVideoIds.current);
        }

        // If no video found, auto-skip to next track
        if (!videoId) {
            console.warn(`No video found for "${trackName}" by "${artistName}" - auto-skipping`);
            setLoadingVideo(false);

            // Auto-skip to next track in playlist
            if (playlistTracks.length > 1) {
                const nextIndex = (index + 1) % playlistTracks.length;
                // Use setTimeout to prevent potential stack overflow from rapid failures
                setTimeout(() => playOnYoutube(playlistTracks[nextIndex], nextIndex), 100);
            }
            return;
        }

        // Update Playing Track
        setPlayingTrack(prev => ({
            ...tempTrack,
            videoId: videoId || undefined
        }));
        setLoadingVideo(false);

        // PERSIST: If we found a NEW videoId, save it to the project playlist
        if (videoId && videoId !== track.videoId) {
            const updatedTrack = { ...track, videoId };
            const newTracks = playlistTracks.map(t => t.id === track.id ? updatedTrack : t);
            setPlaylistTracks(newTracks); // Update Local State
            persistPlaylist(newTracks);   // Update Project Metadata
        }
    };

    const playAlbumTrack = async (track: any, index: number, tracks: any[]) => {
        setActiveContext('album');
        setAlbumContextTracks(tracks);
        setAlbumTrackIndex(index);

        // Reset others
        setCurrentTrackIndex(-1);
        setRadioTrackIndex(-1);
        setSearchTrackIndex(-1);

        setLoadingVideo(true);
        // Optimistic UI update
        const tempTrack = {
            id: track.id,
            name: track.title || track.name,
            artist: track.artist || track.artists?.[0]?.name,
            artistId: track.artistId, // Pass through for direct artist lookup
            image: track.cover,
            videoId: undefined,
            duration: track.duration,
            genre: track.genre
        };
        setPlayingTrack(tempTrack);

        let videoId = await fetchYoutubeId(`${tempTrack.name} ${tempTrack.artist}`, tempTrack.name, tempTrack.artist, true, failedVideoIds.current);

        if (!videoId) {
            console.warn(`No video found for "${tempTrack.name}" - auto-skipping`);
            setLoadingVideo(false);

            // Auto-skip to next track in album
            if (tracks.length > 1) {
                const nextIndex = (index + 1) % tracks.length;
                setTimeout(() => playAlbumTrack(tracks[nextIndex], nextIndex, tracks), 100);
            }
            return;
        }

        setPlayingTrack(prev => ({ ...prev, videoId }));
        setLoadingVideo(false);
    };

    const playNext = () => {
        // Context Awareness
        // Debug: Log context when playNext is called
        console.log(`[playNext] activeContext: ${activeContext}, currentTrackIndex: ${currentTrackIndex}, radioTrackIndex: ${radioTrackIndex}, playlistTracks.length: ${playlistTracks.length}, queue.length: ${queueRef.current.length}`);

        // QUEUE: Check queue first - queued tracks take priority
        if (queueRef.current.length > 0) {
            const queuedTrack = popFromQueue();
            if (queuedTrack) {
                console.log('[playNext] Playing from queue:', queuedTrack.title);
                // Find in playlist or play directly
                const playlistIndex = playlistTracks.findIndex(t => t.id === queuedTrack.id);
                if (playlistIndex !== -1) {
                    playOnYoutube(playlistTracks[playlistIndex], playlistIndex);
                } else {
                    // Play directly without adding to playlist
                    playOnYoutube(queuedTrack, -1);
                }
                return;
            }
        }

        if (activeContext === 'radio') {
            if (radioTracks.length === 0) return;

            // 0. Repeat One (Radio)
            if (repeatOne) {
                // Replay current
                if (player) player.seekTo(0);
                playRadioTrack(radioTracks[radioTrackIndex], radioTrackIndex);
                return;
            }

            // 1. Shuffle (Radio)
            if (isShuffle) {
                let nextIndex;
                if (radioTracks.length === 1) {
                    nextIndex = 0;
                } else {
                    do {
                        nextIndex = Math.floor(Math.random() * radioTracks.length);
                    } while (nextIndex === radioTrackIndex);
                }
                playRadioTrack(radioTracks[nextIndex], nextIndex);
                return;
            }

            // 2. Sequential (Radio)
            const nextIndex = radioTrackIndex + 1;

            // If we reach the end of radio, loop back.
            if (nextIndex < radioTracks.length) {
                playRadioTrack(radioTracks[nextIndex], nextIndex);
            } else {
                playRadioTrack(radioTracks[0], 0);
            }
            return;
        }

        // Search Context
        if (activeContext === 'search') {
            if (searchResults.length === 0) return;

            if (repeatOne) {
                if (player) player.seekTo(0);
                playSearchTrack(searchResults[searchTrackIndex], searchTrackIndex);
                return;
            }

            if (isShuffle) {
                let nextIndex;
                if (searchResults.length === 1) {
                    nextIndex = 0;
                } else {
                    do {
                        nextIndex = Math.floor(Math.random() * searchResults.length);
                    } while (nextIndex === searchTrackIndex);
                }
                playSearchTrack(searchResults[nextIndex], nextIndex);
                return;
            }

            const nextIndex = searchTrackIndex + 1;
            if (nextIndex < searchResults.length) {
                playSearchTrack(searchResults[nextIndex], nextIndex);
            } else {
                playSearchTrack(searchResults[0], 0);
            }
            return;
        }

        // Album Context
        if (activeContext === 'album') {
            if (albumContextTracks.length === 0) return;

            if (repeatOne) {
                if (player) player.seekTo(0);
                playAlbumTrack(albumContextTracks[albumTrackIndex], albumTrackIndex, albumContextTracks);
                return;
            }

            if (isShuffle) {
                let nextIndex;
                if (albumContextTracks.length === 1) {
                    nextIndex = 0;
                } else {
                    do {
                        nextIndex = Math.floor(Math.random() * albumContextTracks.length);
                    } while (nextIndex === albumTrackIndex);
                }
                playAlbumTrack(albumContextTracks[nextIndex], nextIndex, albumContextTracks);
                return;
            }

            const nextIndex = albumTrackIndex + 1;
            if (nextIndex < albumContextTracks.length) {
                playAlbumTrack(albumContextTracks[nextIndex], nextIndex, albumContextTracks);
            } else {
                playAlbumTrack(albumContextTracks[0], 0, albumContextTracks);
            }
            return;
        }

        // Playlist Context
        if (playlistTracks.length === 0) return;

        let nextIndex;

        // 1. Repeat One
        if (repeatOne) {
            if (player) player.seekTo(0);
            playOnYoutube(playlistTracks[currentTrackIndex], currentTrackIndex);
            return;
        }
        // 2. Shuffle
        else if (isShuffle) {
            // Add current to history before moving
            setPlaybackHistory(prev => [...prev, currentTrackIndex]);

            // Pick random that isn't current (unless only 1 track)
            if (playlistTracks.length === 1) {
                nextIndex = 0;
            } else {
                do {
                    nextIndex = Math.floor(Math.random() * playlistTracks.length);
                } while (nextIndex === currentTrackIndex);
            }
        }
        // 3. Sequential (Loop All by Default)
        else {
            setPlaybackHistory(prev => [...prev, currentTrackIndex]);
            nextIndex = currentTrackIndex + 1;

            // Loop back to start if at end
            if (nextIndex >= playlistTracks.length) {
                nextIndex = 0;
            }
        }

        playOnYoutube(playlistTracks[nextIndex], nextIndex);
    };

    // Keep playNextRef updated with the latest playNext function to prevent stale closures
    useEffect(() => { playNextRef.current = playNext; });

    // Keep playOnYoutubeRef updated with the latest function for listen along feature
    useEffect(() => { playOnYoutubeRef.current = playOnYoutube; });

    const playPrev = () => {
        // 0. Repeat One (Universal Check)
        if (repeatOne) {
            if (player) player.seekTo(0);
            if (activeContext === 'radio') {
                playRadioTrack(radioTracks[radioTrackIndex], radioTrackIndex);
            } else if (activeContext === 'search') {
                playSearchTrack(searchResults[searchTrackIndex], searchTrackIndex);
            } else if (activeContext === 'album') {
                playAlbumTrack(albumContextTracks[albumTrackIndex], albumTrackIndex, albumContextTracks);
            } else {
                playOnYoutube(playlistTracks[currentTrackIndex], currentTrackIndex);
            }
            return;
        }

        // Context Awareness
        if (activeContext === 'radio') {
            if (radioTracks.length === 0) return;
            let prevIndex = radioTrackIndex - 1;
            if (prevIndex < 0) {
                prevIndex = radioTracks.length - 1;
            }
            playRadioTrack(radioTracks[prevIndex], prevIndex);
            return;
        }

        if (activeContext === 'album') {
            if (albumContextTracks.length === 0) return;
            let prevIndex = albumTrackIndex - 1;
            if (prevIndex < 0) {
                prevIndex = albumContextTracks.length - 1;
            }
            playAlbumTrack(albumContextTracks[prevIndex], prevIndex, albumContextTracks);
            return;
        }

        if (activeContext === 'search') {
            if (searchResults.length === 0) return;
            let prevIndex = searchTrackIndex - 1;
            if (prevIndex < 0) {
                prevIndex = searchResults.length - 1;
            }
            playSearchTrack(searchResults[prevIndex], prevIndex);
            return;
        }

        // Playlist Context
        if (playlistTracks.length === 0) return;

        // 1. Use History if available
        if (playbackHistory.length > 0) {
            const prevIndex = playbackHistory[playbackHistory.length - 1];
            setPlaybackHistory(prev => prev.slice(0, -1)); // Pop
            playOnYoutube(playlistTracks[prevIndex], prevIndex);
            return;
        }

        // 2. Sequential Fallback
        let prevIndex = currentTrackIndex - 1;
        if (prevIndex < 0) {
            prevIndex = playlistTracks.length - 1; // Wrap to end
        }
        playOnYoutube(playlistTracks[prevIndex], prevIndex);
    };

    const toggleShuffle = () => setIsShuffle(!isShuffle);

    const toggleRepeat = () => {
        setRepeatOne(!repeatOne);
    };

    // Sort assets by date (newest first)
    const filteredAssets = useMemo(() => {
        return [...assets].sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
    }, [assets]);

    // --- Side Effects & Persistence ---

    // Projects are now loaded from ProjectContext - no need to fetch here

    // Persist Active Tab to LocalStorage
    useEffect(() => {
        localStorage.setItem('ezart_sidebar_active_tab', activeTab);
    }, [activeTab]);

    // Update Assets & Playlist when Selected Project Changes
    useEffect(() => {
        if (selectedProject) {
            const project = projects.find(p => p.id === selectedProject);
            if (project) {
                // Flatten shots from scenes
                const allShots = project.scenes?.flatMap(s => s.shots) || [];
                setAssets(allShots);

                // Load Playlist from Metadata
                if (project.metadata?.playlistTracks) {
                    setPlaylistTracks(project.metadata.playlistTracks);
                } else {
                    setPlaylistTracks([]);
                }
                // Only reset playback indices when the actual project ID changes, not on every projects array update
                if (prevSelectedProjectRef.current !== selectedProject) {
                    setCurrentTrackIndex(-1);
                    setRadioTrackIndex(-1);
                    setSearchTrackIndex(-1);
                    prevSelectedProjectRef.current = selectedProject;
                }
            }
        } else {
            setAssets([]);
            setPlaylistTracks([]);
            prevSelectedProjectRef.current = null;
        }
    }, [selectedProject, projects]);

    // Helper to persist playlist
    const persistPlaylist = async (newTracks: typeof playlistTracks) => {
        if (!selectedProject) return;
        const currentProject = projects.find(p => p.id === selectedProject);
        if (!currentProject) return;

        const newMetadata = {
            ...currentProject.metadata,
            playlistTracks: newTracks
        };

        // Optimistic Update
        setProjects(prev => prev.map(p =>
            p.id === selectedProject ? { ...p, metadata: newMetadata } : p
        ));

        await ProjectService.updateProjectMetadata(selectedProject, newMetadata);
    };

    // --- Queue Functions ---

    // Add track to queue
    const addToQueue = (track: any) => {
        if (!user) return;

        const queueTrack: QueueTrack = {
            id: track.id || `queue-${Date.now()}`,
            title: track.title || track.name,
            artist: track.artist || track.artists?.[0]?.name || 'Unknown',
            artistId: track.artistId,
            cover: track.cover || track.album?.images?.[0]?.url || '',
            videoId: track.videoId,
            duration: track.duration,
            addedBy: user.id,
            addedByName: user.username || user.name || 'Unknown',
            addedAt: Date.now()
        };

        const newQueue = [...queueRef.current, queueTrack];
        setQueue(newQueue);

        // Broadcast to project members
        broadcastQueueUpdate(newQueue);

        setToast({ message: `Added "${queueTrack.title}" to queue`, type: 'success' });
    };

    // Remove track from queue
    const removeFromQueue = (trackId: string, addedAt?: number) => {
        // Use addedAt for disambiguation if multiple same tracks
        const newQueue = queueRef.current.filter(t =>
            addedAt ? !(t.id === trackId && t.addedAt === addedAt) : t.id !== trackId
        );
        setQueue(newQueue);
        broadcastQueueUpdate(newQueue);
    };

    // Pop next track from queue (called when track ends)
    const popFromQueue = (): QueueTrack | null => {
        if (queueRef.current.length === 0) return null;

        const [nextTrack, ...remaining] = queueRef.current;
        setQueue(remaining);
        broadcastQueueUpdate(remaining);
        return nextTrack;
    };

    // Clear entire queue
    const clearQueue = () => {
        setQueue([]);
        broadcastQueueUpdate([]);
    };

    // Broadcast queue update to project members
    const broadcastQueueUpdate = (newQueue: QueueTrack[]) => {
        if (!projectSyncChannelRef.current || !user) return;

        projectSyncChannelRef.current.send({
            type: 'broadcast',
            event: 'project_queue_update',
            payload: {
                queue: newQueue,
                actor_id: user.id,
                project_id: selectedProject
            }
        });
    };

    // --- MusicBrainz & Playlist Actions ---

    // Last.fm API Key
    const LASTFM_API_KEY = import.meta.env.VITE_LASTFM_API_KEY;

    // Unified Metadata Fetcher via Supabase Edge Function (Proxy)
    // Solves CORS and Rate Limit issues by handling iTunes/MB requests on the server
    const fetchMusicMetadata = async (title: string, artist: string) => {
        try {
            const { data, error } = await supabase.functions.invoke('search-music', {
                body: { title, artist }
            });

            if (error) {
                console.error('Metadata proxy error:', error);
                return null;
            }

            return data; // { artwork, duration }
        } catch (e) {
            console.error('Metadata fetch failed', e);
            return null;
        }
    };

    // Generic Debounce Hook
    const useDebounce = (value: string, delay: number) => {
        const [debouncedValue, setDebouncedValue] = useState(value);
        useEffect(() => {
            const handler = setTimeout(() => setDebouncedValue(value), delay);
            return () => clearTimeout(handler);
        }, [value, delay]);
        return debouncedValue;
    };

    // Keep track of search request ID to handle race conditions
    const searchRequestId = useRef(0);

    // Search Music (Progressive Loading Strategy)
    // Search Music (Proxied via Supabase -> iTunes)
    const searchMusic = async (query: string) => {
        if (!query.trim()) {
            setSearchResults([]);
            setSearchArtists([]);
            // Don't clear selectedArtist here, let the UI handle transitions
            return;
        }

        const currentId = ++searchRequestId.current;
        setIsSearching(true);

        try {
            console.log('Initiating search for:', query);

            // New Backend Search (Combined Search + Metadata)
            const { data, error } = await supabase.functions.invoke('search-music', {
                body: { query: query }
            });

            console.log('Edge Function Response:', data);

            if (data?.tracks && currentId === searchRequestId.current) {
                // Map Backend format to UI format
                // Backend returns { id, title, artist, cover, duration, album, preview }
                const mappedTracks = data.tracks
                    .map((t: any) => ({
                        id: t.id,
                        title: t.title,
                        artist: t.artist,
                        cover: t.cover,
                        duration: t.duration,
                        genre: t.genre,
                        loading: false,
                        isAdded: playlistTracks.some(pt =>
                            pt.title.toLowerCase() === t.title.toLowerCase() &&
                            pt.artist.toLowerCase() === t.artist.toLowerCase()
                        )
                    }));

                // Deduplicate Tracks (FunnelIcon out identical Title+Artist combos)
                const uniqueTracks: any[] = [];
                const seenTracks = new Set<string>();

                for (const track of mappedTracks) {
                    // Normalize to catch duplicates
                    const sig = `${track.title.toLowerCase().trim()}|${track.artist.toLowerCase().trim()}`;
                    if (!seenTracks.has(sig)) {
                        seenTracks.add(sig);
                        uniqueTracks.push(track);
                    }
                }

                console.log('Search Results:', uniqueTracks);
                setSearchResults(uniqueTracks);

                // Only clear selectedArtist if the query doesn't match the currently viewed artist
                if (selectedArtist && query.toLowerCase().trim() !== selectedArtist.name.toLowerCase().trim()) {
                    setSelectedArtist(null);
                }

                // Set artist suggestions if available
                console.log('Artists from API:', data.artists);
                if (data.artists && data.artists.length > 0) {
                    // FunnelIcon to only artists with valid ID
                    let validArtists = data.artists.filter((a: any) => a.id && a.id !== 'undefined' && a.id !== 'null');

                    // Deduplicate and remove "Feature" spam (e.g. show "Madonna", hide "Madonna & Swae Lee")
                    // 1. Sort by name length (shortest first) to prioritize main profiles
                    validArtists.sort((a: any, b: any) => a.name.length - b.name.length);

                    const uniqueArtists: any[] = [];
                    const seenIds = new Set<string>();
                    const nameCount = new Map<string, number>(); // Track how many times we've seen each name
                    // Removed ', ' to avoid hiding "Tyler, The Creator" if "Tyler" exists
                    const separators = [' & ', ' feat ', ' feat. ', ' ft ', ' ft. ', ' x ', ' with ', ' + '];

                    for (const artist of validArtists) {
                        const nameLower = artist.name.toLowerCase();
                        const artistId = artist.id;

                        // Skip if we've already seen this exact artist ID
                        if (seenIds.has(artistId)) {
                            continue;
                        }

                        // Allow up to 2 artists with the same name (different artists, same name)
                        const currentCount = nameCount.get(nameLower) || 0;
                        if (currentCount >= 2) {
                            continue;
                        }

                        // Check if this artist is likely a collab version of an existing one
                        const isRedundant = uniqueArtists.some(existing => {
                            const eName = existing.name.toLowerCase();

                            // If current name contains the existing name and a collab separator
                            const hasSeparator = separators.some(sep => nameLower.includes(sep));
                            return nameLower.includes(eName) && nameLower.length > eName.length && hasSeparator;
                        });

                        if (!isRedundant) {
                            uniqueArtists.push(artist);
                            seenIds.add(artistId);
                            nameCount.set(nameLower, currentCount + 1);
                        }
                    }

                    console.log('Filtered artists:', uniqueArtists);
                    setSearchArtists(uniqueArtists);
                } else {
                    setSearchArtists([]);
                }
            } else if (error) {
                console.error("Supabase Search Error:", error);
                if (currentId === searchRequestId.current) {
                    setSearchResults([]);
                    setSearchArtists([]);
                }
            }

        } catch (error) {
            console.error('Search error:', error);
            if (currentId === searchRequestId.current) {
                setSearchResults([]);
                setSearchArtists([]);
            }
        } finally {
            if (currentId === searchRequestId.current) setIsSearching(false);
        }
    };

    // Fetch Artist Discography (Albums with tracks)
    const fetchArtistDiscography = async (artistId: string | null, artistName: string, artistImage?: string) => {
        setIsSearching(true);
        setSelectedArtist({ id: artistId, name: artistName, image: artistImage });
        setSearchArtists([]);
        setSearchResults([]); // Clear track results
        setExpandedAlbum(null);

        try {
            let finalId = artistId;
            let finalName = artistName;
            let finalImage = artistImage;

            // If we don't have an ID, search to find one first
            if (!finalId && artistName) {
                const { data: searchData } = await supabase.functions.invoke('search-music', {
                    body: { query: artistName }
                });

                if (searchData?.artists?.length > 0) {
                    // Prefer exact name match (case-insensitive) over fuzzy/popularity match
                    const exactMatch = searchData.artists.find(
                        (a: any) => a.name.toLowerCase() === artistName.toLowerCase()
                    );
                    const bestMatch = exactMatch || searchData.artists[0];

                    finalId = bestMatch.id;
                    finalName = bestMatch.name;
                    finalImage = bestMatch.image || artistImage;

                    // Update state to match found artist
                    setSelectedArtist({ id: finalId, name: finalName, image: finalImage });
                }
            }

            if (!finalId) {
                console.warn('Could not resolve artist ID for lookup');
                setArtistAlbums([]);
                setArtistTopSongs([]);
                return;
            }

            const { data, error } = await supabase.functions.invoke('search-music', {
                body: { artistId: finalId, artist: finalName }
            });

            if (data?.albums) {
                setArtistAlbums(data.albums);
            } else {
                setArtistAlbums([]);
            }

            if (data?.topSongs) {
                setArtistTopSongs(data.topSongs);
            } else {
                setArtistTopSongs([]);
            }
            setDiscographyFilter('popular'); // Reset filter
        } catch (error) {
            console.error('Artist discography error:', error);
            setArtistAlbums([]);
            setArtistTopSongs([]);
        } finally {
            setIsSearching(false);
        }
    };

    // Debounce Effect for Search
    const debouncedSearchQuery = useDebounce(searchQuery, 600);
    useEffect(() => {
        searchMusic(debouncedSearchQuery);
    }, [debouncedSearchQuery]);


    // Close Player Logic
    const handleClosePlayer = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        setIsPlaying(false);
        setPlayingTrack(null);
        if (discord.connected) {
            discord.disconnect();
            // Force clear activity
            lastPresenceUpdateRef.current = null;
        }
    };

    // --- Radio Logic ---
    const fetchRadioRecommendations = async (isAppending = false) => {
        if (playlistTracks.length === 0) return [];
        if (isLoadingRadio && !isAppending) return [];
        setIsLoadingRadio(true);

        try {
            // 1. "Vibe Mix" Strategy: Prioritize artists matching the dominant music genres
            const dominantGenres = getDominantMusicGenres(playlistTracks);

            // FunnelIcon artists who contribute to the dominant vibe to ensure consistency (e.g. language/mood)
            const vibeTracks = playlistTracks.filter(t => {
                if (dominantGenres.length === 0) return true;
                return t.genre && dominantGenres.includes(t.genre);
            });

            const candidateArtists = vibeTracks.length > 0 ? vibeTracks.map(t => t.artist) : playlistTracks.map(t => t.artist);
            const uniqueArtists = Array.from(new Set(candidateArtists));

            const shuffledArtists = uniqueArtists.sort(() => 0.5 - Math.random());
            const seeds = shuffledArtists.slice(0, 5);

            // 2. Call Backend Radio Generator with multi-seed support
            const { data, error } = await supabase.functions.invoke('radio-generator', {
                body: { seeds }
            });

            if (error) throw error;

            const newTracks = data?.tracks || [];

            // 3. FunnelIcon Duplicates (Client Side)
            // We strictly filter out tracks already in the playlist
            const finalTracks = newTracks.filter((t: any) => {
                const isDuplicateInPlaylist = playlistTracks.some(pt =>
                    pt.title.trim().toLowerCase() === t.title.trim().toLowerCase() &&
                    pt.artist.trim().toLowerCase() === t.artist.trim().toLowerCase()
                );

                // If appending, also check against current radio list
                const isDuplicateInRadio = isAppending && radioTracks.some(rt =>
                    rt.title.toLowerCase() === t.title.toLowerCase() &&
                    rt.artist.toLowerCase() === t.artist.toLowerCase()
                );

                return !isDuplicateInPlaylist && !isDuplicateInRadio;
            });

            // 4. Update State
            const formattedTracks = finalTracks.map((t: any) => ({
                id: crypto.randomUUID(),
                title: t.title,
                artist: t.artist,
                artistId: t.artistId, // Preserve artistId for direct lookup
                cover: t.cover,
                duration: t.duration,
                preview: t.preview,
                genre: t.genre
            }));

            if (isAppending) {
                setRadioTracks(prev => [...prev, ...formattedTracks]);
            } else {
                setRadioTracks(formattedTracks);
                // Scroll to top of radio if regenerating
                const radioContainer = document.querySelector('.radio-list-container');
                if (radioContainer) radioContainer.scrollTo({ top: 0, behavior: 'smooth' });
            }

            return formattedTracks;

        } catch (e) {
            console.error('Radio fetch failed', e);
            return [];
        } finally {
            setIsLoadingRadio(false);
        }
    };

    const handleRadioClick = async () => {
        setViewMode('radio');
        if (radioTracks.length === 0) {
            setIsShuffle(true);
            shouldAutoPlayRadio.current = true;
            await fetchRadioRecommendations();
        }
    };

    // --- Generate Mood Image from Playlist ---
    const handleGenerateMood = async () => {
        if (!playlistTracks.length || !selectedProject) return;

        // Switch to Assets tab to show loading
        setActiveTab('assets');
        setIsGeneratingMood(true);

        try {
            // 1. Collect playlist data
            const genres = getDominantMusicGenres(playlistTracks);
            const project = projects.find(p => p.id === selectedProject);

            // 2. Call prompt-generator edge function
            const { data, error } = await supabase.functions.invoke('prompt-generator', {
                body: {
                    tracks: playlistTracks.slice(0, 20).map(t => ({
                        title: t.title,
                        artist: t.artist
                    })),
                    genres,
                    projectDescription: project?.description || project?.title
                }
            });

            if (error) {
                console.error('Prompt generation error:', error);
                return;
            }

            if (!data?.prompt || !data?.title) {
                console.error('Invalid prompt response:', data);
                return;
            }

            console.log('🎨 Generated mood prompt:', data);

            // 3. Generate image in background using Z-Image
            console.log('🖼️ Generating image with aspect ratio:', data.aspectRatio);
            const imageBlob = await generateImageBlob(data.prompt, 'prunaai/z-image-turbo', {
                aspectRatio: data.aspectRatio || '21:9'
            });

            // 4. Upload to R2 (Using existing library)
            console.log('📤 Uploading to R2...');
            const fileName = `mood-${Date.now()}-${crypto.randomUUID()}.jpg`;

            // Convert Blob to File for uploadToR2
            const file = new File([imageBlob], fileName, { type: 'image/jpeg' });

            let imageUrl: string;

            try {
                // Dynamically import to avoid loading AWS SDK if not needed immediately
                const { uploadToR2 } = await import('../lib/storage');
                const userId = user?.id || 'anonymous';
                imageUrl = await uploadToR2(file, `lookbook/${userId}/uploads`, userId);
                console.log('✅ R2 Upload success:', imageUrl);
            } catch (uploadError) {
                console.error('R2 library upload failed:', uploadError);
                // Fallback to edge function if client-side fails (e.g. CORS/Env issues)
                const userId = user?.id || 'anonymous';
                const { data: edgeData, error: edgeError } = await supabase.functions.invoke('r2-upload', {
                    body: {
                        fileName: `lookbook/${userId}/uploads/${fileName}`,
                        contentType: 'image/jpeg',
                        imageData: await blobToBase64(imageBlob)
                    }
                });

                if (edgeData?.url) {
                    imageUrl = edgeData.url;
                } else {
                    console.warn('Edge upload also failed, using blob URL');
                    imageUrl = URL.createObjectURL(imageBlob);
                }
            }

            console.log('✅ Image ready:', imageUrl);

            // 5a. Save to UserIcon Gallery (Reliable Fetch)
            if (user) {
                const { data: { session } } = await supabase.auth.getSession();
                if (session?.access_token) {
                    const res = await fetch(`${(import.meta as any).env.VITE_SUPABASE_URL}/rest/v1/generations`, {
                        method: 'POST',
                        headers: {
                            'apikey': (import.meta as any).env.VITE_SUPABASE_ANON_KEY,
                            'Authorization': `Bearer ${session.access_token}`,
                            'Content-Type': 'application/json',
                            'Prefer': 'return=representation'
                        },
                        body: JSON.stringify({
                            user_id: user.id, prompt: data.prompt, image_url: imageUrl, model: 'prunaai/z-image-turbo',
                            settings: { aspect_ratio: data.aspectRatio || '21:9', source: 'mood-playlist' }
                        })
                    });

                    if (res.ok) console.log('✅ Saved to user gallery (Fetch)');
                    else console.error('❌ Gallery Save Failed:', res.status);
                }
            }

            // 6. Save to project assets (MOOD scene)
            // 5. Save to project assets (MOOD scene) -> Renamed to 6
            const { data: scenes } = await supabase
                .from('scenes')
                .select('id')
                .eq('project_id', selectedProject)
                .eq('slug', 'MOOD')
                .limit(1);

            let moodSceneId: string;

            if (!scenes || scenes.length === 0) {
                const newScene = await ProjectService.createScene(selectedProject, 'MOOD');
                if (newScene) {
                    moodSceneId = newScene.id;
                } else {
                    console.error('Failed to create Mood scene');
                    return;
                }
            } else {
                moodSceneId = scenes[0].id;
            }


            await ProjectService.addShot(
                moodSceneId,
                imageUrl,
                data.prompt,
                data.title
            );

            console.log('✅ Mood image saved to project:', data.title);

            // Refresh project data to show new asset
            const updatedProject = await ProjectService.getProject(selectedProject);
            if (updatedProject) {
                setProjects(prev => prev.map(p => p.id === selectedProject ? updatedProject : p));
            }

            console.log('🚀 Navigating to scenes with image:', imageUrl);
            onNavigateToLookbook?.({
                prompt: data.prompt,
                title: data.title,
                projectId: selectedProject,
                imageUrl: imageUrl,
                aspectRatio: data.aspectRatio || '21:9'
            });

        } catch (e) {
            console.error('Mood generation failed:', e);
        } finally {
            setIsGeneratingMood(false);
        }
    };

    // Helper: Convert blob to base64
    const blobToBase64 = (blob: Blob): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    // Auto-play Effect for Radio once loaded
    useEffect(() => {
        if (shouldAutoPlayRadio.current && !isLoadingRadio && radioTracks.length > 0) {
            shouldAutoPlayRadio.current = false;
            // Start at a random random part as requested
            const randomIndex = Math.floor(Math.random() * radioTracks.length);
            playRadioTrack(radioTracks[randomIndex], randomIndex);
        }
    }, [radioTracks, isLoadingRadio]);

    // Update Radio Logic:
    // 1. If viewing radio and list is empty -> Fetch
    // 2. If playlist CHANGED -> Clear radio (so next view refetches)
    // Ref: UserIcon wants "save seed... UNTIL they add a new song"

    // Use a Ref to store previous playlist ID signature to detect changes
    const prevPlaylistSignature = useRef('');

    useEffect(() => {
        const signature = playlistTracks.map(t => t.id).sort().join(',');

        if (prevPlaylistSignature.current !== '' && prevPlaylistSignature.current !== signature) {
            // Playlist Changed! Reset Radio.
            setRadioTracks([]);
        }
        prevPlaylistSignature.current = signature;

        // If in Radio Mode and Empty -> Auto Fetch
        if (viewMode === 'radio' && radioTracks.length === 0 && playlistTracks.length > 0) {
            fetchRadioRecommendations();
        }
    }, [viewMode, playlistTracks]);

    // Track scroll position for "Back to Top" button
    useEffect(() => {
        const handleScroll = (e: Event) => {
            const target = e.target as HTMLElement;
            if (target) {
                // Show button when scrolled more than 400px (roughly 8-10 tracks)
                setShowBackToTop(target.scrollTop > 400);
            }
        };

        // Delay attachment to ensure DOM is ready
        const timeoutId = setTimeout(() => {
            const containers = document.querySelectorAll('.custom-scrollbar');
            containers.forEach(c => c.addEventListener('scroll', handleScroll));
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            const containers = document.querySelectorAll('.custom-scrollbar');
            containers.forEach(c => c.removeEventListener('scroll', handleScroll));
        };
    }, [activeTab, viewMode, playingTrack]); // Re-attach when tab, view, or playing state changes

    const playRadioTrack = async (track: any, index: number) => {
        // if (!player) return; // FIX: Don't return early; allow state to mount player.
        try {
            // Radio Context
            setActiveContext('radio');
            setRadioTrackIndex(index);
            setCurrentTrackIndex(-1); // Deselect playlist items

            // Optimistic UI update
            const tempTrack = {
                name: track.title,
                artist: track.artist,
                artistId: track.artistId, // Pass through for direct artist lookup
                image: track.cover,
                videoId: undefined,
                genre: track.genre,
                duration: track.duration,
                preview: track.preview
            };
            setPlayingTrack(tempTrack);
            setIsPlaying(true);
            setLoadingVideo(true);

            // Use the SAME robust search logic as the main playlist
            // 1. Check if we strictly have a videoId (unlikely for Radio)
            // 2. Search via Supabase Proxy with "Best Match" filtering
            // STRICT MODE OFF: For Radio, we just want to play music. If strict match fails, give us the best guess.
            let videoId = await fetchYoutubeId(`${track.title} ${track.artist}`, track.title, track.artist, false, failedVideoIds.current);

            if (videoId) {
                setPlayingTrack(prev => prev ? { ...prev, videoId } : null);
                try {
                    if (player) player.loadVideoById({ videoId, suggestedQuality: 'hd1080' });
                } catch (e) {
                    console.warn('Player not ready, video will load on next interaction');
                }
            } else {
                console.warn(`No valid video match found for "${track.title}" - auto-skipping`);
                // Remove from radio and auto-skipping
                setRadioTracks(prev => prev.filter(t => t.id !== track.id));
                setLoadingVideo(false);
                // Play next in radio
                const nextIndex = index < radioTracks.length - 1 ? index : 0;
                if (radioTracks.length > 1) {
                    const nextTrack = radioTracks.filter(t => t.id !== track.id)[nextIndex];
                    if (nextTrack) playRadioTrack(nextTrack, nextIndex);
                }
                return;
            }
        } catch (e) {
            console.error('Radio play failed', e);
        } finally {
            setLoadingVideo(false);
        }
    };

    const playSearchTrack = async (track: any, index: number) => {
        try {
            // Search Context
            setActiveContext('search');
            setSearchTrackIndex(index);
            setCurrentTrackIndex(-1);
            setRadioTrackIndex(-1);

            // Optimistic UI update
            const tempTrack = {
                name: track.title,
                artist: track.artist,
                artistId: track.artistId, // Pass through for direct artist lookup
                image: track.cover,
                videoId: undefined,
                genre: track.genre,
                duration: track.duration
            };
            setPlayingTrack(tempTrack);
            setIsPlaying(true);
            setLoadingVideo(true);

            let videoId = await fetchYoutubeId(`${track.title} ${track.artist}`, track.title, track.artist, false, failedVideoIds.current);

            if (videoId) {
                setPlayingTrack(prev => prev ? { ...prev, videoId } : null);
                try {
                    if (player) player.loadVideoById({ videoId, suggestedQuality: 'hd1080' });
                } catch (e) {
                    console.warn('Player not ready, video will load on next interaction');
                }
            } else {
                console.warn(`No valid video match found for "${track.title}" - auto-skipping`);
                // Remove from search results and auto-skip
                setSearchResults(prev => prev.filter(t => t.id !== track.id));
                setLoadingVideo(false);
                // Play next in search
                const nextIndex = index < searchResults.length - 1 ? index : 0;
                if (searchResults.length > 1) {
                    const nextTrack = searchResults.filter(t => t.id !== track.id)[nextIndex];
                    if (nextTrack) playSearchTrack(nextTrack, nextIndex);
                }
                return;
            }
        } catch (e) {
            console.error('Search play failed', e);
        } finally {
            setLoadingVideo(false);
        }
    };


    const handleArtistClick = async (e: React.MouseEvent, artistName: string, artistId?: string, trackName?: string) => {
        e.stopPropagation();
        if (!artistName) return;

        console.log('[handleArtistClick] artistName:', artistName, 'artistId:', artistId, 'trackName:', trackName);

        // If we have the artistId, use it directly - no guessing needed
        if (artistId) {
            console.log('[handleArtistClick] Using direct artistId lookup');
            setSelectedArtist({ name: artistName, id: artistId, image: null });
            setViewMode('search');
            fetchArtistDiscography(artistId, artistName, null);
            return;
        }

        // Fallback: EXTRACT PRIMARY ARTIST if it's a collab (e.g. "Grimes & Anyma")
        const separators = / x | & | feat\. | ft\. |,| and | featuring /i;
        const primaryArtist = artistName.split(separators)[0].trim();

        // Try to find artistId by searching for the TRACK (not just artist name)
        // This ensures we get the correct artist when multiple artists share the same name
        console.log('[handleArtistClick] No artistId - searching for track to find correct artist');
        setSelectedArtist({ name: primaryArtist, id: null, image: null });
        setViewMode('search');

        try {
            // Search for the specific track to get the correct artistId
            const searchQuery = trackName ? `${trackName} ${primaryArtist}` : primaryArtist;
            console.log('[handleArtistClick] Search query:', searchQuery);

            const { data: searchData } = await supabase.functions.invoke('search-music', {
                body: { query: searchQuery }
            });

            // If we searched for a track, find the track and extract artistId from it
            if (trackName && searchData?.tracks?.length > 0) {
                // Find track where BOTH title AND artist match - this ensures we get the correct artist
                // when multiple artists share the same name (e.g., "Ellis")
                const normalizedTrackName = trackName.toLowerCase().replace(/[^\w\s]/g, '').trim();
                const normalizedArtist = primaryArtist.toLowerCase();

                // Priority 1: Find track where BOTH title AND artist match
                const matchingTrack = searchData.tracks.find((t: any) => {
                    const resultTitle = (t.title || '').toLowerCase().replace(/[^\w\s]/g, '').trim();
                    const resultArtist = (t.artist || '').toLowerCase();
                    const titleMatches = resultTitle.includes(normalizedTrackName) || normalizedTrackName.includes(resultTitle);
                    const artistMatches = resultArtist.includes(normalizedArtist);
                    return titleMatches && artistMatches;
                }) || searchData.tracks.find((t: any) => {
                    // Priority 2: Just title match
                    const resultTitle = (t.title || '').toLowerCase().replace(/[^\w\s]/g, '').trim();
                    return resultTitle.includes(normalizedTrackName) || normalizedTrackName.includes(resultTitle);
                }) || searchData.tracks.find((t: any) =>
                    // Priority 3: Just artist match
                    (t.artist || '').toLowerCase().includes(normalizedArtist)
                );

                if (matchingTrack?.artistId) {
                    console.log('[handleArtistClick] Found artistId from track:', matchingTrack.artistId, 'title:', matchingTrack.title, 'artist:', matchingTrack.artist);
                    setSelectedArtist({ name: matchingTrack.artist, id: matchingTrack.artistId, image: null });
                    fetchArtistDiscography(matchingTrack.artistId, matchingTrack.artist, null);
                    return;
                }
            }

            // Fallback to artist search results
            if (searchData?.artists?.length > 0) {
                const exactMatch = searchData.artists.find(
                    (a: any) => a.name.toLowerCase() === primaryArtist.toLowerCase()
                );
                const bestMatch = exactMatch || searchData.artists[0];

                if (bestMatch?.id) {
                    console.log('[handleArtistClick] Found artistId via artist search:', bestMatch.id);
                    setSelectedArtist({ name: bestMatch.name, id: bestMatch.id, image: null });
                    fetchArtistDiscography(bestMatch.id, bestMatch.name, null);
                    return;
                }
            }
        } catch (e) {
            console.error('[handleArtistClick] Search failed:', e);
        }

        // Final fallback - use name-based search
        console.log('[handleArtistClick] Final fallback - name-based search');
        fetchArtistDiscography(null, primaryArtist, null);
    };


    const removeFromPlaylist = (trackId: string) => {
        const trackIndex = playlistTracks.findIndex(t => t.id === trackId);
        if (trackIndex === -1) return;

        const newTracks = playlistTracks.filter(t => t.id !== trackId);
        setPlaylistTracks(newTracks);
        persistPlaylist(newTracks);

        // Auto-Progress Logic
        if (trackIndex === currentTrackIndex && playingTrack) {
            // We deleted the currently playing song
            if (newTracks.length > 0) {
                // Play the song that fell into this index (or wrap to 0)
                const nextIndex = trackIndex >= newTracks.length ? 0 : trackIndex;
                playOnYoutube(newTracks[nextIndex], nextIndex);
            } else {
                // Playlist is empty
                setPlayingTrack(null);
                setLoadingVideo(false);
                setIsPlaying(false);
                if (player) {
                    player.stopVideo();
                    setPlayer(null); // Clear player ref safely
                }
            }
        } else if (trackIndex < currentTrackIndex) {
            // We deleted a song *before* the current one, so shift index down
            setCurrentTrackIndex(prev => prev - 1);
        }
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            setPlaylistTracks((items) => {
                const oldIndex = items.findIndex((t) => t.id === active.id);
                const newIndex = items.findIndex((t) => t.id === over?.id);

                const newOrder = arrayMove(items, oldIndex, newIndex);
                persistPlaylist(newOrder); // Save new order

                // Adjust currentTrackIndex if necessary
                if (currentTrackIndex === oldIndex) {
                    setCurrentTrackIndex(newIndex);
                } else if (
                    currentTrackIndex > oldIndex && currentTrackIndex <= newIndex
                ) {
                    setCurrentTrackIndex(currentTrackIndex - 1);
                } else if (
                    currentTrackIndex < oldIndex && currentTrackIndex >= newIndex
                ) {
                    setCurrentTrackIndex(currentTrackIndex + 1);
                }

                return newOrder;
            });
        }
    };

    const playlistContent = (
        <div className="relative overflow-x-hidden">
            {/* Floating Back to Top Button - Rendered via Portal */}
            {showBackToTop && playlistTracks.length >= 20 && !playlistSearchQuery && viewMode === 'playlist' && activeTab === 'music' && (
                <Portal>
                    <button
                        onClick={() => {
                            const scrollContainer = document.querySelector('.custom-scrollbar');
                            if (scrollContainer) scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[9998] flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-800/95 hover:bg-zinc-700 border border-white/10 text-zinc-400 hover:text-white transition-all shadow-lg backdrop-blur-sm animate-in fade-in slide-in-from-bottom-2 duration-200"
                    >
                        <CaretUpIcon className="w-3.5 h-3.5" />
                        <span className="text-[10px] font-medium">Back to top</span>
                    </button>
                </Portal>
            )}
            <AnimatePresence mode="wait">
                {/* Listen Along Mode - show only the banner, hide all playlist controls */}
                {isListeningAlong && hostId ? (
                    <motion.div
                        key="listening-along"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-4"
                    >
                        <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-3 flex flex-col gap-3">
                            <div className="flex items-center gap-3">
                                <span className="relative flex h-3 w-3">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-3 w-3 bg-indigo-500"></span>
                                </span>
                                <div className="flex-1">
                                    <p className="text-xs font-bold text-indigo-300">Listening Along</p>
                                    <p className="text-[10px] text-indigo-400/70">Synced with <span className="font-semibold text-indigo-300">@{hostId}</span></p>
                                </div>
                                <button
                                    onClick={disconnectListenAlong}
                                    className="p-1 hover:bg-white/10 rounded text-indigo-300"
                                    title="Stop Listening"
                                >
                                    <XIcon className="w-4 h-4" />
                                </button>
                            </div>
                            <p className="text-[10px] text-indigo-400/50 text-center">
                                Your playlist is hidden while listening along. Click X to regain control.
                            </p>
                            {/* Request to Join button - only show if host has a project and user is not a member */}
                            {hostProjectId && !isHostProjectMember && (
                                <button
                                    onClick={async () => {
                                        const success = await ProjectMemberService.requestToJoinProject(hostProjectId);
                                        if (success) {
                                            setToast({ type: 'success', message: 'Join request sent to project owner!' });
                                        } else {
                                            setToast({ type: 'error', message: 'Could not send request. You may have already requested to join.' });
                                        }
                                    }}
                                    className="w-full py-2 bg-indigo-500/20 hover:bg-indigo-500/30 border border-indigo-500/30 rounded-lg text-xs font-medium text-indigo-300 flex items-center justify-center gap-2 transition-colors"
                                >
                                    <UserPlusIcon className="w-4 h-4" />
                                    Request to Join Project
                                </button>
                            )}
                        </div>
                    </motion.div>
                ) : viewMode === 'playlist' ? (
                    <motion.div
                        key="playlist"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-4"
                    >
                        {/* Listeners Banner - shown when others are listening to us */}
                        {!isListeningAlong && listeners.length > 0 && (
                            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-2 flex items-center gap-3">
                                <span className="relative flex h-3 w-3">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                                </span>
                                <div className="flex-1">
                                    <p className="text-xs font-bold text-emerald-300">
                                        {listeners.length} {listeners.length === 1 ? 'Listener' : 'Listeners'}
                                    </p>
                                    <p className="text-[10px] text-emerald-400/70 truncate">
                                        {listeners.map(l => `@${l}`).join(', ')}
                                    </p>
                                </div>
                                <RadioIcon className="w-4 h-4 text-emerald-400" />
                            </div>
                        )}

                        {/* Header / Add Button */}
                        {/* 🌟 MOOD GENERATION OVERLAY 🌟 */}
                        <AnimatePresence>
                            {isGeneratingMood && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="absolute inset-0 z-50 bg-onyx/95 backdrop-blur-md flex flex-col items-center justify-center p-6 text-center space-y-8 rounded-xl border border-white/5"
                                >
                                    {/* Abstract Animation */}
                                    <div className="relative w-32 h-32 flex items-center justify-center">
                                        <div className="absolute inset-0 bg-violet-500/20 rounded-full animate-[ping_3s_linear_infinite]" />
                                        <div className="absolute inset-4 bg-indigo-500/20 rounded-full animate-pulse" />
                                        <div className="absolute inset-0 border border-white/5 rounded-full animate-[spin_8s_linear_infinite]" />
                                        <div className="absolute inset-0 border-t border-indigo-500/50 rounded-full animate-[spin_2s_linear_infinite]" />
                                        <SparkleIcon className="w-8 h-8 text-white relative z-10 animate-pulse" />
                                    </div>

                                    <div className="space-y-2 z-10">
                                        <motion.h3
                                            key={loadingPhase}
                                            initial={{ opacity: 0, y: 5 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-200 to-violet-200 tracking-[0.2em] uppercase"
                                        >
                                            {loadingPhase}
                                        </motion.h3>
                                        <p className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider">
                                            AI Processing • {playlistTracks.length} Tracks
                                        </p>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Queue Panel - Shows tracks queued to play next */}
                        {queue.length > 0 && (
                            <div className="mb-4 bg-indigo-500/5 border border-indigo-500/20 rounded-xl p-3">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <QueueIcon className="w-4 h-4 text-indigo-400" />
                                        <h4 className="text-xs font-bold text-indigo-300 uppercase tracking-wider">
                                            Up Next ({queue.length})
                                        </h4>
                                    </div>
                                    <button
                                        onClick={clearQueue}
                                        className="text-[10px] text-zinc-500 hover:text-white transition-colors"
                                    >
                                        Clear
                                    </button>
                                </div>
                                <div className="space-y-1 max-h-40 overflow-y-auto scrollbar-hide">
                                    {queue.map((track, i) => (
                                        <div
                                            key={`queue-${track.id}-${track.addedAt}`}
                                            className="flex items-center gap-2 p-2 rounded-lg bg-white/5 group hover:bg-white/10 transition-colors"
                                        >
                                            <img
                                                src={track.cover || 'https://via.placeholder.com/32'}
                                                className="w-8 h-8 rounded object-cover flex-shrink-0"
                                                alt={track.title}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-white truncate">{track.title}</p>
                                                <p className="text-[10px] text-zinc-500 truncate">
                                                    {track.artist} • Added by @{track.addedByName}
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => removeFromQueue(track.id, track.addedAt)}
                                                className="p-1 text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                                                title="Remove from queue"
                                            >
                                                <XIcon className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Header / Actions */}
                        <div className="flex items-center justify-between mb-4 pl-1 pr-1">
                            <div className="flex flex-col">
                                <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wider">Soundtrack</h3>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-[10px] font-bold text-white">{playlistTracks.length} Tracks</span>
                                    {totalDuration && <span className="text-[10px] text-zinc-500">({totalDuration})</span>}
                                </div>
                                {/* Elegant Genre Display */}
                                {getDominantMusicGenres(playlistTracks).length > 0 && (
                                    <div className="text-[10px] font-medium text-indigo-400 mt-1 flex items-center gap-1.5 opacity-90">
                                        {getDominantMusicGenres(playlistTracks).join(' • ')}
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center gap-2">
                                {/* Generate Mood Button - 3+ tracks - TEMPORARILY DISABLED */}
                                {false && playlistTracks.length >= 3 && onNavigateToLookbook && (
                                    <button
                                        onClick={handleGenerateMood}
                                        disabled={true}
                                        className="group flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-800 hover:bg-zinc-700 border border-white/5 hover:border-white/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Generate mood image from playlist (Temporarily disabled)"
                                    >
                                        {isGeneratingMood ? (
                                            <div className="w-3 h-3 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                            <MagicWandIcon className="w-3 h-3 text-indigo-400/70 group-hover:text-indigo-400 transition-colors" />
                                        )}
                                        <span className="text-[10px] font-medium text-zinc-400 group-hover:text-white transition-colors">
                                            {isGeneratingMood ? 'Creating...' : 'Visualize Mood'}
                                        </span>
                                    </button>
                                )}
                                {playlistTracks.length >= 5 && (
                                    <button
                                        onClick={handleRadioClick}
                                        className="group flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-800 hover:bg-zinc-700 border border-white/5 hover:border-white/10 transition-all"
                                    >
                                        <MicrophoneIcon className="w-3 h-3 text-indigo-400/70 group-hover:text-indigo-400 transition-colors" />
                                        <span className="text-[10px] font-medium text-zinc-400 group-hover:text-white transition-colors">Artist Blend</span>
                                    </button>
                                )}
                                {deletedTracks.length > 0 && (
                                    <button
                                        onClick={handleUndoDelete}
                                        className="p-1.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-indigo-400 transition-all border border-white/5 hover:border-white/10"
                                        title="Undo Delete"
                                    >
                                        <ArrowCounterClockwiseIcon className="w-4 h-4" />
                                    </button>
                                )}
                                {/* Playlist Search - Expands on click */}
                                {playlistTracks.length > 0 && (
                                    <div className={`flex items-center transition-all duration-200 ${playlistSearchOpen ? 'bg-zinc-800 rounded-full border border-white/10' : ''}`}>
                                        {playlistSearchOpen && (
                                            <input
                                                ref={playlistSearchInputRef}
                                                type="text"
                                                value={playlistSearchQuery}
                                                onChange={(e) => setPlaylistSearchQuery(e.target.value)}
                                                placeholder="Search playlist..."
                                                className="w-28 bg-transparent text-xs text-white placeholder-zinc-500 px-3 py-1.5 outline-none"
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Escape') {
                                                        setPlaylistSearchOpen(false);
                                                        setPlaylistSearchQuery('');
                                                    }
                                                }}
                                            />
                                        )}
                                        <button
                                            onClick={() => {
                                                if (playlistSearchOpen && playlistSearchQuery) {
                                                    setPlaylistSearchQuery('');
                                                } else {
                                                    setPlaylistSearchOpen(!playlistSearchOpen);
                                                    if (!playlistSearchOpen) {
                                                        setTimeout(() => playlistSearchInputRef.current?.focus(), 100);
                                                    }
                                                }
                                            }}
                                            className={`p-1.5 rounded-full transition-all ${playlistSearchOpen
                                                ? 'text-indigo-400 hover:text-white'
                                                : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white border border-white/5 hover:border-white/10'
                                                }`}
                                            title={playlistSearchOpen ? (playlistSearchQuery ? 'Clear' : 'Close search') : 'Search playlist'}
                                        >
                                            {playlistSearchOpen && playlistSearchQuery ? (
                                                <XIcon className="w-4 h-4" />
                                            ) : (
                                                <MagnifyingGlassIcon className="w-4 h-4" />
                                            )}
                                        </button>
                                    </div>
                                )}
                                <button
                                    onClick={() => setViewMode('search')}
                                    className={`p-1.5 rounded-full transition-all border ${isImportingCSV
                                        ? 'bg-indigo-500/20 border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/30'
                                        : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white border-white/5 hover:border-white/10'
                                        }`}
                                    title={isImportingCSV ? `Importing ${csvImportProgress.current}/${csvImportProgress.total}...` : "Add Music"}
                                >
                                    {isImportingCSV ? (
                                        <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <PlusIcon className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Playlist Items */}
                        {playlistTracks.length > 0 ? (
                            (() => {
                                // Filter first, then slice for virtualization
                                const filteredTracks = playlistTracks.filter(track => {
                                    if (!playlistSearchQuery) return true;
                                    const q = playlistSearchQuery.toLowerCase();
                                    return track.title.toLowerCase().includes(q) ||
                                        track.artist.toLowerCase().includes(q);
                                });
                                // When searching, show all filtered results; otherwise virtualize
                                const tracksToShow = playlistSearchQuery
                                    ? filteredTracks
                                    : filteredTracks.slice(0, visibleTrackCount);

                                return (
                                    <div className="relative">
                                        <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestCenter}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <SortableContext
                                                items={tracksToShow}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                <div className="space-y-2">
                                                    {tracksToShow.map((track) => {
                                                        // Find actual index for playback
                                                        const actualIndex = playlistTracks.findIndex(t => t.id === track.id);
                                                        return (
                                                            <SortableTrackItem
                                                                key={track.id}
                                                                track={track}
                                                                index={actualIndex}
                                                                isActive={currentTrackId === track.id && isPlaying && activeContext === 'playlist'}
                                                                isPlaying={isPlaying}
                                                                onPlay={() => playOnYoutube(track, actualIndex)}
                                                                onPause={togglePlay}
                                                                onDelete={handleRemoveTrack}
                                                                onArtistClick={handleArtistClick}
                                                                onAddToQueue={addToQueue}
                                                            />
                                                        );
                                                    })}
                                                    {/* Load more trigger - only show when there are more tracks to load */}
                                                    {!playlistSearchQuery && visibleTrackCount < playlistTracks.length && (
                                                        <button
                                                            ref={loadMoreRef}
                                                            onClick={loadMoreTracks}
                                                            className="w-full py-4 text-center text-xs text-zinc-500 hover:text-zinc-300 hover:bg-white/5 rounded-lg transition-colors cursor-pointer"
                                                        >
                                                            <div className="w-5 h-5 border-2 border-zinc-600 border-t-indigo-500 rounded-full animate-spin mx-auto" />
                                                            <p className="mt-2">Loading more tracks... (click to load)</p>
                                                        </button>
                                                    )}
                                                </div>
                                            </SortableContext>
                                        </DndContext>
                                    </div>
                                );
                            })()
                        ) : (
                            <div className="text-center py-12 border border-dashed border-white/5 rounded-xl bg-zinc-900/20 backdrop-blur-sm">
                                <MusicNoteIcon className="w-10 h-10 text-zinc-800 mx-auto mb-3" />
                                <p className="text-sm text-zinc-500 font-medium">Your playlist is empty.</p>
                                <button
                                    onClick={() => setViewMode('search')}
                                    className="mt-3 text-xs text-indigo-400 hover:text-indigo-300 underline"
                                >
                                    Add your first song
                                </button>
                            </div>
                        )}
                    </motion.div>
                ) : viewMode === 'radio' ? (
                    <motion.div
                        key="radio"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-4"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <button
                                onClick={() => setViewMode('playlist')}
                                className="p-2 -ml-2 hover:bg-white/5 rounded-full text-zinc-400 hover:text-white transition-colors"
                            >
                                <CaretLeftIcon className="w-5 h-5" />
                            </button>
                            <span className="text-sm font-bold text-white flex items-center gap-2">
                                <MicrophoneIcon className="w-4 h-4 text-indigo-400" />
                                Artist Blend
                            </span>
                        </div>

                        <div className="space-y-1 pr-1 pb-20 radio-list-container">
                            {isLoadingRadio && (
                                <div className="py-8 flex justify-center">
                                    <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                                </div>
                            )}

                            {radioTracks.map((track, index) => {
                                const isCurrent = isPlaying && playingTrack?.name === track.title && playingTrack?.artist === track.artist && activeContext === 'radio';

                                return (
                                    <div
                                        key={track.id}
                                        onClick={() => playRadioTrack(track, index)}
                                        className={`w-full flex items-center gap-4 p-3 rounded-xl hover:bg-white/5 group text-left transition-colors relative cursor-pointer ${isCurrent ? 'bg-indigo-500/10 border border-indigo-500/20' : 'border border-transparent'}`}
                                    >
                                        <div
                                            className="flex items-center gap-4 flex-1 min-w-0 text-left"
                                        >
                                            <div className="w-12 h-12 rounded-lg bg-zinc-800 flex-shrink-0 relative overflow-hidden flex items-center justify-center group-hover:bg-zinc-700 shadow-sm transition-all">
                                                {track.cover ? (
                                                    <>
                                                        <img
                                                            src={track.cover}
                                                            className={`w-full h-full object-cover relative z-10 ${isCurrent ? 'brightness-50' : 'group-hover:brightness-50'} transition-all`}
                                                            onError={(e) => e.currentTarget.style.display = 'none'}
                                                        />
                                                        {/* Play/Pause Overlay */}
                                                        <div className={`absolute inset-0 z-20 flex items-center justify-center ${isCurrent ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                                                            {isCurrent ? (
                                                                <div className="flex gap-0.5 items-end h-4 pb-0.5">
                                                                    <div className="w-1 bg-white animate-music-bar-1 h-3 rounded-full" />
                                                                    <div className="w-1 bg-white animate-music-bar-2 h-4 rounded-full" />
                                                                    <div className="w-1 bg-white animate-music-bar-3 h-2 rounded-full" />
                                                                </div>
                                                            ) : (
                                                                <PlayIcon className="w-5 h-5 text-white fill-white" />
                                                            )}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <MusicNoteIcon className="w-5 h-5 text-zinc-600" />
                                                )}


                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className={`text-base font-medium truncate leading-tight mb-0.5 ${isCurrent ? 'text-indigo-400' : 'text-zinc-200 group-hover:text-white'}`}>{track.title}</p>
                                                <button
                                                    onClick={(e) => handleArtistClick(e, track.artist, track.artistId, track.title)}
                                                    className="text-sm text-zinc-500 truncate group-hover:text-zinc-400 transition-colors hover:text-white hover:underline block max-w-full text-left"
                                                >
                                                    {track.artist}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Duration */}
                                        {track.duration && (
                                            <span className="text-xs font-mono text-zinc-600 hidden sm:block">{track.duration}</span>
                                        )}

                                        {/* Add Button */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                addToPlaylist(track);
                                            }}
                                            className="p-2 text-zinc-600 hover:text-indigo-400 hover:bg-white/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                                            title="Add to Soundtrack"
                                        >
                                            <PlusIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                );
                            })}

                            {!isLoadingRadio && radioTracks.length === 0 && (
                                <div className="text-center py-10 opacity-50">
                                    <p className="text-sm">Add playing tracks to generate radio.</p>
                                </div>
                            )}

                            {radioTracks.length > 0 && (
                                <button
                                    onClick={() => fetchRadioRecommendations(false)}
                                    disabled={isLoadingRadio}
                                    className="w-full py-4 mt-6 flex items-center justify-center gap-3 text-[10px] font-bold text-zinc-400 uppercase tracking-[0.2em] hover:text-white hover:bg-indigo-500/10 rounded-xl transition-all border border-dashed border-white/5 hover:border-indigo-500/20 group"
                                >
                                    {isLoadingRadio ? (
                                        <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            <SparkleIcon className="w-3.5 h-3.5 text-indigo-400 group-hover:animate-pulse" />
                                            Regenerate Score Radio
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    </motion.div>
                ) : (
                    <motion.div
                        key="search"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-4"
                    >
                        {selectedArtist ? (
                            <div className="w-full mb-8 pl-1 pr-1">
                                <div className="flex justify-between items-center">
                                    {/* LEFT: Navigation Controls */}
                                    <div className="flex flex-col gap-4 items-start">
                                        <button
                                            onClick={() => setViewMode('playlist')}
                                            className="flex items-center gap-3 group px-2 py-1 -ml-2 rounded-lg hover:bg-white/5 transition-colors"
                                        >
                                            <CaretLeftIcon className="w-5 h-5 text-zinc-400 group-hover:text-white" />
                                            <span className="text-sm font-bold text-white">Playlist</span>
                                        </button>

                                        <button
                                            onClick={() => setSelectedArtist(null)}
                                            className="flex items-center gap-3 group px-2 py-1 -ml-2 rounded-lg hover:bg-white/5 transition-colors"
                                        >
                                            <MagnifyingGlassIcon className="w-5 h-5 text-zinc-400 group-hover:text-white" />
                                            <span className="text-sm font-bold text-white">Search</span>
                                        </button>

                                        <button
                                            onClick={() => {
                                                // FunnelIcon out "Live" albums/EPs/Singles (e.g. "Live at...", "Album (Live)")
                                                // Use word boundary to allow "Alive", "Olive", etc.
                                                const isStudio = (name: string) => !/\blive\b/i.test(name || '');

                                                const studioAlbums = artistAlbums.filter(a => isStudio(a.name));
                                                const allTracks = studioAlbums.flatMap(a => a.tracks || []);

                                                // Also filter top songs distinct from live albums
                                                const studioTopSongs = artistTopSongs.filter(t => isStudio(t.album));

                                                const combined = [...allTracks, ...studioTopSongs];
                                                // Deduplicate by ID
                                                const seen = new Set();
                                                const unique = [];
                                                for (const t of combined) {
                                                    if (!seen.has(t.id)) {
                                                        seen.add(t.id);
                                                        unique.push(t);
                                                    }
                                                }

                                                const shuffled = unique.sort(() => Math.random() - 0.5);

                                                if (shuffled.length > 0) {
                                                    playAlbumTrack(shuffled[0], 0, shuffled);
                                                    setIsShuffle(true);
                                                }
                                            }}
                                            className="flex items-center gap-3 group px-2 py-1 -ml-2 rounded-lg hover:bg-white/5 transition-colors"
                                        >
                                            <PlayIcon className="w-5 h-5 text-zinc-400 group-hover:text-white" />
                                            <span className="text-sm font-bold text-white">Artist Blend</span>
                                        </button>
                                    </div>

                                    {/* RIGHT: Artist Name */}
                                    <h2 className="text-3xl font-bold text-white tracking-tight text-right">{selectedArtist.name}</h2>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center gap-3 mb-4">
                                <button
                                    onClick={() => setViewMode('playlist')}
                                    className="p-2 hover:bg-white/5 rounded-full text-zinc-400 hover:text-white transition-colors"
                                >
                                    <CaretLeftIcon className="w-5 h-5" />
                                </button>
                                <span className="text-sm font-bold text-white">Add Music</span>
                            </div>
                        )}

                        {!selectedArtist && (
                            <div className="relative mb-4">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                                <input
                                    type="text"
                                    placeholder="Search song or artist..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    autoFocus
                                    className="w-full bg-zinc-900 border border-white/10 rounded-lg pl-9 pr-3 py-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/50 transition-all font-sans"
                                />
                            </div>
                        )}

                        {/* CSV Import Button - Hidden when searching or viewing discography */}
                        {!searchQuery.trim() && !selectedArtist && (
                            <div className="mb-4 flex items-center gap-3">
                                <input
                                    ref={csvFileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleCSVFileChange}
                                    className="hidden"
                                />
                                <button
                                    onClick={handleCSVImport}
                                    disabled={isImportingCSV}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-white/5 hover:border-white/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isImportingCSV ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                                            <span className="text-xs font-medium text-zinc-400">
                                                Importing {csvImportProgress.current}/{csvImportProgress.total}...
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <UploadIcon className="w-4 h-4 text-indigo-400/70" />
                                            <span className="text-xs font-medium text-zinc-400">Import from CSV</span>
                                        </>
                                    )}
                                </button>

                                {/* Info Tooltip */}
                                <div className="group relative">
                                    <div className="p-2 rounded-full hover:bg-white/5 cursor-help transition-colors">
                                        <InfoIcon className="w-4 h-4 text-zinc-500 group-hover:text-zinc-300" />
                                    </div>
                                    <div className="absolute right-0 bottom-full mb-2 w-64 p-3 bg-onyx border border-white/10 rounded-lg shadow-xl text-xs text-zinc-400 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[60]">
                                        <p className="font-bold text-zinc-200 mb-1">CSV Format Requirements</p>
                                        <p className="mb-2">Your CSV must include columns for:</p>
                                        <ul className="list-disc pl-4 space-y-1 text-[10px]">
                                            <li><span className="text-indigo-400">Track</span> (Title, Name, Song)</li>
                                            <li><span className="text-indigo-400">Artist</span> (Band, Performer)</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="space-y-1">

                            {/* Artist Albums (Discography View) */}
                            {selectedArtist && !isSearching && (
                                <div className="space-y-6 mb-4">
                                    {/* Top Songs Section (Always Visible) */}
                                    {artistTopSongs.length > 0 && (
                                        <div className="px-1 mb-6">
                                            <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3 pl-1">Popular</h3>
                                            <div className="space-y-1">
                                                {artistTopSongs.map((track, idx) => {
                                                    const isAdded = playlistTracks.some(pt => pt.title === track.title && pt.artist === track.artist);
                                                    const isPlayingThis = (playingTrack?.name === track.title || playingTrack?.title === track.title) && playingTrack?.artist === track.artist;

                                                    return (
                                                        <div
                                                            key={track.id}
                                                            onClick={() => playAlbumTrack(track, idx, artistTopSongs)}
                                                            className={`w-full flex items-center gap-4 p-3 rounded-xl transition-colors relative cursor-pointer group ${isPlayingThis ? 'bg-indigo-500/10 border border-indigo-500/20' : 'hover:bg-white/5 border border-transparent'}`}
                                                        >
                                                            <div className="w-12 h-12 rounded-lg bg-zinc-800 flex-shrink-0 relative overflow-hidden flex items-center justify-center shadow-sm">
                                                                <img src={track.cover || selectedArtist.image} className="w-full h-full object-cover" />
                                                                <div className={`absolute inset-0 bg-zinc-900/40 flex items-center justify-center transition-opacity ${isPlayingThis ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                                                                    {isPlayingThis && isPlaying ? <PauseIcon className="w-5 h-5 text-white" /> : <PlayIcon className="w-5 h-5 text-white" />}
                                                                </div>
                                                            </div>

                                                            <div className="min-w-0 flex-1">
                                                                <p className={`text-base font-medium truncate leading-tight mb-0.5 ${isPlayingThis ? 'text-indigo-400' : 'text-zinc-200 group-hover:text-white'}`}>{track.title}</p>
                                                                <p className="text-sm text-zinc-500 truncate">{track.duration}</p>
                                                            </div>

                                                            {/* Add to Queue Button */}
                                                            {permissions.canChangeTrack && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        addToQueue({
                                                                            id: track.id || crypto.randomUUID(),
                                                                            title: track.title,
                                                                            artist: track.artist,
                                                                            cover: track.cover || selectedArtist?.image
                                                                        });
                                                                    }}
                                                                    className="p-2 text-zinc-600 hover:text-indigo-400 hover:bg-white/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                                                                    title="Add to Queue"
                                                                >
                                                                    <QueueIcon className="w-5 h-5" />
                                                                </button>
                                                            )}

                                                            {isAdded ? (
                                                                <span className="text-[10px] text-emerald-500 px-1.5 py-0.5 bg-emerald-500/10 rounded">Added</span>
                                                            ) : (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        addToPlaylist({ ...track, id: crypto.randomUUID() });
                                                                    }}
                                                                    className="p-2 text-zinc-600 hover:text-indigo-400 hover:bg-white/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                                                                    title="Add to Soundtrack"
                                                                >
                                                                    <PlusIcon className="w-5 h-5" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Filters */}
                                    <div className="flex justify-end gap-2 px-1 mb-2">
                                        {[
                                            { id: 'popular', label: 'Popular' },
                                            { id: 'albums', label: 'Albums' },
                                            { id: 'singles', label: 'EP / Singles' }
                                        ].map(item => (
                                            <button
                                                key={item.id}
                                                onClick={() => setDiscographyFilter(item.id as any)}
                                                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all border ${discographyFilter === item.id
                                                    ? 'bg-white text-black border-white'
                                                    : 'bg-zinc-900 text-zinc-500 border-white/10 hover:border-white/30 hover:text-zinc-300'
                                                    }`}
                                            >
                                                {item.label}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Albums List */}
                                    <div className="space-y-3">
                                        {artistAlbums
                                            .filter(album => {
                                                const lowerName = album.name.toLowerCase();
                                                // Check if it's a live album
                                                const isLive = lowerName.includes('live at') || lowerName.includes('live from') ||
                                                    lowerName.includes('(live)') || lowerName.includes(' live ') ||
                                                    lowerName.match(/live[,\s]*\d{4}/) || lowerName.startsWith('live ');

                                                // Heuristic: Singles usually have fewer tracks (<= 4) OR explicit type
                                                const isSingle = album.trackCount <= 4 || lowerName.includes(' - single') || lowerName.includes(' - ep') || lowerName.endsWith(' ep');

                                                if (discographyFilter === 'popular') {
                                                    // Popular tab: exclude live versions
                                                    return !isLive;
                                                }
                                                // Albums/Singles tabs: include all (even live)
                                                return discographyFilter === 'singles' ? isSingle : !isSingle;
                                            })
                                            // Sort: Popular uses API order (popularity), Albums/Singles use release date (newest first)
                                            .sort((a, b) => {
                                                if (discographyFilter === 'popular') return 0; // Keep API popularity order
                                                // Sort by release date descending (most recent first)
                                                return new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime();
                                            })
                                            .slice(0, discographyFilter === 'popular' ? 5 : undefined)
                                            .map(album => (
                                                <div key={album.id} className="border border-white/5 rounded-xl overflow-hidden bg-zinc-900/50">
                                                    {/* Album Header */}
                                                    <button
                                                        onClick={() => setExpandedAlbum(expandedAlbum === album.id ? null : album.id)}
                                                        className="w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors"
                                                    >
                                                        <div className="w-14 h-14 rounded-lg bg-zinc-800 overflow-hidden flex-shrink-0 shadow-lg">
                                                            {album.cover ? (
                                                                <img src={album.cover} className="w-full h-full object-cover" />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center">
                                                                    <MusicNoteIcon className="w-6 h-6 text-zinc-600" />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex-1 min-w-0 text-left">
                                                            <p className="text-sm font-bold text-white truncate">{album.name}</p>
                                                            <p className="text-xs text-zinc-500">{album.trackCount} tracks · {new Date(album.releaseDate).getFullYear()}</p>
                                                        </div>
                                                        <CaretDownIcon className={`w-4 h-4 text-zinc-500 transition-transform ${expandedAlbum === album.id ? 'rotate-180' : ''}`} />
                                                    </button>

                                                    {/* Album Tracks (Expanded) */}
                                                    {expandedAlbum === album.id && (
                                                        <div className="border-t border-white/5 bg-zinc-900/20">
                                                            {album.tracks.map((track: any, idx: number) => {
                                                                const isAdded = playlistTracks.some(pt =>
                                                                    pt.title.toLowerCase() === track.title.toLowerCase() &&
                                                                    pt.artist.toLowerCase() === track.artist.toLowerCase()
                                                                );
                                                                const isPlayingThis = playingTrack?.name === track.title && playingTrack?.artist === track.artist;

                                                                return (
                                                                    <div
                                                                        key={track.id}
                                                                        onClick={() => !isAdded && playAlbumTrack(track, idx, album.tracks)}
                                                                        className={`flex items-center gap-3 px-3 py-2 ${isAdded ? 'opacity-50 cursor-default' : 'hover:bg-white/5 cursor-pointer'} ${isPlayingThis ? 'bg-white/5' : ''}`}
                                                                    >
                                                                        <span className="w-6 flex justify-end">
                                                                            {isPlayingThis ? (
                                                                                <SpeakerHighIcon className={`w-3.5 h-3.5 text-indigo-500 ${isPlaying ? 'animate-pulse' : ''}`} />
                                                                            ) : (
                                                                                <span className="text-xs text-zinc-600">{track.trackNumber || idx + 1}</span>
                                                                            )}
                                                                        </span>
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className={`text-sm truncate ${isPlayingThis ? 'text-indigo-400 font-medium' : 'text-zinc-200'}`}>{track.title}</p>
                                                                        </div>
                                                                        {track.duration && (
                                                                            <span className="text-xs font-mono text-zinc-600">{track.duration}</span>
                                                                        )}
                                                                        {/* Add to Queue Button */}
                                                                        {permissions.canChangeTrack && (
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    addToQueue({
                                                                                        id: track.id || crypto.randomUUID(),
                                                                                        title: track.title,
                                                                                        artist: track.artist,
                                                                                        cover: track.cover || album.cover
                                                                                    });
                                                                                }}
                                                                                className="p-1 text-zinc-500 hover:text-indigo-400"
                                                                                title="Add to Queue"
                                                                            >
                                                                                <QueueIcon className="w-3.5 h-3.5" />
                                                                            </button>
                                                                        )}

                                                                        {isAdded ? (
                                                                            <span className="text-[10px] text-emerald-500 px-1.5 py-0.5 bg-emerald-500/10 rounded">Added</span>
                                                                        ) : (
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    addToPlaylist({
                                                                                        id: crypto.randomUUID(),
                                                                                        title: track.title,
                                                                                        artist: track.artist,
                                                                                        artistId: track.artistId || selectedArtist?.id, // Use track's artistId or current artist page
                                                                                        cover: track.cover || album.cover,
                                                                                        duration: track.duration,
                                                                                        genre: track.genre
                                                                                    });
                                                                                }}
                                                                                className="p-1 text-zinc-500 hover:text-indigo-400"
                                                                                title="Add to Soundtrack"
                                                                            >
                                                                                <PlusIcon className="w-3.5 h-3.5" />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}

                            {/* Artist Suggestions */}
                            {searchArtists.length > 0 && !selectedArtist && (
                                <div className="mb-4 pb-4 border-b border-white/5">
                                    <p className="text-xs font-bold uppercase text-zinc-500 tracking-wider mb-2 px-1">Artists</p>
                                    <div className="flex gap-2 overflow-x-auto pb-1">
                                        {searchArtists.map(artist => (
                                            <button
                                                key={artist.id}
                                                onClick={() => fetchArtistDiscography(artist.id, artist.name, artist.image)}
                                                className="flex items-center gap-2 px-3 py-2 bg-zinc-800/50 hover:bg-zinc-700/50 rounded-lg border border-white/5 hover:border-white/10 transition-all flex-shrink-0 group"
                                            >
                                                <span className="text-sm font-medium text-zinc-300 group-hover:text-white truncate max-w-[140px]">{artist.name}</span>
                                                <CaretRightIcon className="w-4 h-4 text-zinc-500 group-hover:text-zinc-300" />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Loading State */}
                            {isSearching && (
                                <div className="py-8 flex justify-center">
                                    <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                                </div>
                            )}

                            {!selectedArtist && searchResults.map((track, index) => {
                                const isCurrent = isPlaying && playingTrack?.name === track.title && playingTrack?.artist === track.artist && activeContext === 'search';

                                return (
                                    <div
                                        key={track.id}
                                        onClick={() => !track.isAdded && playSearchTrack(track, index)}
                                        className={`w-full flex items-center gap-4 p-3 rounded-xl transition-colors relative ${track.isAdded ? 'opacity-50 cursor-default bg-white/5' : 'hover:bg-white/5 cursor-pointer group'} ${isCurrent ? 'bg-indigo-500/10 border border-indigo-500/20' : 'border border-transparent'}`}
                                    >
                                        <div className="flex items-center gap-4 flex-1 min-w-0 text-left">
                                            <div className="w-12 h-12 rounded-lg bg-zinc-800 flex-shrink-0 relative overflow-hidden flex items-center justify-center group-hover:bg-zinc-700 shadow-sm transition-all">
                                                {track.cover ? (
                                                    <img
                                                        src={track.cover}
                                                        className="w-full h-full object-cover relative z-10"
                                                        onError={(e) => e.currentTarget.style.display = 'none'}
                                                    />
                                                ) : (
                                                    <MusicNoteIcon className="w-5 h-5 text-zinc-600" />
                                                )}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className={`text-base font-medium truncate leading-tight mb-0.5 ${isCurrent ? 'text-indigo-400' : 'text-zinc-200'} ${!track.isAdded && 'group-hover:text-white'}`}>{track.title}</p>
                                                <button
                                                    onClick={(e) => handleArtistClick(e, track.artist, track.artistId, track.title)}
                                                    className="text-sm text-zinc-500 truncate transition-colors hover:text-white hover:underline block max-w-full text-left"
                                                >
                                                    {track.artist}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Duration */}
                                        {track.duration && (
                                            <span className="text-xs font-mono text-zinc-600 hidden sm:block">{track.duration}</span>
                                        )}

                                        {/* Add to Queue Button */}
                                        {permissions.canChangeTrack && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    addToQueue({
                                                        id: track.id,
                                                        title: track.title,
                                                        artist: track.artist,
                                                        cover: track.cover
                                                    });
                                                }}
                                                className="p-2 text-zinc-600 hover:text-indigo-400 hover:bg-white/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                                                title="Add to Queue"
                                            >
                                                <QueueIcon className="w-5 h-5" />
                                            </button>
                                        )}

                                        {/* Add Button or Added Label */}
                                        {track.isAdded ? (
                                            <span className="text-[10px] text-emerald-500 px-1.5 py-0.5 bg-emerald-500/10 rounded">Added</span>
                                        ) : (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    addToPlaylist(track);
                                                }}
                                                className="p-2 text-zinc-600 hover:text-indigo-400 hover:bg-white/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                                                title="Add to Soundtrack"
                                            >
                                                <PlusIcon className="w-5 h-5" />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                            {!selectedArtist && searchResults.length === 0 && searchQuery && !isSearching && (
                                <div className="text-center py-8">
                                    <p className="text-xs text-zinc-500">No results found.</p>
                                </div>
                            )}
                            {!selectedArtist && searchResults.length === 0 && !searchQuery && (
                                <div className="text-center py-12 text-zinc-700">
                                    <MagnifyingGlassIcon className="w-8 h-8 mx-auto mb-2 opacity-20" />
                                    <p className="text-xs">Type to search...</p>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div >
    );




    return (
        <>
            {/* Closed Pop-out Miniplayer Slot (Draggable & Resizable) - MOVED OUTSIDE SIDEBAR TRANSFORMS */}
            {/* Constraint Container: Covers valid area. Full screen since sidebar is minimized. */}
            <div ref={constraintsRef} className="fixed inset-4 pointer-events-none z-0" />

            <AnimatePresence>
                {!isOpen && playingTrack && (
                    <motion.div
                        drag
                        dragControls={dragControls}
                        dragListener={false} // Disable default drag listener, use specific handle
                        dragConstraints={constraintsRef}
                        dragElastic={0}
                        dragMomentum={false}
                        dragTransition={{ bounceStiffness: 300, bounceDamping: 20 }}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                        className="fixed top-20 right-20 z-[5000] rounded-xl shadow-2xl border border-white/20 group pointer-events-auto flex flex-col overflow-hidden bg-onyx"
                        style={{ width: popoutWidth, minWidth: '320px' }}
                    >
                        {/* Title Bar / Drag Handle (Window-like) */}
                        <div
                            onPointerDown={(e) => dragControls.start(e)}
                            className="h-8 w-full bg-zinc-900 border-b border-white/10 flex items-center justify-between px-3 cursor-grab active:cursor-grabbing z-50 shrink-0 select-none"
                        >
                            {/* Decorative Window Controls (Mac-like) */}
                            <div className="flex gap-1.5 opacity-50">
                                <div className="w-2.5 h-2.5 rounded-full bg-white/20" />
                                <div className="w-2.5 h-2.5 rounded-full bg-white/20" />
                            </div>

                            {/* Centered Drag Handle Indicator */}
                            <div className="flex-1 flex justify-center">
                                <div className="w-12 h-1 bg-white/10 rounded-full group-hover:bg-white/20 transition-colors" />
                            </div>

                            {/* Close Button */}
                            <button
                                onClick={(e) => { e.stopPropagation(); handleClosePlayer(e); }}
                                onPointerDown={(e) => e.stopPropagation()} // Prevent drag
                                className="text-zinc-500 hover:text-red-400 transition-colors p-1"
                                title="Close Player"
                            >
                                <XIcon className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Video Slot Container */}
                        <div
                            className="relative w-full z-0 bg-black"
                            style={{ aspectRatio: videoAspectRatio, transition: 'aspect-ratio 0.3s ease-in-out' }}
                        >
                            <div ref={closedSlotRef} className="absolute inset-0" />
                        </div>

                        {/* Resize Handle (Bottom Right) */}
                        <motion.div
                            drag="x"
                            dragMomentum={false}
                            dragElastic={0}
                            dragConstraints={{ left: 0, right: 0 }} // We monitor delta, not position
                            onDrag={(e, info) => {
                                setPopoutWidth(prev => Math.max(320, Math.min(800, prev + info.delta.x)));
                            }}
                            className="absolute bottom-0 right-0 w-6 h-6 z-50 cursor-nwse-resize flex items-center justify-center hover:bg-white/10 rounded-br-xl rounded-tl-lg transition-colors pointer-events-auto"
                        >
                            <svg width="6" height="6" viewBox="0 0 6 6" fill="none" xmlns="http://www.w3.org/2000/svg" className="opacity-50">
                                <path d="M6 6L0 6L6 0L6 6Z" fill="white" />
                            </svg>
                        </motion.div>

                    </motion.div>
                )}
            </AnimatePresence>

            {/* Edge hover zone - reveals toggle handle when collapsed */}
            {!isOpen && isActive && (
                <div
                    className={`fixed top-0 h-screen w-6 z-40 hidden xl:block ${side === 'left' ? 'left-0' : 'right-0'}`}
                    onMouseEnter={() => setIsHandleVisible(true)}
                    onMouseLeave={() => setIsHandleVisible(false)}
                />
            )}

            {/* Main Creative Sidebar UI - Only visible when Active */}
            <div
                className={`fixed top-0 bottom-0 w-[640px] bg-onyx border-r border-white/5 shadow-2xl z-40 hidden ${isActive ? 'xl:flex' : ''} flex-col transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] will-change-transform ${activeTab === 'soundtrack' && playerMode === 'video' ? 'bg-zinc-900' : ''
                    } ${isActive ? (isOpen ? 'translate-x-0' : (side === 'left' ? '-translate-x-full' : 'translate-x-[100%]')) : 'hidden'}
            ${side === 'left' ? 'left-0 border-r border-white/5' : 'right-0 border-l border-white/5 border-r-0'}
            `}
            >

                {/* Closed Pop-out Miniplayer Slot (Draggable & Resizable) */}


                {/* Content Container */}
                <div className={`flex flex-col h-full w-[640px] transition-opacity duration-200 ${isOpen ? 'opacity-100 delay-100' : 'opacity-0 pointer-events-none'} relative z-10`}>

                    {/* Header & Tabs */}
                    <div className="flex flex-col border-b border-white/5 bg-onyx relative z-[2000]">
                        {/* Top Row: Tabs + Swap Button */}
                        <div className="flex items-center justify-between px-4 pt-4">
                            <div className="flex items-center gap-6">
                                {[
                                    { id: 'assets', label: 'Assets', icon: FolderIcon },
                                    { id: 'chat', label: 'Chat', icon: ChatCircleIcon },
                                    { id: 'soundtrack', label: 'Temp', icon: MusicNoteIcon },
                                    // Users tab only visible to project owner
                                    ...(currentUserRole === 'owner' ? [{ id: 'users', label: 'Users', icon: UsersIcon }] : [])
                                ].map(tab => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id as any)}
                                        className={`
                                        flex items-center gap-2 pb-3 px-2 text-xs font-bold uppercase tracking-widest border-b-2 transition-all outline-none focus:outline-none focus-visible:outline-none focus:ring-0
                                        ${activeTab === tab.id ? 'text-white border-indigo-500' : 'text-zinc-500 border-transparent hover:text-zinc-300'}
                                    `}
                                    >
                                        <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? 'text-indigo-400' : 'text-zinc-600'}`} />
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            {/* Swap Side Button */}
                            {onSwapSides && side === 'right' && (
                                <button
                                    onClick={onSwapSides}
                                    className="p-2 text-zinc-600 hover:text-white hover:bg-white/5 rounded-full transition-colors"
                                    title="Swap Sidebar Position"
                                >
                                    <ArrowsLeftRightIcon className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                    </div>

                    {/* Main Content Area */}
                    <div className="flex-1 overflow-hidden relative bg-onyx">

                        {/* CHAT TAB */}
                        <div className={`absolute inset-0 flex flex-col bg-onyx transition-opacity duration-300 ${activeTab === 'chat' ? 'z-20 opacity-100 pointer-events-auto' : 'z-0 opacity-0 pointer-events-none'}`}>
                            {selectedProject ? (
                                <ChatPanel projectId={selectedProject} className={playingTrack ? 'pb-32' : ''} />
                            ) : (
                                <div className="flex-1 flex flex-col items-center justify-center text-zinc-500 p-4">
                                    <ChatCircleIcon className="w-12 h-12 mb-3 text-zinc-700" />
                                    <p className="text-sm font-medium">Select a project</p>
                                    <p className="text-xs text-zinc-600 mt-1">Chat with collaborators in your project</p>
                                </div>
                            )}
                        </div>

                        {/* USERS TAB (Owner Only) */}
                        {currentUserRole === 'owner' && (
                            <div className={`absolute inset-0 flex flex-col bg-onyx transition-opacity duration-300 ${activeTab === 'users' ? 'z-20 opacity-100 pointer-events-auto' : 'z-0 opacity-0 pointer-events-none'}`}>
                                {selectedProject ? (
                                    <UsersPanel projectId={selectedProject} className={playingTrack ? 'pb-32' : ''} />
                                ) : (
                                    <div className="flex-1 flex flex-col items-center justify-center text-zinc-500 p-4">
                                        <UsersIcon className="w-12 h-12 mb-3 text-zinc-700" />
                                        <p className="text-sm font-medium">Select a project</p>
                                        <p className="text-xs text-zinc-600 mt-1">Manage collaborators and permissions</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ASSETS TAB */}
                        {/* ASSETS TAB */}
                        <div className={`absolute inset-0 flex flex-col bg-onyx transition-opacity duration-300 ${activeTab === 'assets' ? 'z-20 opacity-100 pointer-events-auto' : 'z-0 opacity-0 pointer-events-none'}`}>
                            {/* Asset Grid or Full Preview */}
                            <div className={`flex-1 overflow-y-auto p-4 custom-scrollbar relative ${playingTrack ? 'pb-32' : ''}`}>
                                {selectedAsset ? (
                                    <div className={`absolute inset-x-0 top-0 ${playingTrack ? 'bottom-32' : 'bottom-0'} bg-zinc-900 z-20 flex flex-col`}>
                                        <div className="flex items-center justify-between p-4 border-b border-white/10 bg-zinc-900/50 backdrop-blur">
                                            <span className="text-xs font-bold truncate text-white max-w-[200px]">{selectedAsset.label || 'Asset Preview'}</span>
                                            <button
                                                onClick={() => setSelectedAsset(null)}
                                                className="p-1.5 hover:bg-white/10 rounded-full text-zinc-400 hover:text-white transition-colors"
                                            >
                                                <XIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div className="flex-1 flex items-center justify-center p-4 bg-onyx">
                                            <img
                                                src={selectedAsset.image_url}
                                                alt={selectedAsset.label}
                                                className="max-w-full max-h-full object-contain shadow-2xl rounded-lg"
                                            />
                                        </div>
                                        <div className="p-4 border-t border-white/10 bg-zinc-900">
                                            <div className="grid grid-cols-2 gap-4 text-[10px] text-zinc-500">
                                                <div>
                                                    <span className="block uppercase font-bold text-zinc-600 mb-1">Prompt</span>
                                                    <p className="text-zinc-400 line-clamp-3 leading-relaxed">{selectedAsset.description}</p>
                                                </div>
                                                <div>
                                                    <span className="block uppercase font-bold text-zinc-600 mb-1">Details</span>
                                                    <p>Type: {selectedAsset.metadata?.tags?.[0] || 'Generated'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    selectedProject ? (
                                        filteredAssets.length > 0 ? (
                                            <Masonry
                                                breakpointCols={{ default: 2, 500: 1 }}
                                                className="flex w-auto -ml-2 p-1"
                                                columnClassName="pl-2 bg-clip-padding"
                                            >
                                                {/* Mood Generation Loading Placeholder */}
                                                {isGeneratingMood && (
                                                    <div className="mb-2 group relative aspect-square bg-gradient-to-br from-violet-500/20 to-indigo-500/20 rounded-lg overflow-hidden border-2 border-violet-500/50 animate-pulse">
                                                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                                                            <div className="w-8 h-8 border-3 border-violet-400 border-t-transparent rounded-full animate-spin" />
                                                            <span className="text-[10px] font-bold text-violet-300 uppercase tracking-wider">Creating Mood...</span>
                                                        </div>
                                                    </div>
                                                )}
                                                {filteredAssets.map((asset, index) => (
                                                    <div
                                                        key={asset.id}
                                                        className="mb-2 group relative bg-zinc-900 rounded-lg overflow-hidden border border-white/5 cursor-pointer hover:border-indigo-500/50 transition-all"
                                                        onClick={() => setSelectedAsset(asset)}
                                                    >
                                                        <img src={asset.image_url} className="w-full h-auto block" loading="lazy" />

                                                        {/* Action buttons - matching gallery design */}
                                                        <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10 p-1 rounded-lg bg-black/40 backdrop-blur-xl border border-white/10 shadow-lg">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setFullscreenAssetIndex(index);
                                                                }}
                                                                className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-all"
                                                                title="View fullscreen"
                                                            >
                                                                <ArrowsOutIcon className="w-3.5 h-3.5" />
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (confirm('Remove this asset from the project?')) {
                                                                        ProjectService.deleteShotById(asset.id).then(() => {
                                                                            setAssets(prev => prev.filter(a => a.id !== asset.id));
                                                                        });
                                                                    }
                                                                }}
                                                                className="p-1.5 rounded-md text-white/70 hover:text-red-400 hover:bg-red-400/10 transition-all"
                                                                title="Remove from project"
                                                            >
                                                                <TrashIcon className="w-3.5 h-3.5" />
                                                            </button>
                                                        </div>

                                                        {/* Bottom info overlay */}
                                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-end p-2 pointer-events-none">
                                                            <p className="text-[10px] text-white font-medium line-clamp-2">{asset.label || asset.description}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </Masonry>
                                        ) : (
                                            <div className="h-full flex flex-col items-center justify-center text-zinc-600 space-y-2">
                                                <FolderIcon className="w-8 h-8 opacity-20" />
                                                <p className="text-xs">No assets in this project yet.</p>
                                            </div>
                                        )
                                    ) : (
                                        <div className="h-full flex flex-col items-center justify-center text-zinc-600 space-y-2">
                                            <FolderIcon className="w-8 h-8 opacity-20" />
                                            <p className="text-xs">Select a project to view assets.</p>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>

                        {/* SOUNDTRACK TAB */}
                        <div className={`absolute inset-0 flex flex-col bg-onyx transition-opacity duration-300 ${activeTab === 'soundtrack' ? 'z-20 opacity-100 pointer-events-auto' : 'z-0 opacity-0 pointer-events-none'}`}>
                            {selectedProject ? (
                                <div className="h-full flex flex-col relative w-full">
                                    {playingTrack ? (
                                        <>
                                            {/* --- VIDEO MODE LAYOUT --- */}
                                            {playerMode === 'video' && (
                                                <div className="flex flex-col h-full w-full">
                                                    {/* Fixed Video Header */}
                                                    <div className="shrink-0 bg-zinc-900 z-50 w-full relative">
                                                        <motion.div
                                                            initial={false}
                                                            animate={{ aspectRatio: videoAspectRatio }}
                                                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                                                            className="w-full bg-zinc-900 relative group"
                                                        >
                                                            {/* Video Slot */}
                                                            <div ref={mainSlotRef} className="absolute inset-0" />

                                                            <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/60 to-transparent pointer-events-none z-[105]" />
                                                            <div className="absolute inset-x-0 bottom-12 h-20 bg-gradient-to-t from-black/80 to-transparent pointer-events-none z-[105]" />

                                                            {/* Show cover image with spinner until video player is ready */}
                                                            {(!player || loadingVideo || !playingTrack.videoId) && (
                                                                <div className="absolute inset-0 z-[110]">
                                                                    <img
                                                                        src={playingTrack.image}
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                                                        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </motion.div>

                                                        {/* Video Mode Standard Controls (Fixed below video) */}
                                                        <div className="bg-onyx p-4 flex items-center gap-4 border-b border-white/5">
                                                            <div
                                                                onClick={togglePlayerMode}
                                                                className="w-20 aspect-square rounded-lg overflow-hidden relative group shrink-0 cursor-pointer hover:ring-2 hover:ring-indigo-500 transition-all border border-white/10"
                                                                title="Switch to Cover Mode"
                                                            >
                                                                <img src={playingTrack.image} className="w-full h-full object-cover" />
                                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                                                    <ImageIcon className="w-5 h-5 text-white" />
                                                                </div>
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <p className="text-base font-medium text-white truncate leading-tight mb-0.5">{playingTrack.name}</p>
                                                                <button onClick={(e) => handleArtistClick(e, playingTrack.artist, playingTrack.artistId, playingTrack.name)} className="text-sm text-zinc-500 truncate hover:text-white hover:underline block max-w-full text-left">{playingTrack.artist}</button>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                {/* Add to Playlist (Only if not already in playlist) */}
                                                                {!playlistTracks.some(t => t.title === playingTrack.name && t.artist === playingTrack.artist) && (
                                                                    <button
                                                                        onClick={() => {
                                                                            addToPlaylist({
                                                                                id: crypto.randomUUID(),
                                                                                title: playingTrack.name,
                                                                                artist: playingTrack.artist,
                                                                                artistId: playingTrack.artistId,
                                                                                cover: playingTrack.image,
                                                                                duration: (playingTrack as any).duration || null,
                                                                                genre: (playingTrack as any).genre
                                                                            });
                                                                        }}
                                                                        className="p-2 text-zinc-400 hover:text-indigo-400 bg-white/5 rounded-full hover:bg-white/10"
                                                                        title="Add to Playlist"
                                                                    >
                                                                        <PlusIcon className="w-4 h-4" />
                                                                    </button>
                                                                )}
                                                                {(() => { const ctrlOff = isListeningAlong || !isPlaybackController; const ctrlTitle = isListeningAlong ? "Leave Listen Along to control playback" : "Only the person playing controls playback"; return (<>
                                                                <button
                                                                    onClick={() => { if (!ctrlOff) playPrev(); }}
                                                                    disabled={ctrlOff}
                                                                    className={`p-2 ${ctrlOff ? 'text-zinc-600 cursor-not-allowed' : 'text-zinc-400 hover:text-white'}`}
                                                                    title={ctrlOff ? ctrlTitle : "Previous"}
                                                                >
                                                                    <SkipBackIcon className="w-5 h-5" />
                                                                </button>
                                                                <button
                                                                    onClick={ctrlOff ? undefined : togglePlay}
                                                                    disabled={ctrlOff}
                                                                    className={`w-10 h-10 flex items-center justify-center rounded-full shadow-lg ${ctrlOff ? 'bg-zinc-700 text-zinc-500 cursor-not-allowed' : 'bg-indigo-500 text-white'}`}
                                                                    title={ctrlOff ? ctrlTitle : isPlaying ? "Pause" : "Play"}
                                                                >
                                                                    {isPlaying ? <PauseIcon className="w-5 h-5" fill="currentColor" strokeWidth={0} /> : <PlayIcon className="w-5 h-5" fill="currentColor" strokeWidth={0} />}
                                                                </button>
                                                                <button
                                                                    onClick={() => { if (!ctrlOff) playNext(); }}
                                                                    disabled={ctrlOff}
                                                                    className={`p-2 ${ctrlOff ? 'text-zinc-600 cursor-not-allowed' : 'text-zinc-400 hover:text-white'}`}
                                                                    title={ctrlOff ? ctrlTitle : "Next"}
                                                                >
                                                                    <SkipForwardIcon className="w-5 h-5" />
                                                                </button>
                                                                </>); })()}
                                                                <button
                                                                    onClick={() => {
                                                                        if (isListeningAlong) return;
                                                                        if (!isShuffle && !repeatOne) {
                                                                            // Off -> Shuffle
                                                                            toggleShuffle();
                                                                        } else if (isShuffle) {
                                                                            // Shuffle -> Repeat
                                                                            toggleShuffle(); // Toggle shuffle off
                                                                            if (!repeatOne) toggleRepeat(); // Toggle repeat on
                                                                        } else {
                                                                            // Repeat -> Off
                                                                            if (repeatOne) toggleRepeat(); // Toggle repeat off
                                                                        }
                                                                    }}
                                                                    disabled={isListeningAlong}
                                                                    className={`p-2 ${isListeningAlong ? 'text-zinc-600 cursor-not-allowed' : isShuffle || repeatOne ? 'text-green-400' : 'text-zinc-400'}`}
                                                                    title={isListeningAlong ? "Leave Listen Along to control playback" : isShuffle ? "Shuffle On (Click for Repeat)" : repeatOne ? "Repeat On (Click for Off)" : "Click to Cycle Mode"}
                                                                >
                                                                    {repeatOne ? <RepeatIcon className="w-4 h-4" /> : <ShuffleIcon className="w-4 h-4" />}
                                                                </button>

                                                                {/* Discord Rich Presence Button */}
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        console.log('[Discord] Button clicked! connected:', discord.connected, 'connecting:', discord.connecting);
                                                                        if (discord.connected) {
                                                                            discord.disconnect();
                                                                        } else {
                                                                            // Try to open the companion app via protocol
                                                                            console.log('[Discord] Attempting to open companion app via protocol...');
                                                                            const link = document.createElement('a');
                                                                            link.href = 'ezart-discord://open';
                                                                            link.style.display = 'none';
                                                                            document.body.appendChild(link);
                                                                            link.click();
                                                                            document.body.removeChild(link);

                                                                            // Wait for app to launch, then retry WebSocket connection
                                                                            setTimeout(() => {
                                                                                console.log('[Discord] Retrying WebSocket connection after protocol...');
                                                                                discord.connect();

                                                                                // Check again after giving WebSocket time to connect
                                                                                setTimeout(() => {
                                                                                    console.log('[Discord] Final check. connected:', discordConnectedRef.current);
                                                                                    if (!discordConnectedRef.current) {
                                                                                        if (player && isPlaying) {
                                                                                            try { player.pauseVideo(); } catch (e) { }
                                                                                        }
                                                                                        setShowDiscordModal(true);
                                                                                    }
                                                                                }, 2000);
                                                                            }, 1500);
                                                                        }
                                                                    }}
                                                                    className={`p-2 rounded-full relative ${discord.connected
                                                                        ? 'text-[#5865F2] bg-[#5865F2]/10'
                                                                        : discord.connecting
                                                                            ? 'text-zinc-400 animate-pulse'
                                                                            : 'text-zinc-500 hover:text-[#5865F2] hover:bg-white/5'
                                                                        }`}
                                                                    title={
                                                                        discord.connected
                                                                            ? 'Discord Connected - Click to disconnect'
                                                                            : discord.connecting
                                                                                ? 'Connecting to Discord...'
                                                                                : discord.error
                                                                                    ? `Discord: ${discord.error} - Click to download companion app`
                                                                                    : 'Connect Discord Rich Presence'
                                                                    }
                                                                >
                                                                    <DiscordIcon className="w-4 h-4" size={16} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Scrollable Content for Video Mode */}
                                                    <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-6 pb-32">
                                                        {playlistContent}
                                                    </div>
                                                </div>
                                            )}

                                            {/* --- COVER MODE LAYOUT --- */}
                                            {playerMode === 'cover' && (
                                                <div className="h-full w-full overflow-y-auto custom-scrollbar relative">
                                                    {/* 1. Large Cover Art (Scrolls Normal flow) - Click to play/pause */}
                                                    {/* 1. Large Cover Art (Scrolls Normal flow) - Click to play/pause */}
                                                    <div
                                                        className={`w-full aspect-square relative z-0 group/cover ${(isListeningAlong || !isPlaybackController) ? 'cursor-default' : 'cursor-pointer'}`}
                                                        onClick={(isListeningAlong || !isPlaybackController) ? undefined : togglePlay}
                                                    >
                                                        <img
                                                            src={playingTrack.image?.replace('600x600', '1200x1200') || playingTrack.image}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </div>

                                                    {/* Custom Progress Bar - Between image and controls */}
                                                    <div className="relative z-[400] group/progress px-0">
                                                        {/* Time Labels - Only on Hover */}
                                                        <div className="absolute -top-6 left-0 right-0 px-3 flex justify-between text-[10px] font-mono text-white opacity-0 group-hover/progress:opacity-100 transition-opacity drop-shadow-lg tabular-nums pointer-events-none">
                                                            <span className="bg-zinc-900/60 px-1.5 py-0.5 rounded">{formatTime(currentTime)}</span>
                                                            <span className="bg-zinc-900/60 px-1.5 py-0.5 rounded">{formatTime(duration)}</span>
                                                        </div>
                                                        {/* Progress Track */}
                                                        <div className="h-1.5 bg-white/10 w-full relative overflow-visible">
                                                            {/* Filled Progress */}
                                                            <div
                                                                className="h-full bg-gradient-to-r from-indigo-600 via-indigo-500 to-violet-500 relative transition-all duration-75 ease-linear shadow-[0_0_12px_rgba(99,102,241,0.6)]"
                                                                style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                                                            >
                                                                {/* Thumb/Knob - Always visible on hover, positioned above controls bar */}
                                                                <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-2.5 h-2.5 bg-white rounded-full shadow-[0_0_10px_rgba(255,255,255,0.5)] opacity-0 group-hover/progress:opacity-100 group-hover/progress:scale-125 transition-all z-[450]" />
                                                            </div>
                                                        </div>
                                                        {/* Invisible Input for Seeking - disabled during Listen Along or when not controller */}
                                                        {!isListeningAlong && isPlaybackController && (
                                                            <input
                                                                type="range"
                                                                min={0}
                                                                max={duration}
                                                                step="any"
                                                                value={currentTime}
                                                                onMouseDown={() => setIsDraggingProgress(true)}
                                                                onTouchStart={() => setIsDraggingProgress(true)}
                                                                onChange={handleSeek}
                                                                onMouseUp={handleSeekEnd}
                                                                onTouchEnd={handleSeekEnd}
                                                                className="absolute inset-0 w-full h-8 opacity-0 cursor-pointer z-[301] -top-3"
                                                            />
                                                        )}
                                                    </div>

                                                    {/* 2. Sticky Controls Bar */}
                                                    <div className="sticky top-0 z-[350] bg-onyx/95 backdrop-blur-xl border-b border-white/10 shadow-2xl">
                                                        <div className="p-4 flex items-center gap-4">
                                                            {/* Small Player Slot for Cover Mode - CLICK TO SWITCH TO VIDEO MODE */}
                                                            <div
                                                                onClick={togglePlayerMode}
                                                                className="w-36 aspect-video rounded-lg overflow-hidden bg-zinc-900 relative shrink-0 border border-white/10 shadow-lg cursor-pointer hover:border-indigo-500/50 transition-colors group/video-switch"
                                                                title="Switch to Video Mode"
                                                            >
                                                                {playingTrack.videoId ? (
                                                                    <div ref={controlsSlotRef} className="absolute inset-0 pointer-events-none" />
                                                                ) : (
                                                                    <img src={playingTrack.image} className="w-full h-full object-cover opacity-50" />
                                                                )}
                                                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/video-switch:opacity-100 bg-black/40 transition-opacity">
                                                                    <MonitorIcon className="w-6 h-6 text-white" />
                                                                </div>
                                                            </div>

                                                            <div className="min-w-0 flex-1">
                                                                <p className="text-base font-medium text-white truncate leading-tight mb-0.5">{playingTrack.name}</p>
                                                                <button onClick={(e) => handleArtistClick(e, playingTrack.artist, playingTrack.artistId, playingTrack.name)} className="text-sm text-zinc-500 truncate hover:text-white hover:underline block max-w-full text-left">{playingTrack.artist}</button>
                                                            </div>

                                                            <div className="flex items-center gap-2">
                                                                {/* Add to Playlist (Only if not already in playlist) */}
                                                                {!playlistTracks.some(t => t.title === playingTrack.name && t.artist === playingTrack.artist) && (
                                                                    <button
                                                                        onClick={() => {
                                                                            // Prioritize metadata from the track object, fallback to player
                                                                            let trackDuration = (playingTrack as any).duration;

                                                                            if (!trackDuration || trackDuration === '0:00') {
                                                                                try {
                                                                                    const playerDur = player?.getDuration?.();
                                                                                    if (playerDur && playerDur > 0) {
                                                                                        trackDuration = formatTime(playerDur);
                                                                                    } else if (duration > 0) {
                                                                                        trackDuration = formatTime(duration);
                                                                                    }
                                                                                } catch (e) {
                                                                                    if (duration > 0) trackDuration = formatTime(duration);
                                                                                }
                                                                            }

                                                                            addToPlaylist({
                                                                                id: crypto.randomUUID(),
                                                                                title: playingTrack.name,
                                                                                artist: playingTrack.artist,
                                                                                artistId: playingTrack.artistId,
                                                                                cover: playingTrack.image,
                                                                                duration: trackDuration,
                                                                                genre: (playingTrack as any).genre,
                                                                                preview: (playingTrack as any).preview
                                                                            });
                                                                        }}
                                                                        className="p-2 text-zinc-400 hover:text-indigo-400 bg-white/5 rounded-full hover:bg-white/10"
                                                                        title="Add to Playlist"
                                                                    >
                                                                        <PlusIcon className="w-4 h-4" />
                                                                    </button>
                                                                )}

                                                                <button
                                                                    onClick={playPrev}
                                                                    disabled={isListeningAlong}
                                                                    className={`p-2 ${isListeningAlong ? 'text-zinc-600 cursor-not-allowed' : 'text-zinc-400 hover:text-white'}`}
                                                                    title={isListeningAlong ? "Leave Listen Along to control playback" : "Previous"}
                                                                >
                                                                    <SkipBackIcon className="w-5 h-5" />
                                                                </button>
                                                                <button
                                                                    onClick={togglePlay}
                                                                    disabled={isListeningAlong}
                                                                    className={`w-10 h-10 flex items-center justify-center rounded-full shadow-lg hover:scale-105 transition-transform active:scale-95 ${isListeningAlong ? 'bg-zinc-700 text-zinc-500 cursor-not-allowed' : 'bg-indigo-500 text-white'}`}
                                                                    title={isListeningAlong ? "Leave Listen Along to control playback" : isPlaying ? "Pause" : "Play"}
                                                                >
                                                                    {isPlaying ? <PauseIcon className="w-5 h-5" fill="currentColor" strokeWidth={0} /> : <PlayIcon className="w-5 h-5" fill="currentColor" strokeWidth={0} />}
                                                                </button>
                                                                <button
                                                                    onClick={playNext}
                                                                    disabled={isListeningAlong}
                                                                    className={`p-2 ${isListeningAlong ? 'text-zinc-600 cursor-not-allowed' : 'text-zinc-400 hover:text-white'}`}
                                                                    title={isListeningAlong ? "Leave Listen Along to control playback" : "Next"}
                                                                >
                                                                    <SkipForwardIcon className="w-5 h-5" />
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        if (isListeningAlong) return;
                                                                        if (!isShuffle && !repeatOne) {
                                                                            // Off -> Shuffle
                                                                            toggleShuffle();
                                                                        } else if (isShuffle) {
                                                                            // Shuffle -> Repeat
                                                                            toggleShuffle(); // Toggle shuffle off
                                                                            if (!repeatOne) toggleRepeat(); // Toggle repeat on
                                                                        } else {
                                                                            // Repeat -> Off
                                                                            if (repeatOne) toggleRepeat(); // Toggle repeat off
                                                                        }
                                                                    }}
                                                                    disabled={isListeningAlong}
                                                                    className={`p-2 rounded-full ${isListeningAlong ? 'text-zinc-600 cursor-not-allowed' : isShuffle || repeatOne ? 'text-green-400 bg-white/5' : 'text-zinc-500 hover:text-white'}`}
                                                                    title={isListeningAlong ? "Leave Listen Along to control playback" : isShuffle ? "Shuffle On (Click for Repeat)" : repeatOne ? "Repeat On (Click for Off)" : "Click to Cycle Mode"}
                                                                >
                                                                    {repeatOne ? <RepeatIcon className="w-4 h-4" /> : <ShuffleIcon className="w-4 h-4" />}
                                                                </button>
                                                                {/* Volume Control */}
                                                                <div
                                                                    className="relative flex items-center z-[500]"
                                                                    onMouseEnter={() => setIsHoveringVolume(true)}
                                                                    onMouseLeave={() => setIsHoveringVolume(false)}
                                                                >
                                                                    <button onClick={toggleMute} className="p-2 text-zinc-400 hover:text-white transition-colors" title={isMuted ? 'Unmute' : 'Mute'}>
                                                                        {isMuted || volume === 0 ? <SpeakerXIcon className="w-4 h-4" /> : <SpeakerHighIcon className="w-4 h-4" />}
                                                                    </button>

                                                                    {/* Volume Slider Dropdown - Compact horizontal slider */}
                                                                    {isHoveringVolume && (
                                                                        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 animate-in fade-in duration-100 z-[600]">
                                                                            <div className="bg-zinc-900/95 border border-white/10 rounded-lg px-3 py-2 shadow-xl backdrop-blur-sm flex items-center gap-2">
                                                                                <input
                                                                                    type="range"
                                                                                    min={0}
                                                                                    max={100}
                                                                                    value={isMuted ? 0 : volume}
                                                                                    onChange={handleVolumeChange}
                                                                                    className="w-20 h-1 bg-zinc-700 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:shadow-sm"
                                                                                    style={{
                                                                                        background: `linear-gradient(to right, #6366f1 0%, #6366f1 ${isMuted ? 0 : volume}%, #3f3f46 ${isMuted ? 0 : volume}%, #3f3f46 100%)`
                                                                                    }}
                                                                                />
                                                                                <span className="text-[10px] font-medium text-zinc-400 w-7 text-right tabular-nums">
                                                                                    {isMuted ? 0 : volume}%
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Discord Rich Presence Button */}
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        console.log('[Discord] Button clicked! connected:', discord.connected, 'connecting:', discord.connecting);
                                                                        if (discord.connected) {
                                                                            discord.disconnect();
                                                                        } else {
                                                                            // Try to open the companion app via protocol
                                                                            console.log('[Discord] Attempting to open companion app via protocol...');
                                                                            const link = document.createElement('a');
                                                                            link.href = 'ezart-discord://open';
                                                                            link.style.display = 'none';
                                                                            document.body.appendChild(link);
                                                                            link.click();
                                                                            document.body.removeChild(link);

                                                                            // Wait for app to launch, then retry WebSocket connection
                                                                            setTimeout(() => {
                                                                                console.log('[Discord] Retrying WebSocket connection after protocol...');
                                                                                discord.connect();

                                                                                // Check again after giving WebSocket time to connect
                                                                                setTimeout(() => {
                                                                                    console.log('[Discord] Final check. connected:', discordConnectedRef.current);
                                                                                    if (!discordConnectedRef.current) {
                                                                                        if (player && isPlaying) {
                                                                                            try { player.pauseVideo(); } catch (e) { }
                                                                                        }
                                                                                        setShowDiscordModal(true);
                                                                                    }
                                                                                }, 2000);
                                                                            }, 1500);
                                                                        }
                                                                    }}
                                                                    className={`p-2 rounded-full relative ${discord.connected
                                                                        ? 'text-[#5865F2] bg-[#5865F2]/10'
                                                                        : discord.connecting
                                                                            ? 'text-zinc-400 animate-pulse'
                                                                            : 'text-zinc-500 hover:text-[#5865F2] hover:bg-white/5'
                                                                        }`}
                                                                    title={
                                                                        discord.connected
                                                                            ? 'Discord Connected - Click to disconnect'
                                                                            : discord.connecting
                                                                                ? 'Connecting to Discord...'
                                                                                : discord.error
                                                                                    ? `Discord: ${discord.error} - Click to download companion app`
                                                                                    : 'Connect Discord Rich Presence'
                                                                    }
                                                                >
                                                                    <DiscordIcon className="w-4 h-4" size={16} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* 3. Normal Content below */}
                                                    <div className="p-4 space-y-6 pb-32">
                                                        {playlistContent}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="h-full w-full overflow-y-auto custom-scrollbar p-4">
                                            {playlistContent}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="h-full flex items-center justify-center">
                                    <p className="text-xs text-zinc-500">Select a project to manage soundtrack</p>
                                </div>
                            )}
                        </div>

                        {/* MINI PLAYER (Visible when not on Soundtrack tab and Playing) */}
                        {activeTab !== 'soundtrack' && playingTrack && (
                            <div className="absolute bottom-0 left-0 right-0 bg-neutral-950 border-t border-white/10 p-5 z-30 flex items-center gap-4 shadow-2xl transition-all duration-300">
                                {/* Mini Video / Cover thumbnail Slot */}
                                {playingTrack.videoId ? (
                                    <div className="w-36 aspect-video rounded-lg overflow-hidden border border-white/5 relative flex-shrink-0 bg-zinc-900">
                                        <div ref={miniSlotRef} className="absolute inset-0" />
                                    </div>
                                ) : playingTrack.image ? (
                                    <div className="w-36 aspect-video rounded overflow-hidden border border-white/5 relative flex-shrink-0">
                                        <img src={playingTrack.image} className="w-full h-full object-cover" />
                                    </div>
                                ) : (
                                    <div className="w-14 h-14 rounded bg-zinc-800 flex items-center justify-center border border-white/5 flex-shrink-0">
                                        <MusicNoteIcon className="w-6 h-6 text-zinc-500" />
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    <p className="text-base font-medium text-white truncate leading-tight mb-0.5">{playingTrack.name}</p>
                                    <button onClick={(e) => handleArtistClick(e, playingTrack.artist, playingTrack.artistId, playingTrack.name)} className="text-sm text-zinc-500 truncate hover:text-white hover:underline block max-w-full text-left">{playingTrack.artist}</button>
                                </div>
                                <div className="flex items-center gap-3">
                                    {(() => { const controlsDisabled = isListeningAlong || !isPlaybackController; const disabledTitle = isListeningAlong ? "Leave Listen Along to control playback" : "Only the person playing controls playback"; return (<>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); if (!controlsDisabled) playPrev(); }}
                                        disabled={controlsDisabled}
                                        className={`p-2 rounded-full transition-colors ${controlsDisabled ? 'text-zinc-600 cursor-not-allowed' : 'text-zinc-400 hover:text-white hover:bg-white/5'}`}
                                        title={controlsDisabled ? disabledTitle : "Previous"}
                                    >
                                        <SkipBackIcon className="w-5 h-5" />
                                    </button>

                                    <button
                                        onClick={controlsDisabled ? undefined : togglePlay}
                                        disabled={controlsDisabled}
                                        className={`p-3 rounded-full transition-all hover:scale-105 active:scale-95 ${controlsDisabled ? 'text-zinc-500 bg-zinc-800 cursor-not-allowed' : 'text-white bg-white/10 hover:bg-white/20'}`}
                                        title={controlsDisabled ? disabledTitle : isPlaying ? "Pause" : "Play"}
                                    >
                                        {isPlaying ? <PauseIcon className="w-5 h-5" fill="currentColor" strokeWidth={0} /> : <PlayIcon className="w-5 h-5" fill="currentColor" strokeWidth={0} />}
                                    </button>

                                    <button
                                        onClick={(e) => { e.stopPropagation(); if (!controlsDisabled) playNext(); }}
                                        disabled={controlsDisabled}
                                        className={`p-2 rounded-full transition-colors ${controlsDisabled ? 'text-zinc-600 cursor-not-allowed' : 'text-zinc-400 hover:text-white hover:bg-white/5'}`}
                                        title={controlsDisabled ? disabledTitle : "Next"}
                                    >
                                        <SkipForwardIcon className="w-5 h-5" />
                                    </button>
                                    </>); })()}
                                </div>
                            </div>
                        )}

                    </div>
                </div>

                {/* SHARED PERSISTENT YOUTUBE PLAYER */}
                <SharedPlayer
                    videoId={playingTrack?.videoId}
                    isPlaying={isPlaying}
                    onReady={(event) => {
                        const p = event.target;
                        setPlayer(p);

                        // --- Restore Saved Volume ---
                        try {
                            const savedVolume = localStorage.getItem('ezart_player_volume');
                            const savedMuted = localStorage.getItem('ezart_player_muted') === 'true';

                            if (savedVolume) {
                                p.setVolume(parseInt(savedVolume));
                            }
                            if (savedMuted) {
                                p.mute();
                            }
                        } catch (e) {
                            console.warn('[YT Volume] Failed to restore volume:', e);
                        }

                        // --- Enhanced Quality Enforcement ---
                        // Try to set the highest available quality (1080p preferred, 720p fallback)
                        const enforceQuality = () => {
                            try {
                                const availableQualities = p.getAvailableQualityLevels?.() || [];
                                const preferredQualities = ['hd1080', 'hd720', 'large', 'medium'];

                                // Find the best available quality from our preference list
                                let targetQuality = 'hd1080'; // Default attempt
                                for (const quality of preferredQualities) {
                                    if (availableQualities.includes(quality)) {
                                        targetQuality = quality;
                                        break;
                                    }
                                }

                                // Apply the quality setting
                                p.setPlaybackQuality(targetQuality);
                                console.log(`[YT Quality] Set to: ${targetQuality} (available: ${availableQualities.join(', ')})`);
                            } catch (e) {
                                console.warn('[YT Quality] Failed to set quality:', e);
                            }
                        };

                        // Apply immediately and after a short delay (qualities may not be available instantly)
                        enforceQuality();
                        setTimeout(enforceQuality, 500);
                        setTimeout(enforceQuality, 1500);

                        // Detect video aspect ratio from iframe after short delay
                        setTimeout(() => {
                            try {
                                const iframe = p.getIframe();
                                if (iframe) {
                                    // Try to get actual video dimensions from player
                                    const videoData = p.getVideoData();
                                    // YouTube standard aspect ratios: 16:9 (1.78), 4:3 (1.33), 1:1 (1.0), 9:16 (0.56)
                                    // Most music videos are 16:9, some older ones are 4:3
                                    // For now, detect from iframe natural dimensions
                                    const iframeWidth = iframe.offsetWidth;
                                    const iframeHeight = iframe.offsetHeight;
                                    if (iframeWidth && iframeHeight) {
                                        const detectedRatio = iframeWidth / iframeHeight;
                                        // Only update if significantly different from current
                                        if (Math.abs(detectedRatio - videoAspectRatio) > 0.1) {
                                            setVideoAspectRatio(detectedRatio);
                                        }
                                    }
                                }
                            } catch (e) { console.log('Could not detect aspect ratio'); }
                        }, 500);
                    }}
                    onPlaybackQualityChange={(event: any) => {
                        // Re-enforce quality if YouTube changes it to something lower
                        const currentQuality = event.data;
                        console.log(`[YT Quality] Changed to: ${currentQuality}`);

                        // If quality dropped below 720p, try to bump it back up
                        const lowQualities = ['small', 'tiny', 'auto'];
                        if (lowQualities.includes(currentQuality) && player) {
                            try {
                                const availableQualities = player.getAvailableQualityLevels?.() || [];
                                if (availableQualities.includes('hd1080')) {
                                    player.setPlaybackQuality('hd1080');
                                } else if (availableQualities.includes('hd720')) {
                                    player.setPlaybackQuality('hd720');
                                } else if (availableQualities.includes('large')) {
                                    player.setPlaybackQuality('large');
                                }
                            } catch (e) { /* Ignore quality enforcement failures */ }
                        }
                    }}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onEnd={() => {
                        // Mark this video as advanced to prevent background interval from double-advancing
                        if (playingTrack?.videoId) {
                            lastAdvancedFromVideoRef.current = playingTrack.videoId;
                        }
                        // Only auto-advance if we're the controller (or not in a project)
                        if (selectedProject && currentUserRole && playbackControllerRef.current && playbackControllerRef.current !== user?.id) {
                            return; // Non-controller: wait for sync from controller
                        }
                        playNextRef.current();
                    }}
                    onError={async (e: any) => {
                        const errorCode = e?.data;
                        const currentVideoId = playingTrack?.videoId;
                        console.warn('Player Error:', errorCode, 'VideoId:', currentVideoId);

                        // Error codes 101 and 150 indicate video cannot be embedded (age-restricted or owner disabled)
                        // Error code 100 means video not found/removed
                        if ((errorCode === 101 || errorCode === 150 || errorCode === 100) && currentVideoId && playingTrack) {
                            console.log(`Video ${currentVideoId} is restricted/unavailable, searching for alternative...`);

                            // Add to failed IDs so we don't try it again
                            failedVideoIds.current.add(currentVideoId);

                            // Try to find an alternative video for the same track
                            const trackName = playingTrack.name;
                            const artistName = playingTrack.artist;

                            if (trackName && artistName) {
                                const alternativeId = await fetchYoutubeId(
                                    `${trackName} ${artistName}`,
                                    trackName,
                                    artistName,
                                    true,
                                    failedVideoIds.current
                                );

                                if (alternativeId && alternativeId !== currentVideoId) {
                                    console.log(`Found alternative video: ${alternativeId}`);
                                    setPlayingTrack(prev => prev ? { ...prev, videoId: alternativeId } : null);

                                    // Update the track in playlist if it exists there
                                    const trackInPlaylist = playlistTracks.find(t =>
                                        t.title.toLowerCase() === trackName.toLowerCase() &&
                                        t.artist.toLowerCase() === artistName.toLowerCase()
                                    );
                                    if (trackInPlaylist) {
                                        const newTracks = playlistTracks.map(t =>
                                            t.id === trackInPlaylist.id ? { ...t, videoId: alternativeId } : t
                                        );
                                        setPlaylistTracks(newTracks);
                                        persistPlaylist(newTracks);
                                    }
                                    return; // Don't skip, we found an alternative
                                } else {
                                    console.log(`No alternative found for "${trackName}" by "${artistName}" - removing from playlist`);

                                    // Remove the failed track from playlist
                                    const trackInPlaylist = playlistTracks.find(t =>
                                        t.title.toLowerCase() === trackName.toLowerCase() &&
                                        t.artist.toLowerCase() === artistName.toLowerCase()
                                    );
                                    if (trackInPlaylist) {
                                        const failedTrackIndex = playlistTracks.findIndex(t => t.id === trackInPlaylist.id);
                                        const newTracks = playlistTracks.filter(t => t.id !== trackInPlaylist.id);
                                        setPlaylistTracks(newTracks);
                                        persistPlaylist(newTracks);
                                        setToast({ message: `Removed "${trackName}" - no playable version found`, type: 'error' });

                                        // Play the next track at the same index (which is now the next song)
                                        // Don't use playNext since the index needs to stay the same
                                        if (newTracks.length > 0) {
                                            const nextIndex = failedTrackIndex >= newTracks.length ? 0 : failedTrackIndex;
                                            setTimeout(() => {
                                                setCurrentTrackIndex(nextIndex);
                                                playOnYoutube(newTracks[nextIndex], nextIndex);
                                            }, 500);
                                            return;
                                        }
                                    }
                                }
                            }
                        }

                        // No alternative found or other error - skip to next track
                        setTimeout(() => playNextRef.current(), 1000);
                    }}
                    activeSlot={
                        !isOpen
                            ? closedSlotRef // Priority 1: If Collapsed, ALWAYS use Popout (Floating).
                            : (!isActive
                                ? portalSlotRef // Priority 2: If Open but Inactive, use Miniplayer (Bar)
                                : (activeTab === 'soundtrack'
                                    ? (playerMode === 'video' ? mainSlotRef : controlsSlotRef)
                                    : miniSlotRef))
                    }
                    isInteractive={!isOpen || (isActive && isOpen && activeTab === 'soundtrack' && playerMode === 'video')}
                    borderRadius={
                        !isActive ? 8 : (!isOpen ? 12 : (activeTab === 'soundtrack' && playerMode === 'video' ? 12 : 8))
                    }
                    disableClipping={
                        !isOpen || // Always disable clipping for Popout (Floating)
                        !isActive || // Always disable clipping for Miniplayer (Floating)
                        (isActive && isOpen && activeTab === 'soundtrack' && playerMode !== 'video') // Disable for Controls/Cover mode (Floating sticky header)
                    }
                    showCloseButton={isOpen} // Only show close button when sidebar is open (not in popout mode)
                    onClose={handleClosePlayer}
                />

            </div >

            {/* Toggle Button - Only visible if Active, hidden when collapsed until hover */}
            <button
                onClick={onToggle}
                onMouseEnter={() => !isOpen && setIsHandleVisible(true)}
                onMouseLeave={() => !isOpen && setIsHandleVisible(false)}
                className={`
                fixed top-0 bottom-0 cursor-pointer flex items-center justify-center group z-50 outline-none
                transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
                ${isOpen
                        ? 'w-[2px] bg-white/5 hover:w-1 hover:bg-white/10'
                        : isHandleVisible
                            ? 'w-6 bg-onyx/95 hover:bg-zinc-900 backdrop-blur-xl'
                            : 'w-0 opacity-0 pointer-events-none'
                    }
                ${side === 'right'
                        ? `${isOpen ? 'right-[640px]' : 'right-0'} ${!isOpen && isHandleVisible && 'border-l border-white/5'}`
                        : `${isOpen ? 'left-[640px]' : 'left-0'} ${!isOpen && isHandleVisible && 'border-r border-white/5'}`
                    }
                ${isActive ? 'xl:flex' : 'hidden'}
            `}
                title={isOpen ? "Collapse Sidebar" : "Expand Sidebar"}
            >
                {/* Chevron Indicator - Only visible when collapsed and handle is shown */}
                {!isOpen && isHandleVisible && (
                    <div className="opacity-40 group-hover:opacity-100 transition-opacity">
                        {side === 'right' ? (
                            <CaretLeftIcon className="w-3 h-3 text-white" />
                        ) : (
                            <CaretRightIcon className="w-3 h-3 text-white" />
                        )}
                    </div>
                )}
            </button>

            {/* Portal Miniplayer (When NOT active and playing) - Only visible if sidebar is OPEN (Otherwise Popout is used) */}
            {!isActive && playingTrack && isOpen && (
                <MiniplayerPortal>
                    <div className={`fixed bottom-0 w-[640px] bg-neutral-950 border-t border-white/10 p-5 z-50 flex items-center gap-4 shadow-2xl animate-in slide-in-from-bottom-4 duration-300 ${side === 'right' ? 'right-0' : 'left-0'}`}>
                        {/* Mini Video / Cover Slot */}
                        <div className="w-36 aspect-video rounded-lg overflow-hidden border border-white/5 relative flex-shrink-0 bg-zinc-900 group/mini">
                            <div ref={portalSlotRef} className="absolute inset-0" />
                            {/* Close Button Overlay for Portal Mode */}
                            <button
                                onClick={handleClosePlayer}
                                className="absolute top-0.5 right-0.5 p-1 bg-zinc-900/60 text-white rounded-full opacity-0 group-hover/mini:opacity-100 transition-opacity z-[99999]"
                            >
                                <XIcon className="w-3 h-3" />
                            </button>
                        </div>

                        <div className="min-w-0 flex-1">
                            <p className="text-base font-medium text-white truncate leading-tight mb-0.5">{playingTrack.name}</p>
                            <button onClick={(e) => handleArtistClick(e, playingTrack.artist, playingTrack.artistId, playingTrack.name)} className="text-sm text-zinc-500 truncate hover:text-white hover:underline block max-w-full text-left">{playingTrack.artist}</button>
                        </div>

                        <div className="flex items-center gap-3">
                            {(() => { const controlsDisabled = isListeningAlong || !isPlaybackController; const disabledTitle = isListeningAlong ? "Leave Listen Along to control playback" : "Only the person playing controls playback"; return (<>
                            <button
                                onClick={(e) => { e.stopPropagation(); if (!controlsDisabled) playPrev(); }}
                                disabled={controlsDisabled}
                                className={`p-2 rounded-full transition-colors ${controlsDisabled ? 'text-zinc-600 cursor-not-allowed' : 'text-zinc-400 hover:text-white hover:bg-white/5'}`}
                                title={controlsDisabled ? disabledTitle : "Previous"}
                            >
                                <SkipBackIcon className="w-5 h-5" />
                            </button>

                            <button
                                onClick={controlsDisabled ? undefined : togglePlay}
                                disabled={controlsDisabled}
                                className={`p-3 rounded-full transition-all hover:scale-105 active:scale-95 ${controlsDisabled ? 'text-zinc-500 bg-zinc-800 cursor-not-allowed' : 'text-white bg-white/10 hover:bg-white/20'}`}
                                title={controlsDisabled ? disabledTitle : isPlaying ? "Pause" : "Play"}
                            >
                                {isPlaying ? <PauseIcon className="w-5 h-5" fill="currentColor" strokeWidth={0} /> : <PlayIcon className="w-5 h-5 ml-0.5" fill="currentColor" strokeWidth={0} />}
                            </button>

                            <button
                                onClick={(e) => { e.stopPropagation(); if (!controlsDisabled) playNext(); }}
                                disabled={controlsDisabled}
                                className={`p-2 rounded-full transition-colors ${controlsDisabled ? 'text-zinc-600 cursor-not-allowed' : 'text-zinc-400 hover:text-white hover:bg-white/5'}`}
                                title={controlsDisabled ? disabledTitle : "Next"}
                            >
                                <SkipForwardIcon className="w-5 h-5" />
                            </button>
                            </>); })()}
                        </div>
                    </div>
                </MiniplayerPortal >
            )}

            {/* Discord Download Modal */}
            <DiscordDownloadModal
                isOpen={showDiscordModal}
                onClose={() => setShowDiscordModal(false)}
                onRetryConnection={() => discord.connect()}
            />

            {/* Fullscreen Asset Viewer */}
            <FullscreenImageViewer
                images={filteredAssets.map(asset => ({
                    url: asset.image_url,
                    label: asset.label || asset.description,
                    id: asset.id,
                    metadata: asset.metadata
                }))}
                currentIndex={fullscreenAssetIndex}
                onClose={() => setFullscreenAssetIndex(null)}
                onNavigate={setFullscreenAssetIndex}
                respectSidebars={true}
                renderActions={(image, index) => {
                    const asset = filteredAssets[index];
                    if (!asset) return null;
                    return (
                        <button
                            onClick={() => {
                                if (confirm('Remove this asset from the project?')) {
                                    ProjectService.deleteShotById(asset.id).then(() => {
                                        setAssets(prev => prev.filter(a => a.id !== asset.id));
                                        setFullscreenAssetIndex(null);
                                    });
                                }
                            }}
                            className="w-9 h-9 rounded-lg bg-graphite hover:bg-red-500/80 flex items-center justify-center transition-colors"
                            title="Remove from project"
                        >
                            <TrashIcon className="w-5 h-5 text-dust" />
                        </button>
                    );
                }}
            />
        </>
    );
};

// --- Miniplayer Portal Component ---
// Safely targets the #miniplayer-portal-root element after mount
const MiniplayerPortal = ({ children }: { children: React.ReactNode }) => {
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);
    if (!mounted) return null;
    const root = document.getElementById('miniplayer-portal-root');
    if (!root) return null;
    return createPortal(children, root);
};

// --- Shared Player Component ---
const SharedPlayer = ({ videoId, isPlaying, onReady, onPlaybackQualityChange, onPlay, onPause, onEnd, onError, activeSlot, isInteractive, borderRadius, disableClipping, showCloseButton, onClose }: any) => {
    const [bounds, setBounds] = useState({ top: 0, left: 0, width: 0, height: 0 });
    const requestRef = useRef<number>(null);
    useEffect(() => {
        const update = () => {
            if (activeSlot.current) {
                let rect = activeSlot.current.getBoundingClientRect();

                // If clipping is disabled (e.g. for sticky headers), just use the raw rect
                if (disableClipping) {
                    setBounds({
                        top: rect.top,
                        left: rect.left,
                        width: rect.width,
                        height: rect.height
                    });
                    requestRef.current = requestAnimationFrame(update);
                    return;
                }

                // --- Manual Clipping Logic ---
                // Walk up the DOM to find scrollable parents and clip the rect
                let parent = activeSlot.current.parentElement;
                let visible = true;

                // Start with the element's own rect
                let clipTop = rect.top;
                let clipBottom = rect.bottom;
                let clipLeft = rect.left;
                let clipRight = rect.right;

                while (parent && parent !== document.body) {
                    const style = window.getComputedStyle(parent);
                    if (style.overflowY === 'auto' || style.overflowY === 'scroll' || style.overflowY === 'hidden' ||
                        style.overflowX === 'auto' || style.overflowX === 'scroll' || style.overflowX === 'hidden') {

                        const parentRect = parent.getBoundingClientRect();

                        // Clip intersection
                        if (clipTop < parentRect.top) clipTop = parentRect.top;
                        if (clipBottom > parentRect.bottom) clipBottom = parentRect.bottom;
                        if (clipLeft < parentRect.left) clipLeft = parentRect.left;
                        if (clipRight > parentRect.right) clipRight = parentRect.right;

                        // Check if completely hidden
                        if (clipTop >= clipBottom || clipLeft >= clipRight) {
                            visible = false;
                            break;
                        }
                    }
                    parent = parent.parentElement;
                }

                const newBounds = { top: 0, left: 0, width: 0, height: 0 };
                if (visible) {
                    newBounds.top = clipTop;
                    newBounds.left = clipLeft;
                    newBounds.width = clipRight - clipLeft;
                    newBounds.height = clipBottom - clipTop;
                }

                setBounds(prev => {
                    if (prev.top === newBounds.top &&
                        prev.left === newBounds.left &&
                        prev.width === newBounds.width &&
                        prev.height === newBounds.height) {
                        return prev;
                    }
                    return newBounds;
                });
            } else {
                setBounds(prev => {
                    if (prev.top === 0 && prev.left === 0 && prev.width === 0 && prev.height === 0) return prev;
                    return { top: 0, left: 0, width: 0, height: 0 };
                });
            }
            requestRef.current = requestAnimationFrame(update);
        };

        requestRef.current = requestAnimationFrame(update);
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        return () => {
            if (requestRef.current) cancelAnimationFrame(requestRef.current);
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, [activeSlot, videoId]);

    if (!videoId) return null;

    // Use actual container dimensions directly - no scaling needed
    const internalWidth = bounds.width || 480;
    const internalHeight = bounds.height || 270;

    const PlayerContent = (
        <motion.div
            initial={false}
            animate={{
                top: bounds.top,
                left: bounds.left,
                width: bounds.width,
                height: bounds.height,
                opacity: (bounds.width > 0 && videoId) ? 1 : 0
            }}
            transition={{
                duration: 0, // Instant update for position track to prevent "flying"
                opacity: { duration: 0.2 } // Smooth fade only
            }}
            className="fixed z-[6000] pointer-events-none overflow-hidden select-none"
            style={{
                borderRadius: `${borderRadius}px`
            }}
        >
            <div className="relative w-full h-full group/shared">
                {/* Close Button - Top Right of the Player Container */}
                {showCloseButton && onClose && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onClose(e); }}
                        className="absolute top-2 right-2 z-[60] bg-zinc-900/40 hover:bg-red-500/80 text-white/50 hover:text-white p-1 rounded-full backdrop-blur-sm transition-all opacity-0 group-hover/shared:opacity-100"
                        title="Close Player"
                    >
                        <XIcon className="w-3 h-3" />
                    </button>
                )}
                <div
                    className={`absolute ${isInteractive ? 'pointer-events-auto' : 'pointer-events-none'}`}
                    style={{
                        width: internalWidth,
                        height: internalHeight,
                        left: '50%',
                        top: '50%',
                        transform: `translate(-50%, -50%)`
                    }}
                >
                    <YouTube
                        videoId={videoId}
                        opts={{
                            width: internalWidth,
                            height: internalHeight,
                            playerVars: {
                                autoplay: 1,
                                controls: 1, // Enable native controls
                                modestbranding: 1,
                                playsinline: 1,
                                rel: 0,
                                showinfo: 0,
                                iv_load_policy: 3,
                                disablekb: 0, // Allow keyboard
                                origin: window.location.origin, // Fix postMessage warning
                                vq: 'hd1080' // Request 1080p quality (hint, may be ignored)
                            }
                        }}
                        onReady={onReady}
                        onPlaybackQualityChange={onPlaybackQualityChange}
                        onPlay={onPlay}
                        onPause={onPause}
                        onEnd={onEnd}
                        onError={onError}
                        className="w-full h-full"
                    />
                </div>

                {/* Click Shield - Start with pointer-events-none so it doesn't block underlying UI clicks */}
                {!isInteractive && (
                    <div className="absolute inset-0 z-50 pointer-events-none" />
                )}
            </div>
        </motion.div>
    );

    return <Portal>{PlayerContent}</Portal>;
};

// Toast Notification Component (placed outside SharedPlayer but still in CreativeSidebar)
const ToastNotification: React.FC<{ toast: { message: string; type: 'success' | 'error' } | null }> = ({ toast }) => {
    if (!toast) return null;

    return (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[300] animate-in fade-in slide-in-from-top-4 duration-300">
            <div className={`
                flex items-center gap-3 px-4 py-2.5 rounded-lg border shadow-xl backdrop-blur-md
                ${toast.type === 'error'
                    ? 'bg-red-500/10 border-red-500/20 text-red-200'
                    : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-200'}
            `}>
                {toast.type === 'error' ? <XIcon className="w-4 h-4" /> : <MusicNoteIcon className="w-4 h-4" />}
                <span className="text-sm font-medium">{toast.message}</span>
            </div>
        </div>
    );
};
